import { pgTable, text, varchar, serial, integer, bigint, boolean, doublePrecision, timestamp, jsonb, date, decimal, uniqueIndex, index, pgEnum, foreignKey, unique, check, uuid as pgUuid, type AnyPgColumn } from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import type {
  OrchestrationTool,
  ProjectExecutionSettings,
  ProjectSessionLimits,
  ProjectToolLimitRecord,
  JsonObject,
} from "./orchestration";

// TRAMS travel type values for invoice-level classification
export const TravelTypeEnum = z.enum([
  "Car",
  "Dom. Air",
  "Hotel",
  "Insurance",
  "Intl Air",
  "Misc.",
  "Rail",
  "Service Fee",
  "Ship-Dpst",
  "Ship-Finl",
  "Tour-Dpst",
  "Tour-Finl",
]);
export type TravelType = z.infer<typeof TravelTypeEnum>;

// TRAMS invoice status codes (INVOICETYPE.INVOICETYPECODE)
// O = Open, C = Closed (paid), V = Voided
export const InvoiceStatusCodeEnum = z.enum(["C", "O", "V"]);
export type InvoiceStatusCode = z.infer<typeof InvoiceStatusCodeEnum>;

// Computed trip payment status derived from linked invoices
export const TripPaymentStatusEnum = z.enum([
  "no_invoices",
  "unpaid",
  "partially_paid",
  "fully_paid",
]);
export type TripPaymentStatus = z.infer<typeof TripPaymentStatusEnum>;

export const TRAMS_UNASSIGNED_INVOICES_TRIP_TYPE = "trams_unassigned_invoices" as const;
export const TRAMS_UNASSIGNED_INVOICES_TRIP_NAME = "Auto-Invoiced" as const;
export const TRAMS_INVOICE_HISTORY_TRIP_TYPE = "trams_invoice_history" as const;

/** TRAMS sync lifecycle only — not commission inquiry or other workflows. */
export const InvoiceSyncStatusEnum = z.enum([
  "pending",
  "connected",
  "trams_validation_failed",
]);
export type InvoiceSyncStatus = z.infer<typeof InvoiceSyncStatusEnum>;

/** True when TRAMS rejected or failed validation (show error UI). */
export function isInvoiceTramsSyncError(
  status: string | null | undefined,
): status is "trams_validation_failed" {
  return status === "trams_validation_failed";
}

// Main trip table
export const trips = pgTable(
  "trips",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    startDate: timestamp("start_date"),
    endDate: timestamp("end_date"),
    countries: jsonb("countries").default([]),
    clientId: integer("client_id"),
    clientName: text("client_name"),
    clientInterests: text("client_interests"),
    tripBrief: text("trip_brief"),
    agencyId: text("agency_id"),
    teamId: integer("team_id").references(() => teams.id, { onDelete: "set null" }),
    advisorId: integer("advisor_id"),
    managerId: integer("manager_id"),
    stage: text("stage").default("lead"),
    tripStatus: text("trip_status").default("no_invoices"),
    estimatedValue: integer("estimated_value"),
    probability: integer("probability").default(50),
    // TRAMS linkage: INVOICE.RECORDLOCATOR (varchar 6)
    // Nullable for legacy rows; createTrip always generates one. PostgreSQL unique index permits multiple NULLs.
    recordLocator: varchar("record_locator", { length: 6 }),
    // System-owned trip buckets, e.g. the advisor-visible holding trip for TRAMS invoices that arrive without a trip.
    systemTripType: text("system_trip_type"),
    isReportingExcluded: boolean("is_reporting_excluded").notNull().default(false),
    // Group trip fields
    isGroup: boolean("is_group").default(false),
    groupName: text("group_name"),
    shareToken: text("share_token").unique(),
    itineraryMetadata: jsonb("itinerary_metadata").default('{}'),
    // Trip creator. Cannot be added with `.default()` (FK to users.id, no
    // sentinel user). On existing deployments the NOT NULL flip is handled by
    // the idempotent migration `drizzle/0029_trips_add_created_by.sql`, which
    // is auto-run by `npm run db:push` (see `scripts/db-push-safe.mjs`).
    // Trips lacking both `advisor_id` and `manager_id` will block the
    // migration with a clear error so the orphan can be resolved manually.
    createdBy: integer("created_by").notNull().references(() => users.id, { onDelete: "restrict" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    recordLocatorUniqueIdx: uniqueIndex("trips_record_locator_unique_idx").on(
      table.recordLocator,
    ),
    systemTripByAdvisorUniqueIdx: uniqueIndex("trips_system_trip_type_advisor_unique_idx")
      .on(table.advisorId, table.systemTripType)
      .where(sql`${table.systemTripType} IS NOT NULL`),
    systemTripRequiresAdvisor: check("trips_system_trip_requires_advisor", sql`system_trip_type IS NULL OR advisor_id IS NOT NULL`),
  }),
);

export const insertTripSchema = createInsertSchema(trips);

// Trip destinations (sourced from WorldVia.com normalized destination list)
export const destinationTypeEnum = pgEnum("destination_type_enum", ["Region", "Country", "US State"]);
export const DestinationTypeEnum = z.enum(["Region", "Country", "US State"]);
export type DestinationType = z.infer<typeof DestinationTypeEnum>;

export const tripDestinations = pgTable(
  "trip_destinations",
  {
    id: serial("id").primaryKey(),
    tripId: integer("trip_id")
      .notNull()
      .references(() => trips.id, { onDelete: "cascade" }),
    worldviaDestinationId: text("worldvia_destination_id").notNull(),
    displayName: text("display_name").notNull(),
    destinationType: destinationTypeEnum("destination_type").notNull(),
    parentRegion: text("parent_region"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    tripIdx: index("trip_destinations_trip_idx").on(table.tripId),
    tripDestUnique: uniqueIndex("trip_destinations_trip_dest_unique").on(
      table.tripId,
      table.worldviaDestinationId,
    ),
  }),
);

export const insertTripDestinationSchema = createInsertSchema(tripDestinations);

export const tripDestinationsRelations = relations(tripDestinations, ({ one }) => ({
  trip: one(trips, {
    fields: [tripDestinations.tripId],
    references: [trips.id],
  }),
}));

export type TripDestination = typeof tripDestinations.$inferSelect;
export type InsertTripDestination = z.infer<typeof insertTripDestinationSchema>;

// Trip components (flights, hotels, activities, etc.)
export const tripComponents = pgTable("trip_components", {
  id: serial("id").primaryKey(),
  tripId: integer("trip_id").notNull(),
  type: text("type").notNull(), // "flight", "hotel", "activity", "transport", "food", "other"
  title: text("title").notNull(),
  description: text("description"),
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date"),
  location: text("location"),
  details: jsonb("details"), // Specific details based on type
  dayIndex: integer("day_index").notNull(), // Which day of the trip this belongs to
  startTime: text("start_time"), // Optional time in HH:MM format
  endTime: text("end_time"), // Optional time in HH:MM format
});

export const insertTripComponentSchema = createInsertSchema(tripComponents);

// Define relations
export const tripsRelations = relations(trips, ({ many }) => ({
  components: many(tripComponents),
  destinations: many(tripDestinations),
  dayDestinations: many(dayDestinations),
  clientTrips: many(clientTrips),
  threadLinks: many(chatSessionTrips),
  invoices: many(invoices),
}));

export const tripComponentsRelations = relations(tripComponents, ({ one }) => ({
  trip: one(trips, {
    fields: [tripComponents.tripId],
    references: [trips.id],
  }),
}));

export type InsertTrip = z.infer<typeof insertTripSchema>;
export type Trip = typeof trips.$inferSelect;

export type InsertTripComponent = z.infer<typeof insertTripComponentSchema>;
export type TripComponent = typeof tripComponents.$inferSelect;

// Schema for validating flight components
export const flightSchema = z.object({
  tripType: z.enum(["roundTrip", "oneWay", "multiCity"]).default("roundTrip"),
  passengers: z.number().default(1),
  cabinClass: z.enum(["economy", "premiumEconomy", "business", "firstClass"]).default("economy"),
  airlineBookingLocator: z.string().optional(),
  gdsBookingLocator: z.string().optional(),
  legs: z.array(z.object({
    departureAirport: z.string().optional(),
    departureCity: z.string().optional(),
    departureAirportCode: z.string().optional(),
    arrivalAirport: z.string().optional(),
    arrivalCity: z.string().optional(),
    arrivalAirportCode: z.string().optional(),
    departureDate: z.string().optional(),
    returnDate: z.string().optional(),
    segments: z.array(z.object({
      airline: z.string().optional(),
      flightNumber: z.string().optional(),
      departureDate: z.string().optional(),
      departureTime: z.string().optional(),
      arrivalDate: z.string().optional(),
      arrivalTime: z.string().optional(),
      departureAirport: z.string().optional(),
      departureCity: z.string().optional(),
      departureAirportCode: z.string().optional(),
      arrivalAirport: z.string().optional(),
      arrivalCity: z.string().optional(),
      arrivalAirportCode: z.string().optional(),
      seatNumber: z.string().optional(),
    })).default([{}])
  })).default([{}])
});

// Schema for component types
export const componentTypeSchema = z.enum([
  "flight",
  "hotel",
  "activity",
  "transport",
  "food",
  "cruise",
  "travel-insurance", 
  "tour",
  "package",
  "other"
]);

export type ComponentType = z.infer<typeof componentTypeSchema>;

// Day destinations table to store location information for each day of a trip
export const dayDestinations = pgTable("day_destinations", {
  id: serial("id").primaryKey(),
  tripId: integer("trip_id").notNull(),
  dayIndex: integer("day_index").notNull(), // Which day of the trip (0-based index)
  destination: text("destination").notNull(), // In "City, Country/State" format
  notes: text("notes"), // Optional additional information about this destination
});

export const insertDayDestinationSchema = createInsertSchema(dayDestinations);

// Day destinations relations
export const dayDestinationsRelations = relations(dayDestinations, ({ one }) => ({
  trip: one(trips, {
    fields: [dayDestinations.tripId],
    references: [trips.id],
  }),
}));

export type InsertDayDestination = z.infer<typeof insertDayDestinationSchema>;
export type DayDestination = typeof dayDestinations.$inferSelect;

//
// CRM Client System
//

// Clients (Core data)
export const clients = pgTable("clients", {
  id: serial("id").primaryKey(),
  // Personal information
  firstName: text("first_name").notNull(),
  middleName: text("middle_name"),
  lastName: text("last_name").notNull(),
  suffix: text("suffix"),
  preferredName: text("preferred_name"),
  salutation: text("salutation"),
  gender: text("gender"),
  
  // Contact information
  email: text("email"), // Primary email
  secondaryEmail: text("secondary_email"),
  phone: text("phone"),
  workPhone: text("work_phone"),
  mobile: text("mobile"),
  agencyId: text("agency_id"),
  teamId: integer("team_id").references(() => teams.id, { onDelete: "set null" }),
  ownerId: integer("owner_id"),
  managerId: integer("manager_id"),
  
  // Mailing address
  address: text("address"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  country: text("country"),
  
  // Billing address
  billingAddress: text("billing_address"),
  billingCity: text("billing_city"),
  billingState: text("billing_state"),
  billingZipCode: text("billing_zip_code"),
  billingCountry: text("billing_country"),
  
  // Social media
  instagram: text("instagram"),
  facebook: text("facebook"),
  twitter: text("twitter"),
  
  // Emergency contact
  emergencyContactName: text("emergency_contact_name"),
  emergencyContactRelationship: text("emergency_contact_relationship"),
  emergencyContactPhone: text("emergency_contact_phone"),
  emergencyContactEmail: text("emergency_contact_email"),
  
  // Health information
  hasMovementRestrictions: boolean("has_movement_restrictions"),
  movementRestrictionsNotes: text("movement_restrictions_notes"),
  
  // Other
  birthdate: date("birthdate"),
  notes: text("notes"),
  avatarUrl: text("avatar_url"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertClientSchema = createInsertSchema(clients);

// Relationships (centralized relationship records)
export const relationshipTypeEnum = pgEnum("relationship_type_enum", [
  "Family",
  "Group",
  "Company",
  "Organization",
]);

export const agenticRolloutStageEnum = pgEnum("agentic_rollout_stage", [
  "internal",
  "staff",
  "alpha",
  "beta",
  "ga",
]);

export const agenticRolloutOverrideModeEnum = pgEnum(
  "agentic_rollout_override_mode",
  ["force_v1", "force_v2"],
);

export const relationships = pgTable("relationships", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  relationshipType: relationshipTypeEnum("relationship_type")
    .notNull()
    .default("Company"),
  industry: text("industry"),
  owner: text("owner"),
  estimatedAnnualTravelSpend: integer("estimated_annual_travel_spend"),
  employees: integer("employees"),
  website: text("website"),
  // Mailing address
  mailingAddress: text("mailing_address"),
  mailingCity: text("mailing_city"),
  mailingState: text("mailing_state"),
  mailingZipCode: text("mailing_zip_code"),
  mailingCountry: text("mailing_country"),
  // Billing address
  billingAddress: text("billing_address"),
  billingCity: text("billing_city"),
  billingState: text("billing_state"),
  billingZipCode: text("billing_zip_code"),
  billingCountry: text("billing_country"),
  primaryClientId: integer("primary_client_id"),
  billingClientId: integer("billing_client_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertRelationshipSchema = createInsertSchema(relationships);

export const relationshipClients = pgTable("relationship_clients", {
  id: serial("id").primaryKey(),
  relationshipId: integer("relationship_id").notNull(),
  clientId: integer("client_id").notNull(),
});

export const insertRelationshipClientSchema =
  createInsertSchema(relationshipClients);

// New: Client Methods (phones/emails) — subresource
export const clientMethods = pgTable("client_methods", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  kind: text("kind").notNull(), // 'email' | 'phone'
  label: text("label"), // 'Personal' | 'Work' | 'Home' | 'Mobile'
  value: text("value"),
  isPrimary: boolean("is_primary").notNull().default(false),
  order: integer("order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertClientMethodSchema = createInsertSchema(clientMethods);

// Company Information
export const clientCompanyInfo = pgTable("client_company_info", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  companyName: text("company_name"),
  jobTitle: text("job_title"),
  department: text("department"),
  workEmail: text("work_email"),
  workPhone: text("work_phone"),
  workAddress: text("work_address"),
  workCity: text("work_city"),
  workState: text("work_state"),
  workZipCode: text("work_zip_code"),
  workCountry: text("work_country"),
  website: text("website"),
  linkedIn: text("linkedin"),
});

export const insertClientCompanyInfoSchema = createInsertSchema(clientCompanyInfo);

// Identity Information (passports, visas, etc)
export const clientIdentity = pgTable("client_identity", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  type: text("type").notNull(), // passport, visa, driver_license, etc
  documentNumber: text("document_number"),
  issuingCountry: text("issuing_country"),
  expirationDate: date("expiration_date"),
  issueDate: date("issue_date"),
  notes: text("notes"),
});

export const insertClientIdentitySchema = createInsertSchema(clientIdentity);

// Travel Preferences
export const clientPreferences = pgTable("client_preferences", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  seatPreference: text("seat_preference"), // window, aisle, etc
  mealPreference: text("meal_preference"), // vegetarian, kosher, etc
  roomPreference: text("room_preference"), // smoking, non-smoking, etc
  specialRequests: text("special_requests"),
  frequentFlyerNumbers: jsonb("frequent_flyer_numbers"), // { "airline": "number" }
  hotelLoyaltyPrograms: jsonb("hotel_loyalty_programs"), // { "hotel": "number" }
  rentalCarPreferences: text("rental_car_preferences"),
  travelInsuranceInfo: text("travel_insurance_info"),
  tsaPreCheckNumber: text("tsa_precheck_number"),
  globalEntryNumber: text("global_entry_number"),
  customPreferences: jsonb("custom_preferences"), // any custom preferences
});

export const insertClientPreferencesSchema = createInsertSchema(clientPreferences);

// Notes for clients
export const clientNotes = pgTable("client_notes", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  title: text("title").notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertClientNotesSchema = createInsertSchema(clientNotes);

// Emails associated with clients
export const clientEmails = pgTable("client_emails", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  subject: text("subject").notNull(),
  content: text("content").notNull(),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
  from: text("from").notNull(),
  to: text("to").notNull(),
  status: text("status").notNull(), // sent, draft, etc
});

export const insertClientEmailsSchema = createInsertSchema(clientEmails);

// Tasks related to clients
export const clientTasks = pgTable("client_tasks", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  dueDate: timestamp("due_date"),
  priority: text("priority"), // high, medium, low
  status: text("status").notNull(), // pending, completed, etc
  assignedTo: text("assigned_to"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});

export const insertClientTasksSchema = createInsertSchema(clientTasks);

// Join table for clients and trips
export const clientTrips = pgTable("client_trips", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  tripId: integer("trip_id").notNull(),
  role: text("role").default("traveler"), // primary, traveler, companion, emergency_contact
});

export const insertClientTripsSchema = createInsertSchema(clientTrips);

// Define all the relations
export const clientsRelations = relations(clients, ({ many, one }) => ({
  companyInfo: one(clientCompanyInfo, {
    fields: [clients.id],
    references: [clientCompanyInfo.clientId],
  }),
  identities: many(clientIdentity),
  preferences: one(clientPreferences, {
    fields: [clients.id],
    references: [clientPreferences.clientId],
  }),
  notes: many(clientNotes),
  emails: many(clientEmails),
  tasks: many(clientTasks),
  trips: many(clientTrips),
  methods: many(clientMethods),
  threadLinks: many(chatSessionClients),
}));

export const clientMethodsRelations = relations(clientMethods, ({ one }) => ({
  client: one(clients, {
    fields: [clientMethods.clientId],
    references: [clients.id],
  }),
}));

export const clientCompanyInfoRelations = relations(clientCompanyInfo, ({ one }) => ({
  client: one(clients, {
    fields: [clientCompanyInfo.clientId],
    references: [clients.id],
  }),
}));

export const relationshipsRelations = relations(relationships, ({ many }) => ({
  clients: many(relationshipClients),
}));

export const relationshipClientsRelations = relations(
  relationshipClients,
  ({ one }) => ({
    relationship: one(relationships, {
      fields: [relationshipClients.relationshipId],
      references: [relationships.id],
    }),
    client: one(clients, {
      fields: [relationshipClients.clientId],
      references: [clients.id],
    }),
  }),
);

export const clientIdentityRelations = relations(clientIdentity, ({ one }) => ({
  client: one(clients, {
    fields: [clientIdentity.clientId],
    references: [clients.id],
  }),
}));

export const clientPreferencesRelations = relations(clientPreferences, ({ one }) => ({
  client: one(clients, {
    fields: [clientPreferences.clientId],
    references: [clients.id],
  }),
}));

export const clientNotesRelations = relations(clientNotes, ({ one }) => ({
  client: one(clients, {
    fields: [clientNotes.clientId],
    references: [clients.id],
  }),
}));

export const clientEmailsRelations = relations(clientEmails, ({ one }) => ({
  client: one(clients, {
    fields: [clientEmails.clientId],
    references: [clients.id],
  }),
}));

export const clientTasksRelations = relations(clientTasks, ({ one }) => ({
  client: one(clients, {
    fields: [clientTasks.clientId],
    references: [clients.id],
  }),
}));

export const clientTripsRelations = relations(clientTrips, ({ one }) => ({
  client: one(clients, {
    fields: [clientTrips.clientId],
    references: [clients.id],
  }),
  trip: one(trips, {
    fields: [clientTrips.tripId],
    references: [trips.id],
  }),
}));

// Types for Client system
export type Client = typeof clients.$inferSelect;
export type InsertClient = z.infer<typeof insertClientSchema>;
export type ClientMethod = typeof clientMethods.$inferSelect;
export type InsertClientMethod = z.infer<typeof insertClientMethodSchema>;

export type Relationship = typeof relationships.$inferSelect;
export type InsertRelationship = z.infer<typeof insertRelationshipSchema>;
export type RelationshipClient = typeof relationshipClients.$inferSelect;
export type InsertRelationshipClient = z.infer<
  typeof insertRelationshipClientSchema
>;
export type RelationshipType =
  (typeof relationshipTypeEnum)["enumValues"][number];

export type ClientCompanyInfo = typeof clientCompanyInfo.$inferSelect;
export type InsertClientCompanyInfo = z.infer<typeof insertClientCompanyInfoSchema>;

export type ClientIdentity = typeof clientIdentity.$inferSelect;
export type InsertClientIdentity = z.infer<typeof insertClientIdentitySchema>;

export type ClientPreferences = typeof clientPreferences.$inferSelect;
export type InsertClientPreferences = z.infer<typeof insertClientPreferencesSchema>;

export type ClientNote = typeof clientNotes.$inferSelect;
export type InsertClientNote = z.infer<typeof insertClientNotesSchema>;

export type ClientEmail = typeof clientEmails.$inferSelect;
export type InsertClientEmail = z.infer<typeof insertClientEmailsSchema>;

export type ClientTask = typeof clientTasks.$inferSelect;
export type InsertClientTask = z.infer<typeof insertClientTasksSchema>;

export type ClientTrip = typeof clientTrips.$inferSelect;

// Proposal table for trip proposals
export const proposals = pgTable("proposals", {
  id: serial("id").primaryKey(),
  tripId: integer("trip_id").notNull(),
  title: text("title").notNull(), // Title for the proposal
  coverImageUrl: text("cover_image_url"), // URL to the proposal cover image
  advisorNote: text("advisor_note"), // Note from the travel advisor
  highlights: jsonb("highlights").default([]), // Key trip highlights
  termsAndConditions: text("terms_and_conditions"), // Terms and conditions text
  pricing: jsonb("pricing"), // Pricing details object 
  status: text("status").notNull().default("draft"), // draft, sent, approved, rejected
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertProposalSchema = createInsertSchema(proposals);

// Proposal documents
export const proposalDocuments = pgTable("proposal_documents", {
  id: serial("id").primaryKey(),
  proposalId: integer("proposal_id").notNull(),
  title: text("title").notNull(),
  documentUrl: text("document_url").notNull(),
  documentType: text("document_type").notNull(), // itinerary, confirmation, etc.
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertProposalDocumentSchema = createInsertSchema(proposalDocuments);

// Define relations
export const proposalsRelations = relations(proposals, ({ one, many }) => ({
  trip: one(trips, {
    fields: [proposals.tripId],
    references: [trips.id],
  }),
  documents: many(proposalDocuments),
}));

export const proposalDocumentsRelations = relations(proposalDocuments, ({ one }) => ({
  proposal: one(proposals, {
    fields: [proposalDocuments.proposalId],
    references: [proposals.id],
  }),
}));

export type InsertProposal = z.infer<typeof insertProposalSchema>;
export type Proposal = typeof proposals.$inferSelect;

export type InsertProposalDocument = z.infer<typeof insertProposalDocumentSchema>;
export type ProposalDocument = typeof proposalDocuments.$inferSelect;
export type InsertClientTrip = z.infer<typeof insertClientTripsSchema>;

// User Roles enum
export const UserRoleEnum = z.enum([
  "SYSTEM_ADMIN",
  "ADMIN",
  "AGENCY_OWNER",
  "WORLDVIA_HQ", 
  "WORLDVIA_ADVISOR",
  "PUBLIC_PRO_USER",
  "PUBLIC_FREE_USER",
  "CLIENT"
]);

export type UserRole = z.infer<typeof UserRoleEnum>;

// Subscription Tiers enum
export const SubscriptionTierEnum = z.enum([
  "FREE",
  "PRO"
]);

export type SubscriptionTier = z.infer<typeof SubscriptionTierEnum>;

// ============================================================================
// AR-13 · Onboarding enums
// Declared above `users` because pgEnum instances are evaluated eagerly when a
// column is built (unlike `references()`, which is a lazy closure).
// ============================================================================

// Member lifecycle during onboarding. `users.is_active` is kept in sync:
// 'active' ⇒ is_active = true, everything else ⇒ false.
export const onboardingStatusEnum = pgEnum("onboarding_status", [
  "checkout_pending",
  "pending",
  "active",
  "suspended",
  "deactivated",
]);

export const addressTypeEnum = pgEnum("address_type", ["billing", "mailing"]);

// One row per system per user in external_system_ids.
export const externalSystemNameEnum = pgEnum("external_system_name", [
  "aivia",
  "worldvia_pro",
  "hubspot_contact",
  "hubspot_company",
  "stripe_customer",
  "stripe_subscription",
  "auth0",
  "trams_interface",
]);

// Mirrors Stripe's subscription.status values so webhook writes never fail on
// an unmapped state.
export const subscriptionStatusEnum = pgEnum("subscription_status", [
  "incomplete",
  "incomplete_expired",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "unpaid",
  "paused",
]);

// ============================================================================
// AR-330 · User-domain additive batch — enums
// Declared above `users` / `teams` for the same reason as the AR-13 block:
// pgEnum instances are evaluated eagerly when a column is built.
//
// Only these four enum types are introduced. Every other constrained field in
// this batch is a varchar with its allowed values in a comment, matching the
// existing `external_system_ids.status` pattern — a Postgres enum needs a
// migration per value change, and these vocabularies are still moving
// (D-01, D-05, D-CO-08 are open in the decision register).
// ============================================================================

// The five-state account lifecycle that replaces the `users.is_active` boolean
// (AR-336). Deliberately a SEPARATE type from `onboarding_status`, which
// happens to carry the same five labels today: onboarding_status describes how
// far a member got through checkout, account_status describes what they are
// allowed to see. AR-336 collapses the overlap once it owns the write path.
export const accountStatusEnum = pgEnum("account_status", [
  "checkout_pending",
  "pending",
  "active",
  "suspended",
  "deactivated",
  // TEMPORARY, migration only (Britta, AR-336): PRO members in Training keep
  // their current access (counts as active for invoicing and statements) until
  // they are cleared manually. AIVIA's own onboarding never sets it. Sunset
  // ~2027-03 means "no row carries it" — Postgres cannot drop an enum value
  // without swapping the type, so the label itself stays.
  "training",
]);

// Why an account is suspended, which is what lets the status access rules
// (AR-339) send a payment-failed member to a payment form and a manually
// suspended member to a lock screen.
export const suspensionSourceEnum = pgEnum("suspension_source", [
  "manual",
  "payment_failed",
  "cascade_owner_suspended",
]);

// Team-level lifecycle, written by the suspension cascade (AR-340). `archived`
// is for the ownerless teams AR-329 turns up, which are retired rather than
// suspended.
export const teamStatusEnum = pgEnum("team_status", [
  "active",
  "suspended",
  "deactivated",
  "archived",
]);

// Position within a team. These three are the hierarchy named in the PRO
// migration brief and used literally by AR-338 (`team_role = 'agency_owner'`).
// NOT the same vocabulary as `UserRoleEnum` / `roles.slug`, which is platform
// RBAC and is blocked on decision D-05.
export const teamRoleEnum = pgEnum("team_role", [
  "agency_owner",
  "manager",
  "advisor",
]);

// Users table for authentication
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  displayName: text("display_name").notNull(),
  avatarUrl: text("avatar_url"),
  // DEPRECATED (AR-345): a mirror of the primary user_role_assignments row,
  // written only by RoleAssignmentService. New code reads the assignment, never
  // this column. Kept until after launch because the admin guards and
  // single_agency_owner_per_agency_idx still read it. The default is ADVISOR
  // (Associate Advisor, Rifat 2026-10-12), a real member role, not the old
  // PUBLIC_FREE_USER placeholder.
  role: text("role").notNull().default("ADVISOR"),
  subscriptionTier: text("subscription_tier").notNull().default("FREE"),
  advancedMode: boolean("advanced_mode").notNull().default(false),
  defaultProvider: text("default_provider").default("ANTHROPIC"),
  defaultModel: text("default_model").default("claude-haiku-4-5"),
  writerProvider: text("writer_provider").default("ANTHROPIC"),
  writerModel: text("writer_model").default("claude-sonnet-4-0"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  agencyId: text("agency_id"),
  // users ↔ teams is a genuine cycle (teams.agency_owner_user_id → users.id),
  // so the ref is a lazy AnyPgColumn closure. FK created in the AR-13 migration.
  teamId: integer("team_id").references((): AnyPgColumn => teams.id, { onDelete: "set null" }),
  managedTeamIds: jsonb("managed_team_ids"),
  auth0Sub: text("auth0_sub"),
  worldviaUserId: text("worldvia_user_id"), // Bubble.io user _id for worldvia.pro sync
  branchNumber: integer("branch_number").notNull().default(16),
  isActive: boolean("is_active").notNull().default(true),
  // Legacy AIVIA chat import tracking
  legacyChatImportStatus: text("legacy_chat_import_status").default("not_checked"), // 'not_checked', 'available', 'imported', 'declined', 'no_history', 'importing'
  legacyChatImportedAt: timestamp("legacy_chat_imported_at"),
  legacyChatImportCount: integer("legacy_chat_import_count").default(0),
  financesUnclaimedAccess: boolean("finances_unclaimed_access").notNull().default(false),
  // ── AR-13 · Onboarding identity columns ────────────────────────────────────
  // Per the AIVIA Onboarding Data Model these EXTEND the canonical users table;
  // no parallel onboarding users table exists. `password` stays because Auth0
  // writes a placeholder into it — real credentials live in Auth0, never here.
  planId: integer("plan_id").references(() => plans.planId, { onDelete: "set null" }),
  routeId: integer("route_id").references(() => routes.routeId, { onDelete: "set null" }),
  pathwayId: integer("pathway_id").references(() => pathways.pathwayId, { onDelete: "set null" }),
  onboardingStatus: onboardingStatusEnum("onboarding_status"),
  // Agency owner of an associate advisor. The associate also shares the
  // owner's agency_id / team_id (teams is the agency record — see AR-13 notes).
  parentUserId: integer("parent_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  referredBy: varchar("referred_by", { length: 255 }),
  marketingOptIn: boolean("marketing_opt_in").notNull().default(false),
  gdsInterest: boolean("gds_interest").notNull().default(false),
  fullLegalName: varchar("full_legal_name", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  dob: date("dob"), // sensitive (background check) — do not expose in list APIs
  // ── AR-330 · User-domain additive batch ────────────────────────────────────
  // Every column here is NULLABLE with no behaviour-changing default. Nothing
  // reads them yet; the tickets listed against each group turn them on behind
  // their own feature flags.
  //
  // The permanent external join key. Mirrors `teams.uuid`, except nullable for
  // now: AR-346 promotes it to NOT NULL once the one-off backfill has run
  // (`drizzle/0037_ar330_users_uuid_backfill.sql`). This is what gets pushed
  // into HubSpot as `aivia_user_id`, replacing the join on HubSpot's own
  // Record ID, which changes on every contact merge (AR-332).
  uuid: pgUuid("uuid").defaultRandom().unique(),
  // Account lifecycle (AR-336). `is_active` stays authoritative until
  // AccountStatusService owns the write path and FEATURE_ACCOUNT_LIFECYCLE
  // is on; both are dual-written for one release after that.
  accountStatus: accountStatusEnum("account_status"),
  accountStatusReasonId: integer("account_status_reason_id")
    .references((): AnyPgColumn => statusReasonCodes.reasonId, { onDelete: "set null" }),
  accountStatusChangedAt: timestamp("account_status_changed_at"),
  accountStatusChangedBy: integer("account_status_changed_by")
    .references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  // Restored on reactivation, and on reversing a cascade — a cascade must put
  // each member back where they were, not blanket-promote them to active
  // (AR-340).
  previousAccountStatus: accountStatusEnum("previous_account_status"),
  suspensionSource: suspensionSourceEnum("suspension_source"),
  lockedAt: timestamp("locked_at"),
  deactivatedAt: timestamp("deactivated_at"),
  // Separate from deactivatedAt: a cancellation can be scheduled for the end
  // of a paid period (AR-340 `deactivate_at_period_end`).
  deactivationEffectiveAt: timestamp("deactivation_effective_at"),
  // From PRO's own "Test account" field. PRO's "Training" Account_Status is
  // not a test account: it maps to `pending` (AR-336).
  isTestAccount: boolean("is_test_account"),
  // Where system mail goes when it must not follow a changed login email.
  notificationEmail: varchar("notification_email", { length: 255 }),
  // NOTE: `full_legal_name` is listed in AR-330 but already shipped in AR-13
  // (see `fullLegalName` above) — no second column is added.
  lastLoginAt: timestamp("last_login_at"),
  lastLoginIp: varchar("last_login_ip", { length: 45 }), // 45 = max IPv6 text form
  lastVerifiedAt: timestamp("last_verified_at"),
  // ── AR-362 / AR-205 · Provenance ───────────────────────────────────────────
  // How the row got here, so a bulk-backfilled member is distinguishable from
  // one whose own first login created it. Nullable varchar, not an enum, per
  // the AR-330 convention: `pro_backfill` (row created by the AR-362 backfill),
  // `pro_backfill_merged` (row existed; the backfill filled gaps),
  // `first_login` (AR-205). Rows that predate both stay NULL.
  provisioningSource: varchar("provisioning_source", { length: 32 }),
  provisionedAt: timestamp("provisioned_at"),
  // ── AR-419 · Billing ───────────────────────────────────────────────────────
  // How this member's plan is paid for: `stripe` (a membership subscription in
  // `subscriptions`), `adjustment` (the ten members billed through Finances
  // adjustments, no Stripe subscription), `none` (free legacy plan with no
  // subscription at all). NULL = not yet classified (the AR-427 import fills
  // it). Nullable varchar, not an enum, per the AR-330 convention.
  billingSource: varchar("billing_source", { length: 16 }),
  // (The 12-month plan-change rule reads `subscriptions.plan_changed_at`,
  // AR-344 — not a users column.)
  // ── AR-393 · Platform settings ─────────────────────────────────────────────
  // The three settings on the Settings module's Color Theme page. Attached to
  // the member's login, not the device (Britta, 2026-09-21), so they follow a
  // member to a new browser and a new device.
  //
  // Nullable with no DB default, per the AR-330 convention: "unset" is a real
  // state and the fallback lives in one place (`shared/platform-settings.ts`),
  // so a retired id such as `honeygold` and a NULL both land on Clairvoyant
  // without a second rule in the database.
  //
  // `display_mode` stores the member's CHOICE — light | dark | system — not the
  // resolved appearance; `system` resolves against the device at runtime. It
  // supersedes the single localStorage('darkMode') boolean.
  colorTheme: varchar("color_theme", { length: 32 }),
  displayMode: varchar("display_mode", { length: 16 }),
  textSize: varchar("text_size", { length: 8 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  singleOwnerPerAgency: uniqueIndex("single_agency_owner_per_agency_idx")
    .on(table.agencyId)
    .where(sql`${table.role} = 'AGENCY_OWNER'`),
  parentUserIdx: index("users_parent_user_id_idx").on(table.parentUserId),
}));

export const insertUserSchema = createInsertSchema(users);

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

type SkillPermissionToolLimit = {
  maxCallsPerMinute?: number | null;
  maxCostPerHour?: number | null;
};

export const userSkillPermissions = pgTable(
  "user_skill_permissions",
  {
    id: serial("id").primaryKey(),
    userId: text("user_id").notNull(),
    allowedSkills: jsonb("allowed_skills").$type<string[] | null>(),
    deniedSkills: jsonb("denied_skills").$type<string[] | null>(),
    toolLimits: jsonb("tool_limits").$type<
      Record<string, SkillPermissionToolLimit> | null
    >(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userUnique: uniqueIndex("user_skill_permissions_user_idx").on(table.userId),
  }),
);

export const insertUserSkillPermissionSchema =
  createInsertSchema(userSkillPermissions);

export type InsertUserSkillPermission = z.infer<
  typeof insertUserSkillPermissionSchema
>;
export type UserSkillPermission = typeof userSkillPermissions.$inferSelect;

// AI Chat Schema
// Chat providers enum
export const ChatProviderEnum = z.enum([
  "OPENAI",
  "ANTHROPIC",
  "GEMINI",
  "PERPLEXITY",
  "PINECONE"
]);

export type ChatProvider = z.infer<typeof ChatProviderEnum>;

// Chat project types enum for travel advisors (simplified folder categories)
export const ChatProjectTypeEnum = z.enum([
  "CLIENT",            // Conversations about a specific client
  "DESTINATION",       // Research and planning for a destination
  "SUPPLIER",          // Communications with suppliers/vendors
  "TRAINING",          // Learning and development conversations
  "PERSONAL",          // Personal travel planning
  "GENERAL"            // General/uncategorized conversations
]);

// Chat projects table - lightweight folders for organizing conversations with shared context
export const chatProjects = pgTable("chat_projects", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(), // The travel advisor who owns this folder
  projectId: text("project_id").notNull().unique(), // External identifier for knowledge graph (e.g., "client-smith-vacation-2024")
  name: text("name").notNull(), // Display name (e.g., "Smith Family Vacation 2024")
  description: text("description"), // Optional description
  projectType: text("project_type").notNull().default("GENERAL"),
  color: text("color").default("#3B82F6"), // Folder color for UI
  
  // Advanced project features
  systemInstructions: text("system_instructions"), // Custom AI behavior for this project
  files: jsonb("files").default([]), // Array of uploaded files with metadata
  
  // Metadata for shared context
  metadata: jsonb("metadata"), // Additional context data (client info, destinations, etc.)
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertChatProjectSchema = createInsertSchema(chatProjects);

export const selectChatProjectSchema = createSelectSchema(chatProjects);
export type ChatProject = z.infer<typeof selectChatProjectSchema>;
export type InsertChatProject = z.infer<typeof insertChatProjectSchema>;

// Update chat sessions to include chat project relationship
export const chatSessions = pgTable("chat_sessions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  chatProjectId: integer("chat_project_id"), // Optional chat project/folder association
  title: text("title").notNull(),
  provider: text("provider").notNull(), // Which AI provider is being used
  model: text("model").notNull(), // Which model from that provider
  metadata: jsonb("metadata"), // Additional metadata like smart flag for AIVIA Smart
  pendingResponse: boolean("pending_response").default(false), // True when AI is processing a response
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertChatSessionSchema = createInsertSchema(chatSessions);

export type InsertChatSession = z.infer<typeof insertChatSessionSchema>;
export type ChatSession = typeof chatSessions.$inferSelect;

// Chat messages table - stores the individual messages in a chat
export const chatMessages = pgTable("chat_messages", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").notNull(),
  role: text("role").notNull(), // "user", "assistant", "system" 
  content: text("content").notNull(),
  metadata: jsonb("metadata"),  // Store any additional data like sources, model info, etc.
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertChatMessageSchema = createInsertSchema(chatMessages);

export type InsertChatMessage = z.infer<typeof insertChatMessageSchema>;
export type ChatMessage = typeof chatMessages.$inferSelect;

export const chatPendingActions = pgTable("chat_pending_actions", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id")
    .notNull()
    .references(() => chatSessions.id, { onDelete: "cascade" }),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  userMessageId: integer("user_message_id")
    .references(() => chatMessages.id, { onDelete: "set null" }),
  assistantMessageId: integer("assistant_message_id")
    .references(() => chatMessages.id, { onDelete: "set null" }),
  tool: text("tool").notNull(),
  operation: text("operation").notNull(),
  summary: text("summary").notNull(),
  payload: jsonb("payload").notNull(),
  status: text("status").notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: integer("resolved_by").references(() => users.id, {
    onDelete: "set null",
  }),
  expiresAt: timestamp("expires_at"),
});

export type ChatPendingAction = typeof chatPendingActions.$inferSelect;
export type InsertChatPendingAction =
  typeof chatPendingActions.$inferInsert;

// Privacy tokens for Threads on AgentCore. The model only ever sees tokens such as
// [[client:k7f3a9c2xq]]; this table maps a token back to what it stands for, per user.
// Entity tokens (client/traveler/advisor) store only the record id — the value is read live,
// with the caller's access re-checked. `pii` tokens hold a value the advisor typed (email,
// phone, address, name), encrypted with AES-256-GCM, and expire.
export const agentPrivacyTokens = pgTable(
  "agent_privacy_tokens",
  {
    id: serial("id").primaryKey(),
    token: text("token").notNull(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    kind: text("kind").notNull(), // client | traveler | advisor | pii
    entityId: integer("entity_id"),
    piiType: text("pii_type"), // EMAIL | PHONE | ADDRESS | NAME ... (pii tokens only)
    valueCiphertext: text("value_ciphertext"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    expiresAt: timestamp("expires_at"),
  },
  (table) => ({
    tokenUnique: uniqueIndex("agent_privacy_tokens_token_idx").on(table.token),
    userKindEntityIdx: index("agent_privacy_tokens_user_kind_entity_idx").on(
      table.userId,
      table.kind,
      table.entityId,
    ),
  }),
);

export type AgentPrivacyToken = typeof agentPrivacyTokens.$inferSelect;
export type InsertAgentPrivacyToken = typeof agentPrivacyTokens.$inferInsert;

export type ChatSessionClient = typeof chatSessionClients.$inferSelect;
export type ChatSessionTrip = typeof chatSessionTrips.$inferSelect;
export type McpToolRun = typeof mcpToolRuns.$inferSelect;

// Define relations
export const chatSessionsRelations = relations(chatSessions, ({ one, many }) => ({
  user: one(users, {
    fields: [chatSessions.userId],
    references: [users.id],
  }),
  chatProject: one(chatProjects, {
    fields: [chatSessions.chatProjectId],
    references: [chatProjects.id],
  }),
  messages: many(chatMessages),
  clients: many(chatSessionClients),
  trips: many(chatSessionTrips),
  toolRuns: many(mcpToolRuns),
}));

export const chatMessagesRelations = relations(chatMessages, ({ one }) => ({
  session: one(chatSessions, {
    fields: [chatMessages.sessionId],
    references: [chatSessions.id],
  }),
}));

export const orchestrationExecutions = pgTable(
  "orchestration_executions",
  {
    id: serial("id").primaryKey(),
    projectKey: text("project_key").notNull(),
    projectId: integer("project_id"),
    projectSlug: text("project_slug"),
    projectType: text("project_type"),
    sessionId: integer("session_id").notNull(),
    userId: integer("user_id"),
    requestId: text("request_id").notNull(),
    correlationId: text("correlation_id"),
    traceId: text("trace_id"),
    normalizedPattern: text("normalized_pattern"),
    patternHash: text("pattern_hash"),
    totalSteps: integer("total_steps").notNull().default(0),
    totalTools: integer("total_tools").notNull().default(0),
    successfulTools: integer("successful_tools").notNull().default(0),
    failedTools: integer("failed_tools").notNull().default(0),
    executionStatus: text("execution_status").notNull(),
    success: boolean("success").notNull().default(false),
    latencyMs: integer("latency_ms").notNull(),
    totalCostUsd: decimal("total_cost_usd", { precision: 18, scale: 6 })
      .notNull()
      .default("0"),
    costByProvider: jsonb("cost_by_provider").default({}),
    toolUsage: jsonb("tool_usage").default({}),
    skillUsage: jsonb("skill_usage").default({}),
    cacheHit: boolean("cache_hit").default(false),
    degraded: boolean("degraded").default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    projectCreatedAtIdx: index("idx_orch_exec_project_created_at").on(
      table.projectKey,
      table.createdAt
    ),
    projectPatternIdx: index("idx_orch_exec_project_pattern").on(
      table.projectKey,
      table.patternHash
    ),
    sessionIdx: index("idx_orch_exec_session_created_at").on(
      table.sessionId,
      table.createdAt
    ),
  })
);

export const orchestrationToolInvocations = pgTable(
  "orchestration_tool_invocations",
  {
    id: serial("id").primaryKey(),
    executionId: integer("execution_id")
      .notNull()
      .references(() => orchestrationExecutions.id, { onDelete: "cascade" }),
    stepId: text("step_id").notNull(),
    toolName: text("tool_name").notNull(),
    skillId: text("skill_id"),
    provider: text("provider"),
    model: text("model"),
    required: boolean("required").default(false),
    status: text("status").notNull(),
    latencyMs: integer("latency_ms"),
    costUsd: decimal("cost_usd", { precision: 18, scale: 6 }).default("0"),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    executionIdx: index("idx_orch_tool_execution").on(table.executionId),
    toolIdx: index("idx_orch_tool_name").on(table.toolName),
  })
);

export type OrchestrationExecution =
  typeof orchestrationExecutions.$inferSelect;
export type OrchestrationToolInvocation =
  typeof orchestrationToolInvocations.$inferSelect;
export type ProjectOrchestrationConfigVersionRow =
  typeof projectOrchestrationConfigVersions.$inferSelect;
export type ProjectOrchestrationConfigStateRow =
  typeof projectOrchestrationConfigState.$inferSelect;
export type ProjectOrchestrationConfigAuditRow =
  typeof projectOrchestrationConfigAudit.$inferSelect;
export type TenantSkillConfigRow = typeof tenantSkillConfigs.$inferSelect;
export type TenantSkillConfigAuditRow =
  typeof tenantSkillConfigAudit.$inferSelect;

export const projectOrchestrationConfigVersions = pgTable(
  "project_orchestration_config_versions",
  {
    id: serial("id").primaryKey(),
    projectId: integer("project_id")
      .notNull()
      .references(() => chatProjects.id, { onDelete: "cascade" }),
    version: integer("version").notNull(),
    enabledSkills: jsonb("enabled_skills")
      .$type<string[]>()
      .notNull()
      .default([]),
    disabledTools: jsonb("disabled_tools")
      .$type<OrchestrationTool[]>()
      .notNull()
      .default([]),
    toolLimits: jsonb("tool_limits")
      .$type<ProjectToolLimitRecord>()
      .notNull()
      .default({}),
    sessionLimits: jsonb("session_limits")
      .$type<ProjectSessionLimits>()
      .notNull()
      .default({}),
    executionSettings: jsonb("execution_settings")
      .$type<ProjectExecutionSettings>()
      .notNull()
      .default({
        trackHistory: true,
        storeResults: true,
        enableCaching: true,
        allowParallel: true,
      }),
    reason: text("reason"),
    previousVersion: integer("previous_version"),
    metadata: jsonb("metadata").$type<JsonObject | null>().default(null),
    createdBy: integer("created_by").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    projectVersionIdx: uniqueIndex(
      "project_config_versions_project_version_idx",
    ).on(table.projectId, table.version),
  }),
);

export const projectOrchestrationConfigState = pgTable(
  "project_orchestration_config_state",
  {
    projectId: integer("project_id")
      .primaryKey()
      .references(() => chatProjects.id, { onDelete: "cascade" }),
    activeVersion: integer("active_version"),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    updatedBy: integer("updated_by").references(() => users.id, {
      onDelete: "set null",
    }),
    cacheTag: text("cache_tag"),
  },
);

export const projectOrchestrationConfigAudit = pgTable(
  "project_orchestration_config_audit",
  {
    id: serial("id").primaryKey(),
    projectId: integer("project_id")
      .notNull()
      .references(() => chatProjects.id, { onDelete: "cascade" }),
    action: text("action").notNull(),
    versionFrom: integer("version_from"),
    versionTo: integer("version_to"),
    userId: integer("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    diff: jsonb("diff").$type<JsonObject | null>().default(null),
    reason: text("reason"),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    metadata: jsonb("metadata").$type<JsonObject | null>().default(null),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    projectCreatedIdx: index(
      "project_config_audit_project_created_idx",
    ).on(table.projectId, table.createdAt),
  }),
);

type SkillToggleMap = Record<string, boolean>;

export const tenantSkillConfigs = pgTable(
  "tenant_skill_configs",
  {
    id: serial("id").primaryKey(),
    tenantId: text("tenant_id").notNull(),
    version: integer("version").notNull().default(1),
    overrides: jsonb("overrides").$type<SkillToggleMap>().notNull().default({}),
    metadata: jsonb("metadata").$type<JsonObject | null>().default(null),
    reason: text("reason"),
    updatedBy: integer("updated_by").references(() => users.id, {
      onDelete: "set null",
    }),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    tenantUnique: uniqueIndex(
      "tenant_skill_configs_tenant_idx",
    ).on(table.tenantId),
  }),
);

export const tenantSkillConfigAudit = pgTable(
  "tenant_skill_config_audit",
  {
    id: serial("id").primaryKey(),
    tenantId: text("tenant_id").notNull(),
    action: text("action").notNull(),
    changes: jsonb("changes").$type<JsonObject | null>().default(null),
    reason: text("reason"),
    userId: integer("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    tenantIdx: index("tenant_skill_config_audit_tenant_idx").on(
      table.tenantId,
    ),
  }),
);

export const chatSessionClients = pgTable(
  "chat_session_clients",
  {
    id: serial("id").primaryKey(),
    sessionId: integer("session_id").notNull(),
    clientId: integer("client_id").notNull(),
    linkedAt: timestamp("linked_at").defaultNow().notNull(),
  },
  (table) => ({
    sessionClientUnique: uniqueIndex(
      "chat_session_clients_session_client_unique",
    ).on(table.sessionId, table.clientId),
    sessionIdx: index("chat_session_clients_session_idx").on(
      table.sessionId,
    ),
    clientIdx: index("chat_session_clients_client_idx").on(
      table.clientId,
    ),
  }),
);

export const chatSessionTrips = pgTable(
  "chat_session_trips",
  {
    id: serial("id").primaryKey(),
    sessionId: integer("session_id").notNull(),
    tripId: integer("trip_id").notNull(),
    linkedAt: timestamp("linked_at").defaultNow().notNull(),
  },
  (table) => ({
    sessionTripUnique: uniqueIndex("chat_session_trips_session_trip_unique").on(
      table.sessionId,
      table.tripId,
    ),
    sessionIdx: index("chat_session_trips_session_idx").on(table.sessionId),
    tripIdx: index("chat_session_trips_trip_idx").on(table.tripId),
  }),
);

export const mcpToolRuns = pgTable(
  "mcp_tool_runs",
  {
    id: serial("id").primaryKey(),
    sessionId: integer("session_id").notNull(),
    toolName: text("tool_name").notNull(),
    arguments: jsonb("arguments").notNull().default({}),
    result: jsonb("result"),
    status: text("status").notNull().default("success"),
    error: text("error"),
    durationMs: integer("duration_ms"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    sessionCreatedIdx: index("mcp_tool_runs_session_created_idx").on(
      table.sessionId,
      table.createdAt,
    ),
  }),
);

export const chatSessionClientsRelations = relations(
  chatSessionClients,
  ({ one }) => ({
    session: one(chatSessions, {
      fields: [chatSessionClients.sessionId],
      references: [chatSessions.id],
    }),
    client: one(clients, {
      fields: [chatSessionClients.clientId],
      references: [clients.id],
    }),
  }),
);

export const chatSessionTripsRelations = relations(chatSessionTrips, ({ one }) => ({
  session: one(chatSessions, {
    fields: [chatSessionTrips.sessionId],
    references: [chatSessions.id],
  }),
  trip: one(trips, {
    fields: [chatSessionTrips.tripId],
    references: [trips.id],
  }),
}));

export const mcpToolRunsRelations = relations(mcpToolRuns, ({ one }) => ({
  session: one(chatSessions, {
    fields: [mcpToolRuns.sessionId],
    references: [chatSessions.id],
  }),
}));

export const projectOrchestrationConfigVersionsRelations = relations(
  projectOrchestrationConfigVersions,
  ({ one }) => ({
    project: one(chatProjects, {
      fields: [projectOrchestrationConfigVersions.projectId],
      references: [chatProjects.id],
    }),
    author: one(users, {
      fields: [projectOrchestrationConfigVersions.createdBy],
      references: [users.id],
    }),
  }),
);

export const projectOrchestrationConfigStateRelations = relations(
  projectOrchestrationConfigState,
  ({ one }) => ({
    project: one(chatProjects, {
      fields: [projectOrchestrationConfigState.projectId],
      references: [chatProjects.id],
    }),
    updatedByUser: one(users, {
      fields: [projectOrchestrationConfigState.updatedBy],
      references: [users.id],
    }),
  }),
);

export const projectOrchestrationConfigAuditRelations = relations(
  projectOrchestrationConfigAudit,
  ({ one }) => ({
    project: one(chatProjects, {
      fields: [projectOrchestrationConfigAudit.projectId],
      references: [chatProjects.id],
    }),
    user: one(users, {
      fields: [projectOrchestrationConfigAudit.userId],
      references: [users.id],
    }),
  }),
);

// Site settings table for feature toggles
export const siteSettings = pgTable("site_settings", {
  id: serial("id").primaryKey(),
  smartWorkspaceEnabled: boolean("smart_workspace_enabled").notNull().default(false),
  orchestratorDefaultModel: text("orchestrator_default_model"),
});

export const insertSiteSettingsSchema = createInsertSchema(siteSettings);
export const updateSiteSettingsSchema = insertSiteSettingsSchema.partial();
export type SiteSettings = typeof siteSettings.$inferSelect;
export type InsertSiteSettings = z.infer<typeof insertSiteSettingsSchema>;

// Alpha feature gates - controls non-admin access to core surfaces during alpha
// Each boolean flag controls whether the corresponding feature is accessible to non-admin users
// Admin/System Admin users always have full access regardless of these settings
export const alphaFeatureGates = pgTable("alpha_feature_gates", {
  id: serial("id").primaryKey(),
  // Individual feature toggles - true = enabled for all users, false = admin-only
  clientsEnabled: boolean("clients_enabled").notNull().default(false),
  tripsEnabled: boolean("trips_enabled").notNull().default(false),
  pipelineEnabled: boolean("pipeline_enabled").notNull().default(false),
  dashboardEnabled: boolean("dashboard_enabled").notNull().default(false),
  proposalsEnabled: boolean("proposals_enabled").notNull().default(false),
  // Audit fields
  updatedBy: integer("updated_by").references(() => users.id, { onDelete: "set null" }),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertAlphaFeatureGatesSchema = createInsertSchema(alphaFeatureGates);
export const updateAlphaFeatureGatesSchema = insertAlphaFeatureGatesSchema.partial();
export type AlphaFeatureGates = typeof alphaFeatureGates.$inferSelect;
export type InsertAlphaFeatureGates = z.infer<typeof insertAlphaFeatureGatesSchema>;

// Type for the feature gate keys (useful for frontend/backend type safety)
export const ALPHA_FEATURE_GATE_KEYS = [
  'clients',
  'trips',
  'pipeline',
  'dashboard',
  'proposals',
] as const;
export type AlphaFeatureGateKey = typeof ALPHA_FEATURE_GATE_KEYS[number];

export interface AgenticRolloutCohort {
  name: string;
  percentage: number;
  description?: string | null;
  variant?: string | null;
  allowV2?: boolean;
}

export const agenticRolloutSettings = pgTable("agentic_rollout_settings", {
  id: serial("id").primaryKey(),
  rolloutStage: agenticRolloutStageEnum("rollout_stage")
    .notNull()
    .default("internal"),
  rolloutPercentage: integer("rollout_percentage").notNull().default(0),
  stickinessTtlMs: integer("stickiness_ttl_ms").notNull().default(900000),
  killSwitch: boolean("kill_switch").notNull().default(false),
  abCohorts: jsonb("ab_cohorts")
    .$type<AgenticRolloutCohort[]>()
    .notNull()
    .default([]),
  defaultCohort: text("default_cohort").notNull().default("control"),
  decisionSalt: text("decision_salt")
    .notNull()
    .default("agentic-orchestration"),
  metadata: jsonb("metadata").$type<JsonObject | null>().default(null),
  updatedBy: integer("updated_by").references(() => users.id, {
    onDelete: "set null",
  }),
  createdBy: integer("created_by").references(() => users.id, {
    onDelete: "set null",
  }),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const agenticRolloutOverrides = pgTable(
  "agentic_rollout_overrides",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    mode: agenticRolloutOverrideModeEnum("mode").notNull(),
    cohort: text("cohort").notNull().default("variant"),
    reason: text("reason"),
    expiresAt: timestamp("expires_at"),
    metadata: jsonb("metadata").$type<JsonObject | null>().default(null),
    createdBy: integer("created_by").references(() => users.id, {
      onDelete: "set null",
    }),
    updatedBy: integer("updated_by").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userUniqueIdx: uniqueIndex(
      "agentic_rollout_overrides_user_unique_idx",
    ).on(table.userId),
  }),
);

export const agenticRolloutAuditLog = pgTable(
  "agentic_rollout_audit_log",
  {
    id: serial("id").primaryKey(),
    action: text("action").notNull(),
    actorId: integer("actor_id").references(() => users.id, {
      onDelete: "set null",
    }),
    previousState: jsonb("previous_state")
      .$type<JsonObject | null>()
      .default(null),
    currentState: jsonb("current_state")
      .$type<JsonObject | null>()
      .default(null),
    metadata: jsonb("metadata").$type<JsonObject | null>().default(null),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    auditActionIdx: index("agentic_rollout_audit_action_idx").on(
      table.action,
      table.createdAt,
    ),
  }),
);

export type AgenticRolloutSettingsRow =
  typeof agenticRolloutSettings.$inferSelect;
export type AgenticRolloutSettingsInsert =
  typeof agenticRolloutSettings.$inferInsert;
export type AgenticRolloutOverrideRow =
  typeof agenticRolloutOverrides.$inferSelect;
export type AgenticRolloutOverrideInsert =
  typeof agenticRolloutOverrides.$inferInsert;
export type AgenticRolloutAuditLogRow =
  typeof agenticRolloutAuditLog.$inferSelect;

// Add chat project relations
export const chatProjectsRelations = relations(chatProjects, ({ one, many }) => ({
  user: one(users, {
    fields: [chatProjects.userId],
    references: [users.id],
  }),
  chatSessions: many(chatSessions),
}));

// ── AR-392 · Account center field additions ─────────────────────────────────
// Declared above `userProfiles` because pgEnum instances are evaluated eagerly
// when a column is built.
//
// The Advisor Showcase's publish state: Published or Draft, shown with the last
// publish date in the Brand + Public Presence page header (D-ACCT-32). Two
// columns on user_profiles are enough for what the page shows today; a
// showcase record of its own is only warranted if publish HISTORY is ever wanted.
export const showcasePublishStateEnum = pgEnum("showcase_publish_state", [
  "draft",
  "published",
]);

// `user_profiles.business_type` — the agreed vocabulary (AR-392).
//
// The column used to describe how the advisor works (independent_agent,
// agency_employee, host_agency, entrepreneur); that question is answered by
// `employment_type` since AR-330. The Account center's Your Business page
// reads it as the LEGAL ENTITY TYPE instead (AR-381: "EIN / LLC / SCORP is a
// values change"), and its option list is `ACCOUNT_ENTITY_TYPES` on the
// client. Stored value ↔ label, in that order:
//   sole_proprietor  ↔ Sole proprietor          (an EIN-only filer)
//   llc              ↔ Limited Liability Company
//   s_corp           ↔ S corporation
//   c_corp           ↔ C corporation
//   partnership      ↔ Partnership
// Stays a nullable text column rather than a pgEnum (the AR-330 convention for
// user_profiles): converting the type in place would be a destructive push.
// drizzle/0038_ar392_business_type_values.sql migrates existing rows.
export const BUSINESS_TYPES = [
  "sole_proprietor",
  "llc",
  "s_corp",
  "c_corp",
  "partnership",
] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

/** Display label per stored value — the same strings the Account center offers. */
export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  sole_proprietor: "Sole proprietor",
  llc: "Limited Liability Company",
  s_corp: "S corporation",
  c_corp: "C corporation",
  partnership: "Partnership",
};

/**
 * The one gate every writer of `business_type` goes through: a vocabulary
 * value passes, anything else (empty string, a legacy value, free text from an
 * upstream sync) becomes NULL. Keeps the column from regrowing values that
 * drizzle/0038 just cleared.
 */
export function normalizeBusinessType(value: unknown): BusinessType | null {
  return typeof value === "string" && (BUSINESS_TYPES as readonly string[]).includes(value)
    ? (value as BusinessType)
    : null;
}

// User Profiles for business info, specialties, and content preferences
export const userProfiles = pgTable('user_profiles', {
  id: serial('id').primaryKey(),
  userId: integer('user_id').notNull(),
  
  // Basic Profile Information
  firstName: text('first_name'),
  lastName: text('last_name'),
  displayName: text('display_name'),
  salutation: text('salutation'), // Mr, Mrs, Ms, etc. (from worldvia.pro Salutation_new)
  bio: text('bio'),
  
  // worldvia.pro Identity & Status
  gemStatus: text('gem_status'), // Crystal, Gold, etc. (Active GEM Status)
  proUserRole: text('pro_user_role'), // PRO permission role
  memberSince: timestamp('member_since'), // Start Date from worldvia.pro
  
  // Business Information
  businessName: text('business_name'),
  businessType: text('business_type'), // legal entity type — one of BUSINESS_TYPES (AR-392); was independent_agent / agency_employee / host_agency / entrepreneur
  businessDescription: text('business_description'),
  headline: text('headline'), // Tagline/motto from worldvia.pro
  timezone: text('timezone'), // User's timezone
  yearsExperience: integer('years_experience'), // Legacy field - prefer experienceStartYear
  experienceStartYear: integer('experience_start_year'), // Year advisor started in travel industry
  
  // Contact & Address
  email: text('email'),
  phone: text('phone'),
  website: text('website'),
  city: text('city'), // City Mailing
  state: text('state'), // State Mailing
  zipCode: text('zip_code'), // Zip
  
  // Social Media
  instagramHandle: text('instagram_handle'),
  facebookPage: text('facebook_page'),
  twitterHandle: text('twitter_handle'),
  linkedinProfile: text('linkedin_profile'),
  tiktokHandle: text('tiktok_handle'),
  youtubeChannel: text('youtube_channel'),
  
  // Travel Specialties & Niches (synced from worldvia.pro)
  primarySpecialties: jsonb('primary_specialties'), // Array of strings
  secondarySpecialties: jsonb('secondary_specialties'), // Array of strings
  destinationExpertise: jsonb('destination_expertise'), // Array of destinations
  clientTypes: jsonb('client_types'), // Array: luxury, budget, family, business, etc.
  destinationCities: jsonb('destination_cities'), // Destinations - cities (resolved from IDs)
  destinationCountries: jsonb('destination_countries'), // Destinations_Country (resolved from IDs)
  destinationRegions: jsonb('destination_regions'), // Destinations_Region (resolved from IDs)
  selectedSpecialties: jsonb('selected_specialties'), // Selected Specialties (resolved from IDs)
  userSpecialties: jsonb('user_specialties'), // User Specialties (resolved from IDs)
  travelTypes: jsonb('travel_types'), // CRM_Travel_Type (resolved from IDs)
  clientInterests: jsonb('client_interests'), // Client_Interests
  clientInterestCategories: jsonb('client_interest_categories'), // Category_Client_Interests
  targetTripBudget: jsonb('target_trip_budget'), // Typical Trip - array of budget ranges
  
  // Suppliers & Brands (synced from worldvia.pro)
  preferredSuppliers: jsonb('preferred_suppliers'), // Array of supplier names
  supplierSpecialties: jsonb('supplier_specialties'), // Preferred brands (resolved from IDs)
  certifications: jsonb('certifications'), // Array of certification objects
  languages: jsonb('languages'), // Array of languages spoken
  
  // Content & Communication Style
  writingTone: text('writing_tone'), // 'professional', 'friendly', 'casual', 'luxury', 'adventure'
  communicationStyle: text('communication_style'), // 'detailed', 'concise', 'visual', 'storytelling'
  brandPersonality: jsonb('brand_personality'), // Array of personality traits
  
  // Content Examples for AI Learning
  sampleBlogPosts: jsonb('sample_blog_posts'), // Array of blog post examples
  sampleSocialPosts: jsonb('sample_social_posts'), // Array of social media post examples
  sampleEmails: jsonb('sample_emails'), // Array of email examples
  sampleItineraries: jsonb('sample_itineraries'), // Array of itinerary examples
  
  // Marketing Preferences
  contentTopics: jsonb('content_topics'), // Array of preferred content topics
  postingFrequency: text('posting_frequency'), // 'daily', 'weekly', 'bi-weekly', 'monthly'
  preferredPostTypes: jsonb('preferred_post_types'), // Array: tips, destinations, client_features, etc.
  
  // System Preferences
  aiAssistanceLevel: text('ai_assistance_level').default('balanced'), // 'minimal', 'balanced', 'comprehensive'
  autoContextAwareness: boolean('auto_context_awareness').default(true),
  contentPersonalization: boolean('content_personalization').default(true),
  
  // worldvia.pro Sync
  worldviaLastSyncedAt: timestamp('worldvia_last_synced_at'), // Last successful sync from worldvia.pro
  pendingConflicts: jsonb('pending_conflicts'), // Array of { field, aiviaValue, worldviaValue, worldviaUpdatedAt }
  
  // ── AR-330 · User-domain additive batch ────────────────────────────────────
  // PRO profile fields with no AIVIA home yet. All nullable, no reader.
  legalName: text('legal_name'), // the advisor's own legal name (vs. displayName)
  employmentType: text('employment_type'), // 'independent' | 'employee' | 'corporate'
  slug: text('slug'), // public Advisor Showcase path segment; AR-334 owns the site
  acceptingLeads: boolean('accepting_leads'), // shown on the public showcase
  tshirtSize: text('tshirt_size'), // swag fulfilment, from the PRO signup form
  companyLegalName: text('company_legal_name'), // distinct from businessName above
  companyBrandName: text('company_brand_name'),
  companyEin: text('company_ein'), // sensitive — do not expose in list APIs

  // ── AR-392 · Account center field additions ────────────────────────────────
  // The fields AR-381 found with no source. All nullable, no defaults; the
  // Account center loader (server-nest/src/modules/account-center) binds them.
  nickname: text('nickname'), // User Account — preferred name, distinct from displayName
  secondaryPhone: text('secondary_phone'), // User Account — primary is `phone` above
  // Your Business — the year the ENTITY has traded since. Not
  // `experienceStartYear`, which is the advisor's own start in travel (AR-381
  // verified the two answer different questions).
  inBusinessSince: integer('in_business_since'),
  // Plan + Agreements — the advisor's own industry credentials. Advisor-entered
  // and unverified: WorldVia neither fills nor checks them, so there are no
  // status / verified_at columns by design (D-ACCT-41).
  cliaNumber: text('clia_number'),
  iatanNumber: text('iatan_number'),
  sellerOfTravelLicense: text('seller_of_travel_license'), // state Seller of Travel licence
  // Brand + Public Presence — page header (D-ACCT-32).
  showcasePublishState: showcasePublishStateEnum('showcase_publish_state'),
  showcaseLastPublishedAt: timestamp('showcase_last_published_at'),

  // Metadata
  isComplete: boolean('is_complete').default(false),
  lastUpdated: timestamp('last_updated').defaultNow().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Insert and select schemas for user profiles
export const insertUserProfileSchema = createInsertSchema(userProfiles);
export const selectUserProfileSchema = createSelectSchema(userProfiles);

export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type SelectUserProfile = z.infer<typeof selectUserProfileSchema>;

// Profile conflict type for worldvia.pro sync
export interface ProfileConflict {
  field: string;
  fieldLabel: string;
  aiviaValue: string | null;
  worldviaValue: string | null;
  worldviaUpdatedAt?: string;
  aiviaUpdatedAt?: string;
}

// Session table for express-session (managed by connect-pg-simple)
// This table is created and managed by the session middleware, not by our app
// We'll exclude this from schema management to avoid conflicts
// export const sessions = pgTable('session', {
//   sid: text('sid').primaryKey(),
//   sess: jsonb('sess').notNull(),
//   expire: timestamp('expire').notNull(),
// });

// MCP Tool Management for System Administrators
export const mcpToolSettings = pgTable('mcp_tool_settings', {
  id: serial('id').primaryKey(),
  toolName: text('tool_name').notNull().unique(), // e.g., 'worldvia_rag_knowledge_base', 'web_search'
  displayName: text('display_name').notNull(), // e.g., 'WorldVia Knowledge Base', 'Web Search'
  description: text('description'), // Tool description for admins
  serverName: text('server_name').notNull(), // e.g., 'pinecone', 'websearch', 'firecrawl'
  category: text('category').notNull().default('general'), // 'knowledge', 'search', 'automation', 'analysis'
  isEnabled: boolean('is_enabled').notNull().default(true), // Global enable/disable flag
  isSystemCritical: boolean('is_system_critical').notNull().default(false), // Prevent disabling critical tools
  lastModifiedBy: integer('last_modified_by'), // User ID who last modified this setting
  lastModifiedAt: timestamp('last_modified_at').defaultNow().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Relations for MCP tool settings
export const mcpToolSettingsRelations = relations(mcpToolSettings, ({ one }) => ({
  lastModifiedByUser: one(users, {
    fields: [mcpToolSettings.lastModifiedBy],
    references: [users.id],
  }),
}));

// Zod schemas for MCP tool settings
export const insertMcpToolSettingsSchema = createInsertSchema(mcpToolSettings);
export const selectMcpToolSettingsSchema = createSelectSchema(mcpToolSettings);
export const updateMcpToolSettingsSchema = insertMcpToolSettingsSchema.partial();

export type McpToolSettings = typeof mcpToolSettings.$inferSelect;
export type InsertMcpToolSettings = typeof mcpToolSettings.$inferInsert;

// Tool call feedback table for admin debugging and prompt improvement
export const toolCallFeedback = pgTable("tool_call_feedback", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").notNull(),
  messageId: integer("message_id").notNull(),
  userQuery: text("user_query").notNull(),
  actualToolCalls: jsonb("actual_tool_calls").default([]), // What tools were actually called
  expectedToolCall: text("expected_tool_call"), // What tool admin expected to be called
  expectedArguments: jsonb("expected_arguments"), // What arguments admin expected
  feedbackReason: text("feedback_reason"), // Why the admin thinks the actual call was wrong
  feedbackType: text("feedback_type").notNull().default("missing_tool_call"), // missing_tool_call, wrong_tool_call, wrong_arguments
  adminUserId: integer("admin_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const toolCallFeedbackRelations = relations(toolCallFeedback, ({ one }) => ({
  session: one(chatSessions, {
    fields: [toolCallFeedback.sessionId],
    references: [chatSessions.id],
  }),
  message: one(chatMessages, {
    fields: [toolCallFeedback.messageId],
    references: [chatMessages.id],
  }),
  adminUser: one(users, {
    fields: [toolCallFeedback.adminUserId],
    references: [users.id],
  }),
}));

// Zod schemas for tool call feedback
export const insertToolCallFeedbackSchema = createInsertSchema(toolCallFeedback);

export const selectToolCallFeedbackSchema = createSelectSchema(toolCallFeedback);

export type ToolCallFeedback = typeof toolCallFeedback.$inferSelect;
export type InsertToolCallFeedback = typeof toolCallFeedback.$inferInsert;

export const chatMessageReplays = pgTable(
  "chat_message_replays",
  {
    id: serial("id").primaryKey(),
    sessionId: integer("session_id")
      .notNull()
      .references(() => chatSessions.id, { onDelete: "cascade" }),
    userMessageId: integer("user_message_id").references(
      () => chatMessages.id,
      { onDelete: "set null" },
    ),
    assistantMessageId: integer("assistant_message_id")
      .notNull()
      .references(() => chatMessages.id, { onDelete: "cascade" }),
    requestId: text("request_id").notNull(),
    plannerProvider: text("planner_provider"),
    plannerModel: text("planner_model"),
    plannerLatencyMs: integer("planner_latency_ms"),
    plannerAttempt: integer("planner_attempt"),
    plannerDegraded: boolean("planner_degraded").default(false),
    decision: jsonb("decision"),
    sanitizedUserInput: text("sanitized_user_input"),
    injectedContext: jsonb("injected_context"),
    toolRouting: jsonb("tool_routing"),
    orchestrationPlan: jsonb("orchestration_plan"),
    eventTrace: jsonb("event_trace"),
    toolExecutions: jsonb("tool_executions"),
    finalResponse: text("final_response").notNull(),
    finalMetadata: jsonb("final_metadata"),
    fallbackReason: text("fallback_reason"),
    degradedReason: text("degraded_reason"),
    version: text("version").notNull().default("v1"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    sessionIdx: index("idx_chat_msg_replays_session_created").on(
      table.sessionId,
      table.createdAt,
    ),
    assistantUnique: uniqueIndex("uniq_chat_msg_replay_assistant").on(
      table.assistantMessageId,
    ),
  }),
);

export const insertChatMessageReplaySchema =
  createInsertSchema(chatMessageReplays);
export const selectChatMessageReplaySchema =
  createSelectSchema(chatMessageReplays);
export type ChatMessageReplay = typeof chatMessageReplays.$inferSelect;
export type InsertChatMessageReplay = typeof chatMessageReplays.$inferInsert;

export const chatMessageFeedback = pgTable(
  "chat_message_feedback",
  {
    id: serial("id").primaryKey(),
    replayId: integer("replay_id").references(() => chatMessageReplays.id, {
      onDelete: "cascade",
    }),
    sessionId: integer("session_id")
      .notNull()
      .references(() => chatSessions.id, { onDelete: "cascade" }),
    messageId: integer("message_id")
      .notNull()
      .references(() => chatMessages.id, { onDelete: "cascade" }),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    rating: integer("rating").notNull(),
    comment: text("comment"),
    metadata: jsonb("metadata"),
    submittedAt: timestamp("submitted_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    sessionIdx: index("idx_chat_msg_feedback_session").on(
      table.sessionId,
      table.submittedAt,
    ),
    ratingIdx: index("idx_chat_msg_feedback_rating").on(
      table.rating,
      table.submittedAt,
    ),
    messageUserUnique: uniqueIndex("uniq_chat_msg_feedback_user").on(
      table.messageId,
      table.userId,
    ),
  }),
);

export const insertChatMessageFeedbackSchema =
  createInsertSchema(chatMessageFeedback);
export const selectChatMessageFeedbackSchema =
  createSelectSchema(chatMessageFeedback);
export type ChatMessageFeedback = typeof chatMessageFeedback.$inferSelect;
export type InsertChatMessageFeedback =
  typeof chatMessageFeedback.$inferInsert;

// Cached Posts Schema (for Bubble.io/WorldViaPro news/events)
export const cachedPosts = pgTable('cached_posts', {
  id: serial('id').primaryKey(),
  externalId: text('external_id').notNull().unique(), // Bubble.io post ID
  postType: text('post_type').notNull(), // 'News' or 'Event'
  title: text('title').notNull(),
  summary: text('summary'), // The summary field from Bubble  
  body: text('body'), // The body content
  slug: text('slug').notNull(), // The Slug field for URL construction
  imageFeatured: text('image_featured'), // The featured image URL
  eventDate: timestamp('event_date'), // For events - parsed from the data
  status: text('status'), // 'Published', etc.
  rawData: jsonb('raw_data'), // Store complete Bubble response for reference
  createdAt: timestamp('created_at').notNull().defaultNow(),
  updatedAt: timestamp('updated_at').notNull().defaultNow(),
  fetchedAt: timestamp('fetched_at').notNull().defaultNow(),
});

export const insertCachedPostSchema = createInsertSchema(cachedPosts);
export const selectCachedPostSchema = createSelectSchema(cachedPosts);

export type CachedPost = typeof cachedPosts.$inferSelect;
export type InsertCachedPost = typeof cachedPosts.$inferInsert;

// ============================================================================
// Suppliers Schema (synced from worldvia.pro/Bubble.io)
// See: agent2/tasks/T-389-command-bar-supplier-list-sync-from-worldvia-pro.md
// ============================================================================

// Supplier category enum for type safety
export const SupplierCategoryEnum = z.enum([
  'Cruise',
  'River Cruise',
  'Tour',
  'Luxury Tour',
  'Private Tour',
  'Package',
  'Hotel',
  'Luxury Hotel',
  'Air',
  'Rail',
  'Car Rental',
  'Insurance',
  'DMC',
  'Other'
]);

export type SupplierCategory = z.infer<typeof SupplierCategoryEnum>;

export const suppliers = pgTable('suppliers', {
  id: serial('id').primaryKey(),
  externalId: text('external_id').notNull().unique(), // Bubble.io supplier ID (stable upstream reference)
  slug: text('slug').notNull(), // URL-safe slug for worldvia.pro links
  name: text('name').notNull(), // Display name
  category: text('category'), // Supplier type/category (e.g., 'Cruise', 'Tour', 'Hotel')
  brandGroup: text('brand_group'), // Parent brand group if applicable
  primaryContact: text('primary_contact'), // Primary contact name
  contactEmail: text('contact_email'), // Contact email
  contactPhone: text('contact_phone'), // Contact phone
  portalUrl: text('portal_url'), // Supplier agent portal URL
  worldviaProUrl: text('worldvia_pro_url'), // Direct link to worldvia.pro supplier page
  baseCommissionRate: text('base_commission_rate'), // e.g., '16%'
  isPreferred: boolean('is_preferred').notNull().default(false), // Preferred supplier flag
  tramsLinkNo: integer('trams_link_no').unique(), // TRAMS PROFILE.PROFILENO for vendor lookup
  travelTypes: jsonb('travel_types').$type<string[] | null>().default(null), // Supported TRAMS travel types (e.g. ["Ship-Dpst","Ship-Finl"])
  isActive: boolean('is_active').notNull().default(true), // Soft delete / active status
  metadata: jsonb('metadata'), // Additional metadata from worldvia.pro
  createdAt: timestamp('created_at').notNull().defaultNow(),
  updatedAt: timestamp('updated_at').notNull().defaultNow(),
  fetchedAt: timestamp('fetched_at').notNull().defaultNow(), // Last sync timestamp
});

export const insertSupplierSchema = createInsertSchema(suppliers);
export const selectSupplierSchema = createSelectSchema(suppliers);

export type Supplier = typeof suppliers.$inferSelect;
export type InsertSupplier = typeof suppliers.$inferInsert;

// ============================================================================
// Supplier Cards Schema (synced from external API)
// Used for supplier context cards in the UI
// ============================================================================

export const supplierCards = pgTable('supplier_cards', {
  id: serial('id').primaryKey(),
  slug: text('slug').notNull().unique(), // Unique identifier for upsert (from API)
  supplierName: text('supplier_name').notNull(),
  supplierCategory: text('supplier_category'),
  commissionBase: text('commission_base'),
  supplierContactName: text('supplier_contact_name'),
  supplierContactEmail: text('supplier_contact_email'),
  supplierBookingPhone: text('supplier_booking_phone'),
  supplierPortalUrl: text('supplier_portal_url'),
  rawData: jsonb('raw_data'), // Original JSON data from API for debugging
  createdAt: timestamp('created_at').notNull().defaultNow(),
  updatedAt: timestamp('updated_at').notNull().defaultNow(),
  syncedAt: timestamp('synced_at').notNull().defaultNow(), // Last sync timestamp
});

export const insertSupplierCardSchema = createInsertSchema(supplierCards);
export const selectSupplierCardSchema = createSelectSchema(supplierCards);

export type SupplierCard = typeof supplierCards.$inferSelect;
export type InsertSupplierCard = typeof supplierCards.$inferInsert;

// ============================================================================
// PRO (worldvia.pro / Bubble) mirror tables — AR-377
// Field-for-field copies of eleven Bubble data types. Column = the Bubble field
// name through proMirrorColumnName(); types follow Bubble's field types
// (GET /api/1.1/meta). Reference fields hold Bubble ids (lists as text[]),
// option sets are plain text, and raw_data keeps the full Bubble record.
// unique_id (Bubble _id) is the PK/upsert key; every other column is nullable.
// No FKs: references stay raw Bubble ids.
// AR-378 sync: missing_from_bubble_at is set when a full sync no longer finds
// the record in Bubble (rows are never deleted) and cleared if it reappears.
// ============================================================================

/** Bubble field name → mirror column: lowercase, non-alphanumeric runs → "_".
 *  The Data API's system fields map to the CSV-named columns. */
export const proMirrorColumnName = (bubbleField: string) => {
  switch (bubbleField) {
    case '_id': return 'unique_id';
    case 'Created Date': return 'creation_date';
    case 'Created By': return 'creator';
    default: return bubbleField.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  }
};

// Post — Bubble type `post`, 135 columns.
export const proPost = pgTable('pro_post', {
  '25Destinations': text('25_destinations').array(),
  '25Regions': text('25_regions').array(),
  academyFeatured: boolean('academy_featured'),
  aiviaBubbleFile: text('aivia_bubble_file'),
  aiviaLoaded: boolean('aivia_loaded'),
  aiviaPineconeId: text('aivia_pinecone_id'),
  aiviaPineconeTxtFileUrl: text('aivia_pinecone_txt_file_url'),
  alg: text('alg'),
  allowAdvisorSpecialty: boolean('allow_advisor_specialty'),
  alphaList: text('alpha_list'),
  altText: text('alt_text'),
  author: text('author'),
  bannerImages: text('banner_images'),
  body: text('body'),
  commission: text('commission').array(),
  commissionFile: text('commission_file'),
  commissionLink: text('commission_link'),
  contactMapLinkFile: text('contact_map_link_file'),
  contactMapLinkLink: text('contact_map_link_link'),
  copy: doublePrecision('copy'),
  countriesApi: jsonb('countries_api'),
  createTicketButtonLabel: text('create_ticket_button_label'),
  ctaTitle: text('cta_title'),
  ctaUrl: text('cta_url'),
  customLabel1: text('custom_label_1'),
  customLabel2: text('custom_label_2'),
  customLabel3: text('custom_label_3'),
  deleteDudaWorkflowId: text('delete_duda_workflow_id'),
  destinations: text('destinations').array(),
  dudaIdLinks: text('duda_id_links').array(),
  durationSeconds: doublePrecision('duration_seconds'),
  dynamicPromotion: boolean('dynamic_promotion'),
  emailCustomSubject: text('email_custom_subject'),
  episodeCategory: text('episode_category'),
  episodeNumber: doublePrecision('episode_number'),
  eventCategory: text('event_category'),
  eventEndTime: timestamp('event_end_time', { withTimezone: true }),
  eventHost: text('event_host').array(),
  eventInfoUrl: text('event_info_url'),
  eventMultidayRange: timestamp('event_multiday_range', { withTimezone: true }).array(),
  eventRegistrationUrl: text('event_registration_url'),
  eventStartTime: timestamp('event_start_time', { withTimezone: true }),
  eventType: text('event_type'),
  expirationDate: timestamp('expiration_date', { withTimezone: true }),
  featuredSection: boolean('featured_section'),
  files: text('files').array(),
  formEmbedCode: text('form_embed_code'),
  freshdeskTicketId: text('freshdesk_ticket_id'),
  idTraveltekCruise: text('id_traveltek_cruise'),
  idWfEmail: text('id_wf_email'),
  idWfExpirationDate: text('id_wf_expiration_date'),
  idWfNewsletter: text('id_wf_newsletter'),
  imageAdditional: text('image_additional').array(),
  imageFeatured: text('image_featured'),
  imageFeaturedId: text('image_featured_id'),
  inPersonRegistrationUrl: text('in_person_registration_url'),
  informationUrl: text('information_url').array(),
  informationUrlNew: text('information_url_new'),
  introductoryVideo: text('introductory_video'),
  issueStatus: text('issue_status'),
  jiraTicketId: text('jira_ticket_id'),
  linkedPost: text('linked_post').array(),
  linkedSupplier: text('linked_supplier').array(),
  lmsPlaylist: text('lms_playlist').array(),
  newsCategory: text('news_category').array(),
  newsletterFeatured: boolean('newsletter_featured'),
  notification: doublePrecision('notification'),
  otherCommission: text('other_commission'),
  postCopy: text('post_copy'),
  postType: text('post_type'),
  preferredStatus: text('preferred_status'),
  promotionBookingDates: timestamp('promotion_booking_dates', { withTimezone: true }).array(),
  promotionCategory: text('promotion_category'),
  promotionCode: text('promotion_code'),
  promotionLabel: text('promotion_label'),
  promotionTravelDates: timestamp('promotion_travel_dates', { withTimezone: true }).array(),
  publicPromotionDescriptionText: text('public_promotion_description_text'),
  publicPromotionHeadline: text('public_promotion_headline'),
  publicPromotionImage: text('public_promotion_image'),
  publicPromotionTitle: text('public_promotion_title'),
  publicPromotionsTerms: text('public_promotions_terms'),
  publishDate: timestamp('publish_date', { withTimezone: true }),
  recommendedAlternatives: text('recommended_alternatives').array(),
  region: text('region').array(),
  searchField: text('search_field'),
  shipCruise: text('ship_cruise').array(),
  showGroups: boolean('show_groups'),
  showAtTheSupplierSource: boolean('show_at_the_supplier_source'),
  solutionCategory: text('solution_category').array(),
  solutionSortOrder: doublePrecision('solution_sort_order'),
  solutionSubcategory: text('solution_subcategory').array(),
  sortCommissionActivitiesLiveMin: doublePrecision('sort_commission_activities_live_min'),
  sortCommissionAirLiveMin: doublePrecision('sort_commission_air_live_min'),
  sortCommissionAttractionsLiveMin: doublePrecision('sort_commission_attractions_live_min'),
  sortCommissionBaseLiveMin: doublePrecision('sort_commission_base_live_min'),
  sortCommissionLandLiveMin: doublePrecision('sort_commission_land_live_min'),
  sortCommissionProductLiveMin: doublePrecision('sort_commission_product_live_min'),
  sortCommissionTransfersLiveMin: doublePrecision('sort_commission_transfers_live_min'),
  status: text('status'),
  summary: text('summary'),
  supplierBooking: text('supplier_booking'),
  supplierCategory: text('supplier_category').array(),
  supplierCommissionOld: text('supplier_commission_old'),
  supplierContact: text('supplier_contact').array(),
  supplierEducation: text('supplier_education').array(),
  supplierListingLink1: text('supplier_listing_link_1'),
  supplierListingLink2: text('supplier_listing_link_2'),
  supplierListingLink3: text('supplier_listing_link_3'),
  supplierPolicy: text('supplier_policy').array(),
  supplierProfile: text('supplier_profile'),
  supplierRegistration: text('supplier_registration'),
  supplierReview: text('supplier_review').array(),
  supplierReward: text('supplier_reward').array(),
  tagCategories: text('tag_categories').array(),
  tags: text('tags').array(),
  title: text('title'),
  topStory: boolean('top_story'),
  update: text('update'),
  uploadSup: text('upload_sup'),
  userRoles: text('user_roles').array(),
  videoUrl: text('video_url'),
  videoHrs: doublePrecision('video_hrs'),
  videoMins: doublePrecision('video_mins'),
  videoSecs: doublePrecision('video_secs'),
  videoShort: boolean('video_short'),
  views: doublePrecision('views'),
  virtualRegistrationUrl: text('virtual_registration_url'),
  visibility: text('visibility'),
  votes: doublePrecision('votes'),
  wfIdPublishDate: text('wf_id_publish_date'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Booking — Bubble type `supplierbooking`, 27 columns.
export const proSupplierBooking = pgTable('pro_supplier_booking', {
  agencyAccountNumber: text('agency_account_number'),
  agencyAccountPhone: text('agency_account_phone'),
  altText: text('alt_text'),
  bookingProcedure: text('booking_procedure'),
  bookingProcedureImage: text('booking_procedure_image').array(),
  bookingUrl: text('booking_url'),
  emailGroupReservations: text('email_group_reservations'),
  emailReservations: text('email_reservations'),
  otherEmailLabel1: text('other_email_label_1'),
  otherEmailLabel2: text('other_email_label_2'),
  otherEmail1: text('other_email_1'),
  otherEmail2: text('other_email_2'),
  otherPhoneLabel1: text('other_phone_label_1'),
  otherPhoneLabel2: text('other_phone_label_2'),
  otherPhone1: text('other_phone_1'),
  otherPhone2: text('other_phone_2'),
  phoneGroupReservations: text('phone_group_reservations'),
  phoneGroupReservationsExt: text('phone_group_reservations_ext'),
  phoneReservations: text('phone_reservations'),
  phoneReservationsExt: text('phone_reservations_ext'),
  supplierPost: text('supplier_post'),
  travelportCruise: boolean('travelport_cruise'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Category — Bubble type `suppliercategory`, 10 columns.
export const proSupplierCategory = pgTable('pro_supplier_category', {
  childCategory: text('child_category').array(),
  image: text('image'),
  parentCategory: text('parent_category'),
  sortLevel: doublePrecision('sort_level'),
  title: text('title'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Commission (New) — Bubble type `suppliercommission(new)`, 14 columns.
export const proSupplierCommissionNew = pgTable('pro_supplier_commission_new', {
  comissionCategoryDev: text('comission_category_dev'),
  comissionCategoryLive: text('comission_category_live'),
  comissionLabelDev: text('comission_label_dev'),
  comissionLabelLive: text('comission_label_live'),
  comissionRateMaxDev: doublePrecision('comission_rate_max_dev'),
  comissionRateMaxLive: doublePrecision('comission_rate_max_live'),
  comissionRateMinDev: doublePrecision('comission_rate_min_dev'),
  comissionRateMinLive: doublePrecision('comission_rate_min_live'),
  supplier: text('supplier'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Contact — Bubble type `suppliercontact`, 10 columns.
export const proSupplierContact = pgTable('pro_supplier_contact', {
  primaryContact: text('primary_contact'),
  primaryContactInternal: text('primary_contact_internal'),
  secondaryContacts: text('secondary_contacts').array(),
  secondaryContactsInternal: text('secondary_contacts_internal').array(),
  supplierPost: text('supplier_post'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Contact User — Bubble type `suppliercontactuser`, 13 columns.
export const proSupplierContactUser = pgTable('pro_supplier_contact_user', {
  email: text('email'),
  fullName: text('full_name'),
  jobTitle: text('job_title'),
  phone: text('phone'),
  phoneExt: text('phone_ext'),
  secondaryPhone: text('secondary_phone'),
  secondaryPhoneExt: text('secondary_phone_ext'),
  user: text('user'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Education — Bubble type `suppliereducation`, 11 columns.
export const proSupplierEducation = pgTable('pro_supplier_education', {
  altText: text('alt_text'),
  programDescription: text('program_description'),
  programImage: text('program_image').array(),
  programTitle: text('program_title'),
  programUrl: text('program_url'),
  supplierPost: text('supplier_post'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Policy — Bubble type `supplierpolicy`, 10 columns.
export const proSupplierPolicy = pgTable('pro_supplier_policy', {
  policyDescription: text('policy_description'),
  policyFile: text('policy_file'),
  policyTitle: text('policy_title'),
  policyUrl: text('policy_url'),
  supplierPost: text('supplier_post'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Profile — Bubble type `supplierprofile`, 11 columns.
export const proSupplierProfile = pgTable('pro_supplier_profile', {
  altText: text('alt_text'),
  consumerSiteUrl: text('consumer_site_url'),
  logo: text('logo'),
  logoSquare: text('logo_square'),
  preferred: text('preferred'),
  supplierPost: text('supplier_post'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Registration — Bubble type `supplierregistration`, 11 columns.
export const proSupplierRegistration = pgTable('pro_supplier_registration', {
  altText: text('alt_text'),
  registrationImage: text('registration_image').array(),
  registrationInstructions: text('registration_instructions'),
  registrationUrl: text('registration_url'),
  selfRegister: boolean('self_register'),
  supplierPost: text('supplier_post'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

// Supplier Reward — Bubble type `supplierreward`, 11 columns.
export const proSupplierReward = pgTable('pro_supplier_reward', {
  altText: text('alt_text'),
  rewardDescription: text('reward_description'),
  rewardImage: text('reward_image').array(),
  rewardTitle: text('reward_title'),
  rewardUrl: text('reward_url'),
  supplierPost: text('supplier_post'),
  creationDate: timestamp('creation_date', { withTimezone: true }),
  modifiedDate: timestamp('modified_date', { withTimezone: true }),
  slug: text('slug'),
  creator: text('creator'),
  uniqueId: text('unique_id').primaryKey(),
  rawData: jsonb('raw_data'),
  syncedAt: timestamp('synced_at', { withTimezone: true }).defaultNow(),
  missingFromBubbleAt: timestamp('missing_from_bubble_at', { withTimezone: true }),
});

/** Bubble Data API type name → mirror table. */
export const proMirrorTables = {
  post: proPost,
  supplierbooking: proSupplierBooking,
  suppliercategory: proSupplierCategory,
  'suppliercommission(new)': proSupplierCommissionNew,
  suppliercontact: proSupplierContact,
  suppliercontactuser: proSupplierContactUser,
  suppliereducation: proSupplierEducation,
  supplierpolicy: proSupplierPolicy,
  supplierprofile: proSupplierProfile,
  supplierregistration: proSupplierRegistration,
  supplierreward: proSupplierReward,
} as const;

export type ProPost = typeof proPost.$inferSelect;
export type ProSupplierBooking = typeof proSupplierBooking.$inferSelect;
export type ProSupplierCategory = typeof proSupplierCategory.$inferSelect;
export type ProSupplierCommissionNew = typeof proSupplierCommissionNew.$inferSelect;
export type ProSupplierContact = typeof proSupplierContact.$inferSelect;
export type ProSupplierContactUser = typeof proSupplierContactUser.$inferSelect;
export type ProSupplierEducation = typeof proSupplierEducation.$inferSelect;
export type ProSupplierPolicy = typeof proSupplierPolicy.$inferSelect;
export type ProSupplierProfile = typeof proSupplierProfile.$inferSelect;
export type ProSupplierRegistration = typeof proSupplierRegistration.$inferSelect;
export type ProSupplierReward = typeof proSupplierReward.$inferSelect;

// AR-378: one row per PRO mirror sync — a full sync of every mirrored type, a
// changes-only sync of posts modified since the last good run (AR-469), or a
// single record refetched by Bubble id. The partial unique index allows one
// running full or changes-only sync at a time across every API task (the
// cross-task lock); a run whose heartbeat goes stale is closed as failed by the
// next trigger.
export const proSyncRuns = pgTable(
  'pro_sync_runs',
  {
    id: serial('id').primaryKey(),
    scope: text('scope').notNull(), // 'full' | 'incremental' | 'single'
    status: text('status').notNull().default('running'), // 'running' | 'succeeded' | 'failed'
    trigger: text('trigger').notNull(), // 'schedule' | 'admin'
    triggeredByUserId: integer('triggered_by_user_id').references(() => users.id, { onDelete: 'set null' }),
    bubbleType: text('bubble_type'), // single-record runs only
    bubbleId: text('bubble_id'), // single-record runs only
    sourceOrigin: text('source_origin').notNull(), // Bubble origin read, e.g. https://worldvia.pro
    startedAt: timestamp('started_at', { withTimezone: true }).notNull().defaultNow(),
    heartbeatAt: timestamp('heartbeat_at', { withTimezone: true }).notNull().defaultNow(),
    finishedAt: timestamp('finished_at', { withTimezone: true }),
    createdCount: integer('created_count').notNull().default(0),
    updatedCount: integer('updated_count').notNull().default(0),
    unchangedCount: integer('unchanged_count').notNull().default(0),
    missingCount: integer('missing_count').notNull().default(0),
    failedCount: integer('failed_count').notNull().default(0),
    tableStats: jsonb('table_stats'), // per Bubble type: source total, fetched, created, updated, …
    error: text('error'),
  },
  (table) => ({
    oneBulkRunning: uniqueIndex('pro_sync_runs_one_bulk_running')
      .on(table.status)
      .where(sql`${table.status} = 'running' AND ${table.scope} IN ('full', 'incremental')`),
    startedAtIdx: index('pro_sync_runs_started_at_idx').on(table.startedAt),
  }),
);

// AR-378: records a sync could not map or write, with the reason. The run carries on.
export const proSyncFailures = pgTable(
  'pro_sync_failures',
  {
    id: serial('id').primaryKey(),
    runId: integer('run_id').notNull().references(() => proSyncRuns.id),
    bubbleType: text('bubble_type').notNull(),
    bubbleId: text('bubble_id'),
    reason: text('reason').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    runIdx: index('pro_sync_failures_run_id_idx').on(table.runId),
  }),
);

export type ProSyncRun = typeof proSyncRuns.$inferSelect;
export type ProSyncFailure = typeof proSyncFailures.$inferSelect;

// ============================================================================
// TRAMS Suppliers (synced from Middle Layer REST API)
// Source-of-truth for supplier_name + travel_type selection in invoice flow.
// Nightly sync pulls TRAMS vendor profiles that satisfy ALL of:
//   - PROFILETYPE_LINKCODE = 'V' (Vendor)
//   - ISACTIVE = 'Y'
//   - A MARKETING record with MARKETINGVALUE = 'GRASP' (Sept 2026 rollout;
//     replaces the legacy `PROFILE.NOTES contains 'AIVIA'` requirement).
// Filtering is applied upstream in the Lambda `/profile/suppliers` query;
// the AIVIA-side sync trusts the response as the eligible set.
// See `server-nest/src/modules/invoices/trams-supplier-sync.service.ts`
// and `trams-supplier-sync-mode.ts` for the rollout mode contract.
// ============================================================================

export const tramsSuppliers = pgTable(
  'trams_suppliers',
  {
    // Primary key — PROFILENO from InterBase. NOT auto-incremented; we use the source ID.
    supplierId: integer('supplier_id').primaryKey(),
    supplierName: varchar('supplier_name', { length: 150 }).notNull(),
    preferredVendor: varchar('preferred_vendor', { length: 1 }).notNull().default('N'),
    isActive: varchar('is_active', { length: 1 }).notNull().default('Y'),
    modifiedDate: timestamp('modified_date', { withTimezone: false }).notNull(),
    createdDate: timestamp('created_date', { withTimezone: false }).notNull(),
    agencyLinkNo: integer('agency_link_no').notNull().default(0),
    unappliedBalance: decimal('unapplied_balance', { precision: 18, scale: 4 })
      .notNull()
      .default('0'),
    cbNotesBlobTypeLinkNo: integer('cbnotesblobtype_linkno').notNull().default(2),
    // AIVIA-only: "Y" if supplier has travel type 9 or 10 (Tour) — drives property-name field visibility.
    tourPropertyVisible: varchar('tour_property_visible', { length: 1 }).notNull().default('N'),
    // AIVIA-only: "Y" to show the Tour Name field independently of tourPropertyVisible.
    tourNameVisible: varchar('tour_name_visible', { length: 1 }).notNull().default('N'),
    // AIVIA-only: "Y" if the supplier is bookable through Expedia.
    expediaBookable: varchar('expedia_bookable', { length: 1 }).notNull().default('N'),
    syncedAt: timestamp('synced_at').notNull().defaultNow(),
  },
  (table) => ({
    nameIdx: index('trams_suppliers_name_idx').on(table.supplierName),
    activeIdx: index('trams_suppliers_is_active_idx').on(table.isActive),
    preferredIdx: index('trams_suppliers_preferred_idx').on(table.preferredVendor),
  }),
);

export const tramsSupplierTravelTypes = pgTable(
  'trams_supplier_travel_types',
  {
    supplierId: integer('supplier_id')
      .notNull()
      .references(() => tramsSuppliers.supplierId, { onDelete: 'cascade' }),
    travelTypeLinkNo: integer('traveltype_linkno').notNull(),
    travelTypeName: varchar('traveltype_name', { length: 40 }).notNull(),
    agencyLinkNo: integer('agency_link_no').notNull().default(0),
  },
  (table) => ({
    pk: uniqueIndex('trams_supplier_travel_types_pk').on(
      table.supplierId,
      table.travelTypeLinkNo,
    ),
    supplierIdx: index('trams_supplier_travel_types_supplier_idx').on(table.supplierId),
  }),
);

/**
 * Highest TRAMS `eventSeq` already applied for an invoice, for last-writer-wins
 * on v2 sync callbacks.
 *
 * Every v2 payload is a complete snapshot rather than a delta, so a stale
 * delivery does not merely arrive late — it would revert every booking on the
 * invoice to older state. TRAMS retries resend the stored payload with its
 * original sequence number while a coalesce rebuilds and draws a higher one,
 * so comparing against the high-water mark is enough to discard the stale one.
 *
 * Keyed on the TRAMS invoice number rather than `invoices.id` because the gate
 * has to run before any AIVIA row exists (the new_invoice case) and because one
 * TRAMS invoice maps to N AIVIA rows, one per booking.
 */
export const tramsInvoiceEventSeq = pgTable('trams_invoice_event_seq', {
  tramsInvoiceNo: integer('trams_invoice_no').primaryKey(),
  lastEventSeq: bigint('last_event_seq', { mode: 'number' }).notNull(),
  updatedAt: timestamp('updated_at').notNull().defaultNow(),
});

export const insertTramsSupplierSchema = createInsertSchema(tramsSuppliers);
export const selectTramsSupplierSchema = createSelectSchema(tramsSuppliers);
export type TramsSupplier = typeof tramsSuppliers.$inferSelect;
export type InsertTramsSupplier = typeof tramsSuppliers.$inferInsert;

export const insertTramsSupplierTravelTypeSchema = createInsertSchema(
  tramsSupplierTravelTypes,
);
export const selectTramsSupplierTravelTypeSchema = createSelectSchema(
  tramsSupplierTravelTypes,
);
export type TramsSupplierTravelType = typeof tramsSupplierTravelTypes.$inferSelect;
export type InsertTramsSupplierTravelType = typeof tramsSupplierTravelTypes.$inferInsert;

// ============================================================================
// RBAC (Role-Based Access Control) Tables
// See: agent2/docs/specs/admin-role-permissions.md §4
// ============================================================================

// Module access level enum
export const moduleAccessLevelEnum = pgEnum("module_access_level", [
  "none",
  "read",
  "full",
]);

// Override source enum
export const overrideSourceEnum = pgEnum("override_source", [
  "manual",
  "flag",
]);

// Roles table - defines available roles in the system
export const roles = pgTable("roles", {
  id: serial("id").primaryKey(),
  slug: text("slug").notNull().unique(), // e.g., 'SYSTEM_ADMIN', 'AGENCY_OWNER'
  displayName: text("display_name").notNull(), // e.g., 'System Admin'
  description: text("description"),
  scope: text("scope").default("global"), // 'global', 'agency', etc.
  requiresAuth0Sync: boolean("requires_auth0_sync").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Role module permissions - defines default module access per role
export const roleModulePermissions = pgTable(
  "role_module_permissions",
  {
    id: serial("id").primaryKey(),
    roleId: integer("role_id")
      .notNull()
      .references(() => roles.id, { onDelete: "cascade" }),
    moduleKey: text("module_key").notNull(), // e.g., 'admin.user-management', 'crm.pipeline'
    accessLevel: moduleAccessLevelEnum("access_level").notNull().default("none"),
    enforcedByFlag: text("enforced_by_flag"), // optional feature flag key that must be enabled
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    roleModuleUnique: uniqueIndex("role_module_permissions_role_module_idx").on(
      table.roleId,
      table.moduleKey,
    ),
  }),
);

// User role assignments - links users to their primary role
export const userRoleAssignments = pgTable(
  "user_role_assignments",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    roleId: integer("role_id")
      .notNull()
      .references(() => roles.id, { onDelete: "cascade" }),
    isPrimary: boolean("is_primary").notNull().default(true),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userPrimaryUnique: uniqueIndex("user_role_assignments_user_primary_idx").on(
      table.userId,
      table.isPrimary,
    ),
  }),
);

// User module overrides - per-user overrides to default role permissions
export const userModuleOverrides = pgTable(
  "user_module_overrides",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    moduleKey: text("module_key").notNull(),
    accessLevel: moduleAccessLevelEnum("access_level").notNull(),
    source: overrideSourceEnum("source").notNull().default("manual"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userModuleUnique: uniqueIndex("user_module_overrides_user_module_idx").on(
      table.userId,
      table.moduleKey,
    ),
  }),
);

// User features - stores per-user feature flags like threadsAdvancedEnabled
export const userFeatures = pgTable(
  "user_features",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    threadsAdvancedEnabled: boolean("threads_advanced_enabled").notNull().default(false),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userUnique: uniqueIndex("user_features_user_idx").on(table.userId),
  }),
);

// RBAC Relations
export const rolesRelations = relations(roles, ({ many }) => ({
  permissions: many(roleModulePermissions),
  assignments: many(userRoleAssignments),
}));

export const roleModulePermissionsRelations = relations(
  roleModulePermissions,
  ({ one }) => ({
    role: one(roles, {
      fields: [roleModulePermissions.roleId],
      references: [roles.id],
    }),
  }),
);

export const userRoleAssignmentsRelations = relations(
  userRoleAssignments,
  ({ one }) => ({
    user: one(users, {
      fields: [userRoleAssignments.userId],
      references: [users.id],
    }),
    role: one(roles, {
      fields: [userRoleAssignments.roleId],
      references: [roles.id],
    }),
  }),
);

export const userModuleOverridesRelations = relations(
  userModuleOverrides,
  ({ one }) => ({
    user: one(users, {
      fields: [userModuleOverrides.userId],
      references: [users.id],
    }),
  }),
);

export const userFeaturesRelations = relations(userFeatures, ({ one }) => ({
  user: one(users, {
    fields: [userFeatures.userId],
    references: [users.id],
  }),
}));

// RBAC Types
export type Role = typeof roles.$inferSelect;
export type InsertRole = typeof roles.$inferInsert;
export type RoleModulePermission = typeof roleModulePermissions.$inferSelect;
export type InsertRoleModulePermission = typeof roleModulePermissions.$inferInsert;
export type UserRoleAssignment = typeof userRoleAssignments.$inferSelect;
export type InsertUserRoleAssignment = typeof userRoleAssignments.$inferInsert;
export type UserModuleOverride = typeof userModuleOverrides.$inferSelect;
export type InsertUserModuleOverride = typeof userModuleOverrides.$inferInsert;
export type UserFeature = typeof userFeatures.$inferSelect;
export type InsertUserFeature = typeof userFeatures.$inferInsert;

export type ModuleAccessLevel = "none" | "read" | "full";
export type OverrideSource = "manual" | "flag";

// RBAC Schemas
export const insertRoleSchema = createInsertSchema(roles);
export const selectRoleSchema = createSelectSchema(roles);
export const insertRoleModulePermissionSchema = createInsertSchema(roleModulePermissions);
export const insertUserRoleAssignmentSchema = createInsertSchema(userRoleAssignments);
export const insertUserModuleOverrideSchema = createInsertSchema(userModuleOverrides);
export const insertUserFeatureSchema = createInsertSchema(userFeatures);

// ============================================================================
// Admin Audit Events Table
// Centralized audit log for admin actions (RBAC, MCP tools, orchestration, etc.)
// See: agent2/tasks/T-369-admin-audit-log-surfacing-for-rbac-and-admin-events.md
// ============================================================================

// Audit event category enum
export const auditEventCategoryEnum = pgEnum("audit_event_category", [
  "rbac",
  "mcp-tools",
  "orchestration",
  "skills",
  "admin-settings",
  "impersonation",
]);

export const adminAuditEvents = pgTable(
  "admin_audit_events",
  {
    id: serial("id").primaryKey(),
    timestamp: timestamp("timestamp").defaultNow().notNull(),
    actorUserId: integer("actor_user_id")
      .references(() => users.id, { onDelete: "set null" }),
    actorRole: text("actor_role").notNull(),
    targetUserId: integer("target_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    category: text("category").notNull(), // 'rbac', 'mcp-tools', 'orchestration', 'skills', 'admin-settings'
    action: text("action").notNull(), // e.g., 'role.changed', 'threadsAdvanced.toggled', 'mcpTool.disabled'
    details: jsonb("details").$type<{
      before?: Record<string, unknown>;
      after?: Record<string, unknown>;
      reason?: string;
      summary?: string;
      [key: string]: unknown;
    }>(),
  },
  (table) => ({
    timestampIdx: index("admin_audit_events_timestamp_idx").on(table.timestamp),
    actorIdx: index("admin_audit_events_actor_idx").on(table.actorUserId),
    targetIdx: index("admin_audit_events_target_idx").on(table.targetUserId),
    categoryActionIdx: index("admin_audit_events_category_action_idx").on(
      table.category,
      table.action,
    ),
  }),
);

// Admin audit event relations
export const adminAuditEventsRelations = relations(adminAuditEvents, ({ one }) => ({
  actor: one(users, {
    fields: [adminAuditEvents.actorUserId],
    references: [users.id],
    relationName: "auditActor",
  }),
  target: one(users, {
    fields: [adminAuditEvents.targetUserId],
    references: [users.id],
    relationName: "auditTarget",
  }),
}));

// Admin audit event types
export type AdminAuditEvent = typeof adminAuditEvents.$inferSelect;
export type InsertAdminAuditEvent = typeof adminAuditEvents.$inferInsert;

// Admin audit event schemas
export const insertAdminAuditEventSchema = createInsertSchema(adminAuditEvents);
export const selectAdminAuditEventSchema = createSelectSchema(adminAuditEvents);

// Audit event category type
export type AuditEventCategory = "rbac" | "mcp-tools" | "orchestration" | "skills" | "admin-settings" | "impersonation";

// ============================================================================
// RBAC Default Seed Data (for reference - apply via migration)
// Roles: SYSTEM_ADMIN, AGENCY_OWNER, AGENCY_MANAGER, ADVISOR, READ_ONLY
// Module keys: admin.user-management, admin.agency-settings, crm.pipeline,
//              crm.clients, trips.builder, threads.workspace, proposals.workspace,
//              finance.commissions, analytics.dashboards
// See admin-role-permissions.md §2-3 for the full matrix
// ============================================================================

// ============================================================================
// Prompt Library Tables
// See: agent2/tasks/T-372-threads-prompt-library-backend-and-admin-ui.md
// ============================================================================

// Prompt categories table - organizes prompts into groups
export const promptCategories = pgTable(
  "prompt_categories",
  {
    id: serial("id").primaryKey(),
    slug: text("slug").notNull().unique(), // e.g., 'planning-strategy-finance'
    label: text("label").notNull(), // Display name
    description: text("description"),
    sortOrder: integer("sort_order").notNull().default(0),
    isSystemCategory: boolean("is_system_category").notNull().default(true),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    slugIdx: uniqueIndex("prompt_categories_slug_idx").on(table.slug),
    sortOrderIdx: index("prompt_categories_sort_order_idx").on(table.sortOrder),
  }),
);

// Prompt templates table - stores reusable prompts (system defaults + user-owned)
export const promptTemplates = pgTable(
  "prompt_templates",
  {
    id: serial("id").primaryKey(),
    categoryId: integer("category_id").references(() => promptCategories.id, {
      onDelete: "set null",
    }),
    ownerUserId: integer("owner_user_id").references(() => users.id, {
      onDelete: "cascade",
    }), // NULL = global default, non-null = user-owned
    title: text("title").notNull(),
    description: text("description"),
    bodyTemplate: text("body_template").notNull(), // Contains {{variableKey}} placeholders
    isActive: boolean("is_active").notNull().default(true), // Soft-delete/archival flag
    isSystemDefault: boolean("is_system_default").notNull().default(false), // True only for global defaults
    createdByUserId: integer("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    metadata: jsonb("metadata").$type<{
      tags?: string[];
      usageCount?: number;
      lastUsedAt?: string;
      // T-379: Suggested action configuration
      enableSuggestedActions?: boolean;
      suggestedSurfaces?: ('threads' | 'client' | 'trip')[];
      [key: string]: unknown;
    }>(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    categoryIdx: index("prompt_templates_category_idx").on(table.categoryId),
    ownerIdx: index("prompt_templates_owner_idx").on(table.ownerUserId),
    activeIdx: index("prompt_templates_active_idx").on(table.isActive),
    systemDefaultIdx: index("prompt_templates_system_default_idx").on(
      table.isSystemDefault,
    ),
  }),
);

// Prompt Library Relations
export const promptCategoriesRelations = relations(
  promptCategories,
  ({ many }) => ({
    templates: many(promptTemplates),
  }),
);

export const promptTemplatesRelations = relations(
  promptTemplates,
  ({ one }) => ({
    category: one(promptCategories, {
      fields: [promptTemplates.categoryId],
      references: [promptCategories.id],
    }),
    owner: one(users, {
      fields: [promptTemplates.ownerUserId],
      references: [users.id],
      relationName: "promptOwner",
    }),
    createdBy: one(users, {
      fields: [promptTemplates.createdByUserId],
      references: [users.id],
      relationName: "promptCreator",
    }),
  }),
);

// Prompt Library Types
export type PromptCategory = typeof promptCategories.$inferSelect;
export type InsertPromptCategory = typeof promptCategories.$inferInsert;
export type PromptTemplate = typeof promptTemplates.$inferSelect;
export type InsertPromptTemplate = typeof promptTemplates.$inferInsert;

// Prompt Library Schemas
export const insertPromptCategorySchema = createInsertSchema(promptCategories);
export const selectPromptCategorySchema = createSelectSchema(promptCategories);
export const insertPromptTemplateSchema = createInsertSchema(promptTemplates);
export const selectPromptTemplateSchema = createSelectSchema(promptTemplates);

// Monthly Usage Tracking - tracks user message interactions per calendar month
export const userMonthlyUsage = pgTable(
  "user_monthly_usage",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    yearMonth: text("year_month").notNull(), // Format: "YYYY-MM" (e.g., "2025-12")
    messageCount: integer("message_count").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userYearMonthIdx: uniqueIndex("user_monthly_usage_user_year_month_idx").on(
      table.userId,
      table.yearMonth,
    ),
    userIdx: index("user_monthly_usage_user_idx").on(table.userId),
  }),
);

export const userMonthlyUsageRelations = relations(
  userMonthlyUsage,
  ({ one }) => ({
    user: one(users, {
      fields: [userMonthlyUsage.userId],
      references: [users.id],
    }),
  }),
);

// Monthly Usage Types
export type UserMonthlyUsage = typeof userMonthlyUsage.$inferSelect;
export type InsertUserMonthlyUsage = typeof userMonthlyUsage.$inferInsert;

// Monthly Usage Schemas
export const insertUserMonthlyUsageSchema = createInsertSchema(userMonthlyUsage);
export const selectUserMonthlyUsageSchema = createSelectSchema(userMonthlyUsage);

// ──────────────────────────────────────────────────────────────────────────────
// Product Feature Updates
// ──────────────────────────────────────────────────────────────────────────────

export const productUpdateStatusEnum = pgEnum("product_update_status", [
  "draft",
  "published",
  "expired",
]);

export const productUpdates = pgTable(
  "product_updates",
  {
    id: serial("id").primaryKey(),
    version: text("version").notNull(),
    releaseDate: timestamp("release_date").notNull(),
    headline: text("headline").notNull(),
    heroImageUrl: text("hero_image_url"),
    heroDescription: text("hero_description").notNull(),
    status: productUpdateStatusEnum("status").default("draft").notNull(),
    publishDate: timestamp("publish_date"),
    createdById: integer("created_by_id").references(() => users.id),
    lastModifiedById: integer("last_modified_by_id").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    statusIdx: index("product_updates_status_idx").on(table.status),
    releaseDateIdx: index("product_updates_release_date_idx").on(table.releaseDate),
  }),
);

export const productUpdateFeatures = pgTable(
  "product_update_features",
  {
    id: serial("id").primaryKey(),
    productUpdateId: integer("product_update_id")
      .notNull()
      .references(() => productUpdates.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    description: text("description").notNull(),
    learnMoreUrl: text("learn_more_url"),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
);

export const productUpdateViews = pgTable(
  "product_update_views",
  {
    id: serial("id").primaryKey(),
    productUpdateId: integer("product_update_id")
      .notNull()
      .references(() => productUpdates.id, { onDelete: "cascade" }),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    viewedAt: timestamp("viewed_at").defaultNow().notNull(),
  },
  (table) => ({
    uniqueView: uniqueIndex("product_update_views_unique_idx").on(
      table.productUpdateId,
      table.userId,
    ),
    userIdx: index("product_update_views_user_idx").on(table.userId),
  }),
);

// Product Updates Relations
export const productUpdatesRelations = relations(productUpdates, ({ many, one }) => ({
  features: many(productUpdateFeatures),
  views: many(productUpdateViews),
  createdBy: one(users, {
    fields: [productUpdates.createdById],
    references: [users.id],
  }),
  lastModifiedBy: one(users, {
    fields: [productUpdates.lastModifiedById],
    references: [users.id],
  }),
}));

export const productUpdateFeaturesRelations = relations(productUpdateFeatures, ({ one }) => ({
  productUpdate: one(productUpdates, {
    fields: [productUpdateFeatures.productUpdateId],
    references: [productUpdates.id],
  }),
}));

export const productUpdateViewsRelations = relations(productUpdateViews, ({ one }) => ({
  productUpdate: one(productUpdates, {
    fields: [productUpdateViews.productUpdateId],
    references: [productUpdates.id],
  }),
  user: one(users, {
    fields: [productUpdateViews.userId],
    references: [users.id],
  }),
}));

// Product Updates Types
export type ProductUpdate = typeof productUpdates.$inferSelect;
export type InsertProductUpdate = typeof productUpdates.$inferInsert;
export type ProductUpdateFeature = typeof productUpdateFeatures.$inferSelect;
export type InsertProductUpdateFeature = typeof productUpdateFeatures.$inferInsert;
export type ProductUpdateView = typeof productUpdateViews.$inferSelect;
export type InsertProductUpdateView = typeof productUpdateViews.$inferInsert;

// Product Updates Schemas
export const insertProductUpdateSchema = createInsertSchema(productUpdates);
export const selectProductUpdateSchema = createSelectSchema(productUpdates);
export const insertProductUpdateFeatureSchema = createInsertSchema(productUpdateFeatures);
export const selectProductUpdateFeatureSchema = createSelectSchema(productUpdateFeatures);

// ============================================================================
// Teams Table
// Groups agency owners with their associate advisors using a shared Team ID
// derived from the TRAMS Agent Name field in WorldVia PRO.
//
// Team ID format (text, normalized from TRAMS Agent Name):
//   Agency Owner:      "FirstName LastName TRAMSProfileNo"
//   Associate Advisor: extracted from parentheses in their agent name
//     e.g. "Stephanie Chewning (Brittni Strickland 290062) 294723"
//          → Team ID = "Brittni Strickland 290062"
// ============================================================================

export const teams = pgTable("teams", {
  id: serial("id").primaryKey(),
  uuid: pgUuid("uuid").defaultRandom().notNull().unique(),
  teamIdentifier: text("team_identifier").notNull().unique(),
  name: text("name").notNull(), // agency_name
  agencyId: text("agency_id"),
  agencyOwnerUserId: integer("agency_owner_user_id").references(() => users.id, { onDelete: "set null" }),
  // AR-13: the onboarding "agencies" record maps onto teams (see data model —
  // teams replaces the proposed agencies table). memberCount = associate_count.
  brandName: text("brand_name"),
  agencyLegalEntityName: text("agency_legal_entity_name"),
  memberCount: integer("member_count").notNull().default(1),
  isActive: boolean("is_active").notNull().default(true),
  // ── AR-330 · User-domain additive batch ────────────────────────────────────
  // Team lifecycle, written by the owner-suspension cascade (AR-340). Nullable
  // with no default; `is_active` stays the read path until AR-340 ships.
  status: teamStatusEnum("status"),
  statusChangedAt: timestamp("status_changed_at"),
  statusChangedBy: integer("status_changed_by")
    .references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  suspendedReasonId: integer("suspended_reason_id")
    .references((): AnyPgColumn => statusReasonCodes.reasonId, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const teamsRelations = relations(teams, ({ one, many }) => ({
  agencyOwner: one(users, {
    fields: [teams.agencyOwnerUserId],
    references: [users.id],
  }),
  invoices: many(invoices),
  goals: many(goals),
}));

export type Team = typeof teams.$inferSelect;
export type InsertTeam = typeof teams.$inferInsert;

export const insertTeamSchema = createInsertSchema(teams);
export const selectTeamSchema = createSelectSchema(teams);

/**
 * Extract Team ID from a TRAMS Agent Name string.
 *
 * Agency Owner format:  "FirstName LastName TRAMSProfileNo"
 *   → returns the full string unchanged
 *
 * Associate Advisor format: "FirstName LastName (OwnerFirst OwnerLast OwnerProfileNo) ProfileNo"
 *   → returns the content inside parentheses: "OwnerFirst OwnerLast OwnerProfileNo"
 */
export function extractTeamIdentifier(tramsAgentName: string): string {
  const input = typeof tramsAgentName === 'string' ? tramsAgentName : '';
  const raw = input.match(/\(([^)]+)\)/);
  const extracted = raw ? raw[1] : input;
  return extracted.trim().replace(/\s+/g, ' ').toLowerCase();
}

// Atomic counter for TRAMS INVOICE.INVOICENUMBER — AIVIA's own series.
// Grasp uses the 100M range; AIVIA starts at 200M to avoid collisions.
// Application code increments via SELECT ... FOR UPDATE in InvoiceRepository.
export const invoiceNumberAllocator = pgTable(
  "invoice_number_allocator",
  {
    id: integer("id").primaryKey().notNull().default(1),
    nextInvoiceNumber: integer("next_invoice_number")
      .notNull()
      .default(200000000),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    singletonCheck: check(
      "invoice_number_allocator_singleton_chk",
      sql`${table.id} = 1`,
    ),
  }),
);

// ============================================================================
// Invoices Table
// Trip-scoped invoice with TRAMS field mapping
// See: Grasp Replacement Project_Connected TRAMS Fields Table
// ============================================================================

export const invoices = pgTable(
  "invoices",
  {
    id: serial("id").primaryKey(),
    // Ownership / scoping
    teamId: integer("team_id").references(() => teams.id),
    companyUniqueIdentifier: text("company_unique_identifier"),
    userId: integer("user_id").references(() => users.id),
    createdBy: integer("created_by").references(() => users.id),
    tramsAccountId: integer("trams_account_id").references(() => userTramsAccounts.id),
    // Owner account the booking was sold under (the lead AGENTBKG's TRAMS
    // account). Written only when that owner has 2+ TRAMS accounts (e.g. an
    // EIN change), so Team view can keep the old and new entity apart. NULL for
    // every single-account owner; see migrations/2026-09-24-invoices-lead-trams-account.sql.
    leadTramsAccountId: integer("lead_trams_account_id").references(() => userTramsAccounts.id),
    // Trip linkage: every invoice belongs to exactly 1 trip
    tripId: integer("trip_id")
      .notNull()
      .references(() => trips.id),

    // --- TRAMS INVOICE table fields ---
    invoiceNumber: text("invoice_number"), // AIVIA Invoice Number (AIN + 10 random digits, e.g. AIN4829173650)
    recordLocator: varchar("record_locator", { length: 6 }), // INVOICE.RECORDLOCATOR (varchar 6, same as trip)
    agencyLinkNo: integer("agency_link_no"), // INVOICE.AGENCY_LINKNO
    clientLinkNo: integer("client_link_no"), // INVOICE.CLIENT_LINKNO (TRAMS Profile ID)
    branchLinkNo: integer("branch_link_no"), // INVOICE.BRANCH_LINKNO (from users.branch_number)
    tramsInvoiceNo: integer("trams_invoice_no"), // TRAMS-generated INVOICENO (PK in InterBase)
    tramsBookingNo: integer("trams_booking_no"), // TRAMS-generated BOOKINGNO (PK in InterBase)
    tramsAgentBkgNo: integer("trams_agent_bkg_no"), // TRAMS-generated AGENTBKGNO (PK in InterBase)
    issueDate: timestamp("issue_date"), // INVOICE.ISSUEDATE
    invoiceStatusCode: text("invoice_status_code").default("O"), // INVOICETYPE.INVOICETYPECODE: O (Open) / C (Closed) / V (Voided)

    // --- TRAMS BOOKING table fields ---
    travelerFirstName: text("traveler_first_name"), // User-entered first name
    travelerLastName: text("traveler_last_name"), // User-entered last name
    primaryTraveler: text("primary_traveler").notNull(), // Computed: CamelCase(Last)/CamelCase(First) — maps to BOOKING.PASSENGERNAME
    supplierLinkNo: integer("supplier_link_no"), // BOOKING.VENDOR_LINKNO (joins PROFILE.PROFILENO)
    supplierName: text("supplier_name"), // Denormalized for display
    travelType: text("travel_type"), // TRAVELTYPE.TRAVELTYPE
    confirmationNumber: text("confirmation_number"), // BOOKING.CONFIRMNO (varchar 40)
    departureDate: timestamp("departure_date"), // BOOKING.DEPARTDATE
    returnDate: timestamp("return_date"), // BOOKING.RETURNDATE
    totalFare: text("total_fare"), // BOOKING.TOTALFARE (double precision)
    commissionAmount: text("commission_amount"), // BOOKING.COMMAMT (double precision)
    // This row's AGENTBKG.RATE / AMOUNT (already descaled). Not the lead
    // agent's money and not a rate-segment recompute. Nullable until repaired.
    agentRatePercent: decimal("agent_rate_percent", { precision: 5, scale: 2 }),
    agentCommissionAmount: decimal("agent_commission_amount", { precision: 12, scale: 2 }),
    // Payable AGENTBKG (agency owner / lead). What Worldvia pays. Same as
    // this-row agent_* when the creator is the owner; owner split when the
    // row is an associate/manager at 0. Invoice Log Net Comm for Agency
    // Owners reads these. Do not copy this onto agent_*.
    ownerRatePercent: decimal("owner_rate_percent", { precision: 5, scale: 2 }),
    ownerCommissionAmount: decimal("owner_commission_amount", { precision: 12, scale: 2 }),

    // Optional detail fields per travel type
    propertyName: text("property_name"), // Hotel travel type
    shipName: text("ship_name"), // Ship-Dpst / Ship-Finl travel types
    cabinCount: integer("cabin_count"), // Ship-Dpst / Ship-Finl (required for cruise types)
    tourName: text("tour_name"), // Tour-Dpst / Tour-Finl travel types
    description: text("description"), // All other travel types

    // Origin tracking (Gap 10: TRAMS-originated invoices)
    source: text("source").default("cm"), // 'cm' = created in Commission Manager, 'trams' = originated in TRAMS
    // Phase 3a nightly reconcile marker. True only for rows the statement
    // reconcile created from a TRAMS statement line AIVIA had no invoice for.
    // First-class column (rather than a JSONB probe) so the rollback query for
    // a bad reconcile run stays fast and unambiguous — see the DELETE recipe in
    // agent2/PM/DEV-2026-08-25T044500Z-baseline-sizing-ceiling.md.
    createdByReconcile: boolean("created_by_reconcile").notNull().default(false),

    // General fields
    status: text("status").notNull().default("draft"),
    syncStatus: text("sync_status").notNull().default("pending"), // TRAMS sync only: pending | connected | trams_validation_failed
    amount: text("amount"),
    currency: text("currency").default("USD"),
    dueAt: timestamp("due_at"),
    paidAt: timestamp("paid_at"),
    statementDate: date("statement_date", { mode: "date" }),
    metadata: jsonb("metadata"),
    revision: integer("revision").notNull().default(1),
    lastEditOperationId: text("last_edit_operation_id"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    tripIdx: index("invoices_trip_idx").on(table.tripId),
    recordLocatorIdx: index("invoices_record_locator_idx").on(table.recordLocator),
    invoiceNumberUniqueIdx: uniqueIndex("invoices_invoice_number_unique_idx")
      .on(table.invoiceNumber)
      .where(sql`${table.invoiceNumber} IS NOT NULL`),
    userIdx: index("invoices_user_idx").on(table.userId),
    teamIdx: index("invoices_team_idx").on(table.teamId),
    tramsAccountIdx: index("invoices_trams_account_idx").on(table.tramsAccountId),
    syncStatusIdx: index("invoices_sync_status_idx").on(table.syncStatus),
    statusCodeIdx: index("invoices_status_code_idx").on(table.invoiceStatusCode),
    departureDateIdx: index("invoices_departure_date_idx").on(table.departureDate),
    returnDateIdx: index("invoices_return_date_idx").on(table.returnDate),
    createdAtIdx: index("invoices_created_at_idx").on(table.createdAt),
    statementDateIdx: index("invoices_statement_date_idx").on(table.statementDate),
    supplierNameIdx: index("invoices_supplier_name_idx").on(table.supplierName),
    primaryTravelerIdx: index("invoices_primary_traveler_idx").on(table.primaryTraveler),
    paidAtIdx: index("invoices_paid_at_idx").on(table.paidAt),
    // One AIVIA row per TRAMS AGENTBKGNO. Relations: invoice → booking (1:many),
    // booking → agentbkg (1:many). Uniqueness is keyed on the full
    // (INVOICENO, BOOKINGNO, AGENTBKGNO) tuple so every agentbkg becomes its
    // own row. Two narrower partial indexes cover the legacy shapes where
    // agentbkg and/or booking are absent.
    tramsAgentBkgCompositeIdx: uniqueIndex("invoices_trams_agent_bkg_composite_idx")
      .on(table.tramsInvoiceNo, table.tramsBookingNo, table.tramsAgentBkgNo)
      .where(sql`${table.tramsInvoiceNo} IS NOT NULL AND ${table.tramsBookingNo} IS NOT NULL AND ${table.tramsAgentBkgNo} IS NOT NULL`),
    tramsBookingCompositeIdx: uniqueIndex("invoices_trams_booking_composite_idx")
      .on(table.tramsInvoiceNo, table.tramsBookingNo)
      .where(sql`${table.tramsInvoiceNo} IS NOT NULL AND ${table.tramsBookingNo} IS NOT NULL AND ${table.tramsAgentBkgNo} IS NULL`),
    tramsNullBookingIdx: uniqueIndex("invoices_trams_null_booking_idx")
      .on(table.tramsInvoiceNo)
      .where(sql`${table.tramsInvoiceNo} IS NOT NULL AND ${table.tramsBookingNo} IS NULL`),
  }),
);

export const invoicesRelations = relations(invoices, ({ one }) => ({
  team: one(teams, {
    fields: [invoices.teamId],
    references: [teams.id],
  }),
  user: one(users, {
    fields: [invoices.userId],
    references: [users.id],
  }),
  trip: one(trips, {
    fields: [invoices.tripId],
    references: [trips.id],
  }),
}));

export type Invoice = typeof invoices.$inferSelect;
export type InsertInvoice = typeof invoices.$inferInsert;

export const insertInvoiceSchema = createInsertSchema(invoices).extend({
  syncStatus: InvoiceSyncStatusEnum.optional(),
});
export const selectInvoiceSchema = createSelectSchema(invoices).extend({
  syncStatus: InvoiceSyncStatusEnum,
});

// ============================================================================
// Invoice Audit Events
// Captures invoice-level history (move-between-trips, future overrides, etc.).
// Distinct from admin_audit_events which scopes to RBAC / admin actions.
// See: server-nest/src/modules/invoices/MOVE_INVOICE.md
// ============================================================================

export const invoiceAuditEvents = pgTable(
  "invoice_audit_events",
  {
    id: serial("id").primaryKey(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    invoiceId: integer("invoice_id")
      .notNull()
      .references(() => invoices.id, { onDelete: "cascade" }),
    actorUserId: integer("actor_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    actorRole: text("actor_role"),
    action: text("action").notNull(),
    fromTripId: integer("from_trip_id").references(() => trips.id, {
      onDelete: "set null",
    }),
    toTripId: integer("to_trip_id").references(() => trips.id, {
      onDelete: "set null",
    }),
    fromRecordLocator: varchar("from_record_locator", { length: 16 }),
    toRecordLocator: varchar("to_record_locator", { length: 16 }),
    reason: text("reason"),
    operationId: text("operation_id"),
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  },
  (table) => ({
    invoiceCreatedIdx: index("invoice_audit_events_invoice_created_idx").on(
      table.invoiceId,
      table.createdAt,
    ),
    actionIdx: index("invoice_audit_events_action_idx").on(table.action),
    actorIdx: index("invoice_audit_events_actor_idx").on(table.actorUserId),
    operationIdUniqueIdx: uniqueIndex("invoice_audit_events_operation_id_unique")
      .on(table.operationId)
      .where(sql`${table.operationId} IS NOT NULL`),
  }),
);

export const invoiceAuditEventsRelations = relations(
  invoiceAuditEvents,
  ({ one }) => ({
    invoice: one(invoices, {
      fields: [invoiceAuditEvents.invoiceId],
      references: [invoices.id],
    }),
    actor: one(users, {
      fields: [invoiceAuditEvents.actorUserId],
      references: [users.id],
    }),
    fromTrip: one(trips, {
      fields: [invoiceAuditEvents.fromTripId],
      references: [trips.id],
      relationName: "invoiceAuditFromTrip",
    }),
    toTrip: one(trips, {
      fields: [invoiceAuditEvents.toTripId],
      references: [trips.id],
      relationName: "invoiceAuditToTrip",
    }),
  }),
);

export type InvoiceAuditEvent = typeof invoiceAuditEvents.$inferSelect;
export type InsertInvoiceAuditEvent = typeof invoiceAuditEvents.$inferInsert;

export const insertInvoiceAuditEventSchema = createInsertSchema(invoiceAuditEvents);
export const selectInvoiceAuditEventSchema = createSelectSchema(invoiceAuditEvents);

export const INVOICE_AUDIT_ACTIONS = [
  "move",
] as const;
export type InvoiceAuditAction = (typeof INVOICE_AUDIT_ACTIONS)[number];

// ============================================================================
// User TRAMS Accounts
// Links users to one or more TRAMS profiles for invoice/report/statement scoping.
// At most one account per user may be marked isDefault (enforced by partial
// unique index). Zero defaults are allowed; the app-level fallback in
// TramsAccountsService.getCurrentContext() picks the highest ProfileID.
// ============================================================================

export const userTramsAccounts = pgTable(
  "user_trams_accounts",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    profileId: integer("profile_id").notNull(),
    corporateId: integer("corporate_id"), // TODO: make notNull after backfill is complete
    agentName: text("agent_name").notNull(),
    interfaceId: text("interface_id").notNull(),
    isDefault: boolean("is_default").notNull().default(false),
    // Agent commission rates synced from TRAMS (stored as percent, e.g. 70.00 = 70%)
    baseOutsideRate: decimal("base_outside_rate", { precision: 7, scale: 4 }),
    airRate: decimal("air_rate", { precision: 7, scale: 4 }),
    serviceFeeRate: decimal("service_fee_rate", { precision: 7, scale: 4 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdx: index("user_trams_accounts_user_idx").on(table.userId),
    // Supports profileId-only lookups (e.g. rate-change notifications keyed on
    // PROFILE.PROFILENO) that the composite (userId, profileId) index can't serve.
    profileIdIdx: index("user_trams_accounts_profile_id_idx").on(table.profileId),
    userProfileUnique: uniqueIndex("user_trams_accounts_user_profile_uniq").on(
      table.userId,
      table.profileId,
    ),
    idUserUnique: unique("user_trams_accounts_id_user_uniq").on(
      table.id,
      table.userId,
    ),
    oneDefaultPerUser: uniqueIndex("user_trams_accounts_one_default_per_user")
      .on(table.userId)
      .where(sql`${table.isDefault} = true`),
    baseOutsideRateRange: check(
      "user_trams_accounts_base_outside_rate_range_chk",
      sql`${table.baseOutsideRate} IS NULL OR (${table.baseOutsideRate} >= 0 AND ${table.baseOutsideRate} <= 100)`,
    ),
    airRateRange: check(
      "user_trams_accounts_air_rate_range_chk",
      sql`${table.airRate} IS NULL OR (${table.airRate} >= 0 AND ${table.airRate} <= 100)`,
    ),
    serviceFeeRateRange: check(
      "user_trams_accounts_service_fee_rate_range_chk",
      sql`${table.serviceFeeRate} IS NULL OR (${table.serviceFeeRate} >= 0 AND ${table.serviceFeeRate} <= 100)`,
    ),
  }),
);

export const userTramsAccountsRelations = relations(
  userTramsAccounts,
  ({ one }) => ({
    user: one(users, {
      fields: [userTramsAccounts.userId],
      references: [users.id],
    }),
  }),
);

export type UserTramsAccount = typeof userTramsAccounts.$inferSelect;
export type InsertUserTramsAccount = typeof userTramsAccounts.$inferInsert;

export const insertUserTramsAccountSchema = createInsertSchema(userTramsAccounts);
export const selectUserTramsAccountSchema = createSelectSchema(userTramsAccounts);

// ============================================================================
// USER TRAMS ACCOUNT RATES — Effective-dated commission rate history
// ----------------------------------------------------------------------------
// TRAMS has no effective-dating; the only time signal is the OXSYNC change time
// (delivered as `updatedAt` on the rate-sync notification). We segment the rate
// timeline on that boundary. Each row is a half-open interval
// [effective_from, effective_to); the single open row (effective_to IS NULL)
// mirrors the denormalized "current" rate columns on user_trams_accounts.
//
// An invoice's commission rate is resolved by its ISSUE DATE against these
// segments, so invoices issued before a plan change keep the old rate and those
// issued after get the new rate — without re-pricing history.
// ============================================================================

export const userTramsAccountRates = pgTable(
  "user_trams_account_rates",
  {
    id: serial("id").primaryKey(),
    tramsAccountId: integer("trams_account_id")
      .notNull()
      .references(() => userTramsAccounts.id, { onDelete: "cascade" }),
    // Same shape/scale as user_trams_accounts (percent, e.g. 70.00 = 70%)
    baseOutsideRate: decimal("base_outside_rate", { precision: 7, scale: 4 }),
    airRate: decimal("air_rate", { precision: 7, scale: 4 }),
    serviceFeeRate: decimal("service_fee_rate", { precision: 7, scale: 4 }),
    // Half-open interval. effective_to NULL = current/open segment.
    effectiveFrom: timestamp("effective_from").notNull(),
    effectiveTo: timestamp("effective_to"),
    // Calendar date (America/New_York) on which this segment takes effect.
    // Rate resolution compares an invoice's Eastern issue date against this
    // date-only value, so an intraday rate change applies to invoices issued
    // that same Eastern day (avoids a UTC-vs-local off-by-one). Populated at
    // write time from effective_from; nullable for legacy rows (resolver falls
    // back to deriving it from effective_from).
    effectiveFromDate: date("effective_from_date"),
    // 'trams_sync' (push notification) | 'pull_sync' (Lambda refresh) | 'backfill'
    source: text("source").notNull().default("trams_sync"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    // Leading column also serves tramsAccountId-only lookups, so no separate
    // single-column index is needed.
    accountEffectiveIdx: index("user_trams_account_rates_account_effective_idx").on(
      table.tramsAccountId,
      table.effectiveFrom,
    ),
    accountEffectiveDateIdx: index(
      "user_trams_account_rates_account_effective_date_idx",
    ).on(table.tramsAccountId, table.effectiveFromDate),
    // At most one open (current) segment per account.
    oneOpenPerAccount: uniqueIndex("user_trams_account_rates_one_open_per_account")
      .on(table.tramsAccountId)
      .where(sql`${table.effectiveTo} IS NULL`),
    effectiveRangeChk: check(
      "user_trams_account_rates_effective_range_chk",
      sql`${table.effectiveTo} IS NULL OR ${table.effectiveTo} >= ${table.effectiveFrom}`,
    ),
    // Mirror the 0..100 bounds enforced on the parent user_trams_accounts.
    baseOutsideRateRange: check(
      "user_trams_account_rates_base_outside_rate_range_chk",
      sql`${table.baseOutsideRate} IS NULL OR (${table.baseOutsideRate} >= 0 AND ${table.baseOutsideRate} <= 100)`,
    ),
    airRateRange: check(
      "user_trams_account_rates_air_rate_range_chk",
      sql`${table.airRate} IS NULL OR (${table.airRate} >= 0 AND ${table.airRate} <= 100)`,
    ),
    serviceFeeRateRange: check(
      "user_trams_account_rates_service_fee_rate_range_chk",
      sql`${table.serviceFeeRate} IS NULL OR (${table.serviceFeeRate} >= 0 AND ${table.serviceFeeRate} <= 100)`,
    ),
  }),
);

export const userTramsAccountRatesRelations = relations(
  userTramsAccountRates,
  ({ one }) => ({
    account: one(userTramsAccounts, {
      fields: [userTramsAccountRates.tramsAccountId],
      references: [userTramsAccounts.id],
    }),
  }),
);

export type UserTramsAccountRate = typeof userTramsAccountRates.$inferSelect;
export type InsertUserTramsAccountRate = typeof userTramsAccountRates.$inferInsert;

// Tracks which TRAMS account the user currently has selected (persists across sessions)
export const userTramsAccountSelections = pgTable(
  "user_trams_account_selections",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    selectedTramsAccountId: integer("selected_trams_account_id").notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userUnique: uniqueIndex("user_trams_selection_user_uniq").on(table.userId),
    accountOwnerFk: foreignKey({
      columns: [table.selectedTramsAccountId, table.userId],
      foreignColumns: [userTramsAccounts.id, userTramsAccounts.userId],
    }).onDelete("cascade"),
  }),
);

export const userTramsAccountSelectionsRelations = relations(
  userTramsAccountSelections,
  ({ one }) => ({
    user: one(users, {
      fields: [userTramsAccountSelections.userId],
      references: [users.id],
    }),
    tramsAccount: one(userTramsAccounts, {
      fields: [userTramsAccountSelections.selectedTramsAccountId],
      references: [userTramsAccounts.id],
    }),
  }),
);

export type UserTramsAccountSelection = typeof userTramsAccountSelections.$inferSelect;

// ============================================================================
// Agent Adjustments
// Manual charges or credits added by accounting in TRAMS (e.g. IATAN renewal
// fees). Synced from InterBase via the Lambda/PostgreSQL middle layer.
// Statement Date follows the same Wed–Tue ET window → Friday payout logic.
// ============================================================================

export const agentAdjustments = pgTable(
  "agent_adjustments",
  {
    id: serial("id").primaryKey(),
    adjustmentNo: integer("adjustment_no").notNull().unique(),
    adjustmentDate: timestamp("adjustment_date").notNull(),
    statementDate: date("statement_date", { mode: "date" }),
    tramsAccountId: integer("trams_account_id")
      .notNull()
      .references(() => userTramsAccounts.id),
    profileLinkNo: integer("profile_link_no").notNull(),
    amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
    agencyLinkNo: integer("agency_link_no").notNull().default(0),
    remarks: text("remarks"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    tramsAccountIdx: index("agent_adjustments_trams_account_idx").on(table.tramsAccountId),
    statementDateIdx: index("agent_adjustments_statement_date_idx").on(table.statementDate),
  }),
);

export const agentAdjustmentsRelations = relations(
  agentAdjustments,
  ({ one }) => ({
    tramsAccount: one(userTramsAccounts, {
      fields: [agentAdjustments.tramsAccountId],
      references: [userTramsAccounts.id],
    }),
  }),
);

export type AgentAdjustment = typeof agentAdjustments.$inferSelect;
export type InsertAgentAdjustment = typeof agentAdjustments.$inferInsert;

// ============================================================================
// Goals
// Yearly sales/commission/trips targets at two levels:
//   - Team goals (teamId set, userId null) — editable by Owner only.
//   - Personal goals (userId set) — editable by the owning user.
// Unique constraint: one goal row per (year, teamId, userId) combination.
// commissionGoal is the gross goal; netCommissionGoal (AR-314) is set by
// Agency Owners only and feeds the Snapshot net commission projection.
// ============================================================================

export const goalScopeEnum = z.enum(["team", "personal"]);
export type GoalScope = z.infer<typeof goalScopeEnum>;

export const goals = pgTable(
  "goals",
  {
    id: serial("id").primaryKey(),
    year: integer("year").notNull(),
    scope: text("scope").notNull().default("personal"), // "team" | "personal"
    teamId: integer("team_id").references(() => teams.id, { onDelete: "cascade" }),
    userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }),
    salesGoal: decimal("sales_goal", { precision: 14, scale: 2 }).notNull().default("0"),
    commissionGoal: decimal("commission_goal", { precision: 14, scale: 2 }).notNull().default("0"),
    netCommissionGoal: decimal("net_commission_goal", { precision: 14, scale: 2 }).notNull().default("0"),
    tripsTraveledGoal: integer("trips_traveled_goal").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    yearTeamUserUniq: uniqueIndex("goals_year_team_user_uniq").on(
      table.year,
      table.teamId,
      table.userId,
    ),
    personalYearUserUniq: uniqueIndex("goals_personal_year_user_uniq")
      .on(table.year, table.userId)
      .where(sql`${table.scope} = 'personal' AND ${table.teamId} IS NULL`),
    teamYearTeamUniq: uniqueIndex("goals_team_year_team_uniq")
      .on(table.year, table.teamId)
      .where(sql`${table.scope} = 'team' AND ${table.userId} IS NULL`),
    teamIdx: index("goals_team_idx").on(table.teamId),
    userIdx: index("goals_user_idx").on(table.userId),
    yearIdx: index("goals_year_idx").on(table.year),
    scopeConsistency: check(
      "goals_scope_consistency_chk",
      sql`(${table.scope} = 'team' AND ${table.teamId} IS NOT NULL AND ${table.userId} IS NULL)
       OR (${table.scope} = 'personal' AND ${table.userId} IS NOT NULL AND ${table.teamId} IS NULL)`,
    ),
  }),
);

export const goalsRelations = relations(goals, ({ one }) => ({
  team: one(teams, {
    fields: [goals.teamId],
    references: [teams.id],
  }),
  user: one(users, {
    fields: [goals.userId],
    references: [users.id],
  }),
}));

export type Goal = typeof goals.$inferSelect;
export type InsertGoal = typeof goals.$inferInsert;

export const insertGoalSchema = createInsertSchema(goals);
export const selectGoalSchema = createSelectSchema(goals);

// ============================================================================
// In-App Notifications
// Stores user-facing notifications (e.g., TRAMS sync events, payment mismatches).
// ============================================================================

export const inAppNotifications = pgTable(
  "in_app_notifications",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    type: text("type").notNull(), // e.g. 'invoice_edit_rejected', 'payment_amount_mismatch', 'new_trams_invoice'
    title: text("title").notNull(),
    message: text("message").notNull(),
    metadata: jsonb("metadata"),
    read: boolean("read").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdx: index("in_app_notifications_user_idx").on(table.userId),
    userUnreadIdx: index("in_app_notifications_user_unread_idx")
      .on(table.userId, table.read)
      .where(sql`${table.read} = false`),
    typeIdx: index("in_app_notifications_type_idx").on(table.type),
    createdAtIdx: index("in_app_notifications_created_at_idx").on(table.createdAt),
  }),
);

export const inAppNotificationsRelations = relations(inAppNotifications, ({ one }) => ({
  user: one(users, {
    fields: [inAppNotifications.userId],
    references: [users.id],
  }),
}));

export type InAppNotification = typeof inAppNotifications.$inferSelect;
export type InsertInAppNotification = typeof inAppNotifications.$inferInsert;

// ============================================================================
// Unclaimed Commission — Acknowledgements
// One row per (user, terms_version). Records that the user watched the
// training video and accepted terms before the Unclaimed Commission report
// is unlocked. Versioning lets us re-prompt when the terms or video change.
// ============================================================================

export const unclaimedAcknowledgements = pgTable(
  "unclaimed_acknowledgements",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    termsVersion: text("terms_version").notNull(),
    videoId: text("video_id").notNull(),
    videoWatchedAt: timestamp("video_watched_at").notNull(),
    acceptedAt: timestamp("accepted_at").defaultNow().notNull(),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userTermsUniq: uniqueIndex("unclaimed_ack_user_terms_uniq").on(
      table.userId,
      table.termsVersion,
    ),
    userIdx: index("unclaimed_ack_user_idx").on(table.userId),
  }),
);

export const unclaimedAcknowledgementsRelations = relations(
  unclaimedAcknowledgements,
  ({ one }) => ({
    user: one(users, {
      fields: [unclaimedAcknowledgements.userId],
      references: [users.id],
    }),
  }),
);

export type UnclaimedAcknowledgement =
  typeof unclaimedAcknowledgements.$inferSelect;
export type InsertUnclaimedAcknowledgement =
  typeof unclaimedAcknowledgements.$inferInsert;

// ============================================================================
// Unclaimed Commission — Claims
// One row per individual claim submission. A "claim" maps exactly one
// unclaimed TRAMS invoice (tramsInvoiceNo) to either a NEW or EXISTING
// AIVIA invoice. The actual TRAMS write (clearing the placeholder agent)
// is performed manually by support after reviewing the email + claim
// record. This table is the source of truth for "did the user already
// claim this row?" and powers per-claim audit / idempotency.
// ============================================================================

export const unclaimedClaims = pgTable(
  "unclaimed_claims",
  {
    id: serial("id").primaryKey(),
    claimantUserId: integer("claimant_user_id")
      .notNull()
      .references(() => users.id),
    // FK is enforced as a composite (trams_account_id, claimant_user_id) →
    // (user_trams_accounts.id, user_id) in the table extras below, so an
    // advisor cannot attach a claim to another user's TRAMS account.
    // (Single-column ownership check at the service layer is preserved as
    // a UX-friendly fallback.)
    tramsAccountId: integer("trams_account_id").notNull(),
    // Source unclaimed TRAMS row. The unclaimed pool uses a special TRAMS
    // model: ONE invoice per calendar month, with each new unclaimed
    // commission appended as a new BOOKING (and AGENT_BKG line) under that
    // monthly invoice. So `tramsInvoiceNo` alone is NOT row-unique — the
    // composite (invoice, booking, agent_bkg) is.
    tramsInvoiceNo: integer("trams_invoice_no").notNull(),
    tramsBookingNo: integer("trams_booking_no").notNull(),
    tramsAgentBkgNo: integer("trams_agent_bkg_no").notNull(),
    // Target invoice — either an existing AIVIA invoice (FK) or a freshly
    // created one (also FK once the create-invoice path completes).
    targetKind: text("target_kind").notNull(), // 'existing' | 'new'
    targetInvoiceId: integer("target_invoice_id").references(
      () => invoices.id,
    ),
    targetInvoiceNumber: text("target_invoice_number"),
    // Idempotency: same key replays the same outcome (no duplicate emails).
    idempotencyKey: text("idempotency_key").notNull(),
    status: text("status").notNull().default("pending_review"), // pending_review | approved | rejected | withdrawn
    supportEmailSentAt: timestamp("support_email_sent_at"),
    supportTicketRef: text("support_ticket_ref"),
    // Optional free-text note from the member to Member Success (AR-245).
    note: text("note"),
    // Frozen copy of the Lambda payload at submit time. Lets support
    // reviewers see exactly what the user saw, even if TRAMS data later
    // shifts.
    payloadSnapshot: jsonb("payload_snapshot"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    claimantIdx: index("unclaimed_claims_claimant_idx").on(
      table.claimantUserId,
    ),
    // Composite lookup index used by the list endpoint to annotate rows
    // with their existing claim status (matches the row's natural key).
    tramsBookingIdx: index("unclaimed_claims_trams_booking_idx").on(
      table.tramsInvoiceNo,
      table.tramsBookingNo,
      table.tramsAgentBkgNo,
    ),
    statusIdx: index("unclaimed_claims_status_idx").on(table.status),
    // Idempotency uniqueness is per-user. Business rule: multiple advisors
    // can submit claims for the same booking-line simultaneously (accounting
    // arbitrates), so two advisors deriving the same FE key from the same
    // triple must NOT collide. Composite `(claimantUserId, idempotencyKey)`
    // gives each advisor their own idempotency namespace.
    idempotencyUniq: uniqueIndex("unclaimed_claims_idempotency_uniq").on(
      table.claimantUserId,
      table.idempotencyKey,
    ),
    // Spec invariant: at most one *active* (pending_review | approved)
    // claim per (user, booking-line). The unclaimed pool model is "many
    // advisors can claim the same row; accounting decides who actually
    // earned the commission and voids the others" — so we DO NOT enforce
    // cross-user uniqueness here. Withdrawn/rejected rows are excluded so
    // the same advisor can re-submit after accounting rejects them.
    activeClaimUniq: uniqueIndex("unclaimed_claims_active_uniq")
      .on(
        table.claimantUserId,
        table.tramsInvoiceNo,
        table.tramsBookingNo,
        table.tramsAgentBkgNo,
      )
      .where(sql`${table.status} IN ('pending_review', 'approved')`),
    targetKindChk: check(
      "unclaimed_claims_target_kind_chk",
      sql`${table.targetKind} IN ('existing', 'new')`,
    ),
    statusChk: check(
      "unclaimed_claims_status_chk",
      sql`${table.status} IN ('pending_review', 'approved', 'rejected', 'withdrawn')`,
    ),
    // Composite ownership FK: the chosen TRAMS account MUST belong to the
    // claimant user. (`user_trams_accounts` exposes a `(id, user_id)`
    // composite UNIQUE for exactly this purpose.) Without this, the
    // service-layer check could be bypassed by a direct DB insert.
    tramsAccountOwnerFk: foreignKey({
      columns: [table.tramsAccountId, table.claimantUserId],
      foreignColumns: [userTramsAccounts.id, userTramsAccounts.userId],
    }),
    // Consistency between targetKind and its identifier columns:
    //   - kind='existing' MUST carry the existing AIVIA invoice id.
    //   - kind='new'      MUST carry an invoice number (always populated
    //                     once the freshly-created invoice lands).
    // We deliberately do NOT require targetInvoiceId IS NULL for 'new',
    // because the wizard creates the row up-front and stores the id too —
    // the invariant we care about is "we always have something to point at".
    targetKindShapeChk: check(
      "unclaimed_claims_target_shape_chk",
      sql`(
        (${table.targetKind} = 'existing' AND ${table.targetInvoiceId} IS NOT NULL)
        OR
        (${table.targetKind} = 'new' AND ${table.targetInvoiceNumber} IS NOT NULL)
      )`,
    ),
  }),
);

export const unclaimedClaimsRelations = relations(unclaimedClaims, ({ one }) => ({
  claimant: one(users, {
    fields: [unclaimedClaims.claimantUserId],
    references: [users.id],
  }),
  tramsAccount: one(userTramsAccounts, {
    fields: [unclaimedClaims.tramsAccountId],
    references: [userTramsAccounts.id],
  }),
  targetInvoice: one(invoices, {
    fields: [unclaimedClaims.targetInvoiceId],
    references: [invoices.id],
  }),
}));

export type UnclaimedClaim = typeof unclaimedClaims.$inferSelect;
export type InsertUnclaimedClaim = typeof unclaimedClaims.$inferInsert;

// ============================================================================
// Impersonation Sessions
// Tracks admin "View As Member" sessions for the Invoice Tool.
// Each row represents an active or completed impersonation session.
// ============================================================================

export const impersonationSessions = pgTable(
  "impersonation_sessions",
  {
    id: serial("id").primaryKey(),
    adminUserId: integer("admin_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "restrict" }),
    targetUserId: integer("target_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "restrict" }),
    reason: text("reason"),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    startedAt: timestamp("started_at").defaultNow().notNull(),
    endedAt: timestamp("ended_at"),
    isActive: boolean("is_active").notNull().default(true),
  },
  (table) => ({
    adminIdx: index("impersonation_sessions_admin_idx").on(table.adminUserId),
    targetIdx: index("impersonation_sessions_target_idx").on(table.targetUserId),
    activeAdminUniq: uniqueIndex("impersonation_sessions_active_admin_uniq")
      .on(table.adminUserId)
      .where(sql`${table.isActive} = true`),
    activeEndedChk: check(
      "impersonation_sessions_active_ended_check",
      sql`(${table.isActive} = true AND ${table.endedAt} IS NULL) OR (${table.isActive} = false AND ${table.endedAt} IS NOT NULL)`,
    ),
  }),
);

export const impersonationSessionsRelations = relations(impersonationSessions, ({ one }) => ({
  admin: one(users, {
    fields: [impersonationSessions.adminUserId],
    references: [users.id],
    relationName: "impersonationAdmin",
  }),
  target: one(users, {
    fields: [impersonationSessions.targetUserId],
    references: [users.id],
    relationName: "impersonationTarget",
  }),
}));

export type ImpersonationSession = typeof impersonationSessions.$inferSelect;
export type InsertImpersonationSession = typeof impersonationSessions.$inferInsert;

// ============================================================================
// Sync Error Log
// Centralized log of TRAMS ↔ AIVIA sync failures and data conflicts.
// Admins view/resolve these from Admin → Invoice Tool → Sync Error Log.
// ============================================================================

export const SyncErrorTypeEnum = z.enum([
  "failed_sync",
  "data_conflict",
  "missing_data",
  "overwrite_error",
  "other",
]);
export type SyncErrorType = z.infer<typeof SyncErrorTypeEnum>;

export const SyncErrorStatusEnum = z.enum([
  "unresolved",
  "in_progress",
  "resolved",
]);
export type SyncErrorStatus = z.infer<typeof SyncErrorStatusEnum>;

export const SyncErrorResolutionSourceEnum = z.enum([
  "trams",
  "aivia",
]);
export type SyncErrorResolutionSource = z.infer<typeof SyncErrorResolutionSourceEnum>;

export const syncErrorLog = pgTable(
  "sync_error_log",
  {
    id: serial("id").primaryKey(),

    // Invoice identifiers
    invoiceId: integer("invoice_id").references(() => invoices.id, { onDelete: "set null" }),
    tramsInvoiceNo: integer("trams_invoice_no"),
    invoiceNumber: text("invoice_number"),

    // Member context
    memberId: integer("member_id").references(() => users.id, { onDelete: "set null" }),
    memberDisplayName: text("member_display_name"),

    // TRAMS account context
    tramsAccountId: integer("trams_account_id").references(() => userTramsAccounts.id, { onDelete: "set null" }),
    tramsProfileId: integer("trams_profile_id"),
    tramsInterfaceId: text("trams_interface_id"),

    // Error classification
    errorType: text("error_type").notNull().default("other"),
    errorSubtype: text("error_subtype"),
    errorMessage: text("error_message").notNull(),
    rawErrorDetails: jsonb("raw_error_details").$type<Record<string, unknown>>(),

    // Conflict-specific: snapshot of both sides for resolution
    tramsData: jsonb("trams_data").$type<Record<string, unknown>>(),
    aiviaData: jsonb("aivia_data").$type<Record<string, unknown>>(),

    // Status & resolution
    status: text("status").notNull().default("unresolved"),
    resolvedAt: timestamp("resolved_at"),
    resolvedByUserId: integer("resolved_by_user_id").references(() => users.id, { onDelete: "set null" }),
    resolutionNote: text("resolution_note"),
    resolutionSource: text("resolution_source"),

    // Deduplication / grouping
    correlationId: text("correlation_id"),
    operationId: text("operation_id"),
    occurrenceCount: integer("occurrence_count").notNull().default(1),
    lastOccurredAt: timestamp("last_occurred_at").defaultNow().notNull(),

    // Trip context
    tripId: integer("trip_id").references(() => trips.id, { onDelete: "set null" }),

    // Team context
    teamId: integer("team_id").references(() => teams.id, { onDelete: "set null" }),

    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    statusIdx: index("sync_error_log_status_idx").on(table.status),
    errorTypeIdx: index("sync_error_log_error_type_idx").on(table.errorType),
    memberIdx: index("sync_error_log_member_idx").on(table.memberId),
    invoiceIdx: index("sync_error_log_invoice_idx").on(table.invoiceId),
    tramsAccountIdx: index("sync_error_log_trams_account_idx").on(table.tramsAccountId),
    createdAtIdx: index("sync_error_log_created_at_idx").on(table.createdAt),
    correlationIdx: index("sync_error_log_correlation_idx").on(table.correlationId),
    teamIdx: index("sync_error_log_team_idx").on(table.teamId),
    // Partial: rows without an operationId are not deduplicated, and many
    // of them exist. Declared partial so a fresh `db:push` reproduces the
    // index prod actually has — `upsertByOperationId` repeats this exact
    // predicate as its ON CONFLICT target.
    operationIdUniqueIdx: uniqueIndex("sync_error_log_operation_id_unique")
      .on(table.operationId)
      .where(sql`${table.operationId} IS NOT NULL`),
    errorTypeChk: check(
      "sync_error_log_error_type_chk",
      sql`${table.errorType} IN ('failed_sync', 'data_conflict', 'missing_data', 'overwrite_error', 'other')`,
    ),
    statusChk: check(
      "sync_error_log_status_chk",
      sql`${table.status} IN ('unresolved', 'in_progress', 'resolved')`,
    ),
    resolutionSourceChk: check(
      "sync_error_log_resolution_source_chk",
      sql`${table.resolutionSource} IN ('trams', 'aivia')`,
    ),
  }),
);

export const syncErrorLogRelations = relations(syncErrorLog, ({ one }) => ({
  invoice: one(invoices, {
    fields: [syncErrorLog.invoiceId],
    references: [invoices.id],
  }),
  member: one(users, {
    fields: [syncErrorLog.memberId],
    references: [users.id],
    relationName: "syncErrorLogMember",
  }),
  resolvedBy: one(users, {
    fields: [syncErrorLog.resolvedByUserId],
    references: [users.id],
    relationName: "syncErrorLogResolver",
  }),
  tramsAccount: one(userTramsAccounts, {
    fields: [syncErrorLog.tramsAccountId],
    references: [userTramsAccounts.id],
  }),
  trip: one(trips, {
    fields: [syncErrorLog.tripId],
    references: [trips.id],
  }),
  team: one(teams, {
    fields: [syncErrorLog.teamId],
    references: [teams.id],
  }),
}));

export type SyncErrorLogEntry = typeof syncErrorLog.$inferSelect;
export type InsertSyncErrorLogEntry = typeof syncErrorLog.$inferInsert;

/** Idempotency ledger for the daily sync-error digest email (one row per ET calendar day). */
export const syncErrorDigestRuns = pgTable(
  "sync_error_digest_runs",
  {
    id: serial("id").primaryKey(),
    digestDateEt: date("digest_date_et").notNull(),
    /** Set when the send slot is claimed; cleared only by delete on failed send. */
    claimedAt: timestamp("claimed_at").defaultNow().notNull(),
    /** Set only after email is successfully sent. */
    sentAt: timestamp("sent_at"),
    entryCount: integer("entry_count").notNull().default(0),
    recipientCount: integer("recipient_count").notNull().default(0),
  },
  (table) => ({
    digestDateUnique: uniqueIndex("sync_error_digest_runs_date_et_unique").on(
      table.digestDateEt,
    ),
  }),
);

export type SyncErrorDigestRun = typeof syncErrorDigestRuns.$inferSelect;

/** Idempotency ledger for weekly statement notification emails (one row per user + statement date). */
export const statementEmailSends = pgTable(
  "statement_email_sends",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    statementDate: date("statement_date").notNull(),
    recipientEmail: text("recipient_email").notNull(),
    claimId: pgUuid("claim_id").defaultRandom().notNull(),
    /** Set when a send slot is claimed; used to detect stale in-flight claims. */
    claimedAt: timestamp("claimed_at").defaultNow().notNull(),
    /** Set only after email is successfully sent. */
    sentAt: timestamp("sent_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    userStatementUnique: uniqueIndex("statement_email_sends_user_date_unique").on(
      table.userId,
      table.statementDate,
    ),
    statementDateIdx: index("statement_email_sends_statement_date_idx").on(
      table.statementDate,
    ),
  }),
);

export type StatementEmailSend = typeof statementEmailSends.$inferSelect;

// ============================================================================
// INVOICE CONFIRMATION EMAIL SENDS
// Claim/finalize ledger (statement_email_sends pattern) for at-least-once
// delivery of one confirmation email per invoice-creation submission. Keyed
// on (user_id, submission_key) — NOT invoice id — because one submission can
// create several invoices and must produce a single grouped email.
// The unique claim slot prevents concurrent live sends; a stale-claim
// reclaim can still produce a duplicate if the first attempt already left
// the provider. The x-aivia-invoice-confirmation header is correlation only.
// ============================================================================

export const invoiceConfirmationEmailSends = pgTable(
  "invoice_confirmation_email_sends",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    /** Idempotency key of the creation submission (batch identifier). */
    submissionKey: text("submission_key").notNull(),
    recipientEmail: text("recipient_email").notNull(),
    /** Invoice ids covered by this email (jsonb number[]). */
    invoiceIds: jsonb("invoice_ids").notNull(),
    claimId: pgUuid("claim_id").defaultRandom().notNull(),
    /** Set when a send slot is claimed; used to detect stale in-flight claims. */
    claimedAt: timestamp("claimed_at").defaultNow().notNull(),
    /** Set only after the mail provider confirms delivery. */
    sentAt: timestamp("sent_at"),
    attemptCount: integer("attempt_count").notNull().default(0),
    lastError: text("last_error"),
    lastAttemptAt: timestamp("last_attempt_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    userSubmissionUnique: uniqueIndex(
      "invoice_confirmation_email_sends_user_submission_unique",
    ).on(table.userId, table.submissionKey),
    unsentIdx: index("invoice_confirmation_email_sends_unsent_idx")
      .on(table.claimedAt)
      .where(sql`${table.sentAt} IS NULL`),
  }),
);

export type InvoiceConfirmationEmailSend =
  typeof invoiceConfirmationEmailSends.$inferSelect;

// ============================================================================
// STATEMENT SNAPSHOTS
// Immutable record of statement periods. Once a snapshot is generated,
// the statement's line items are frozen and cannot change.
// ============================================================================

export const StatementStatusEnum = z.enum(['generated', 'emailed', 'failed']);
export type StatementStatus = z.infer<typeof StatementStatusEnum>;

export const statements = pgTable(
  "statements",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    statementDate: date("statement_date").notNull(),
    status: text("status").notNull().default("generated"),
    generatedAt: timestamp("generated_at").defaultNow().notNull(),
    emailSentAt: timestamp("email_sent_at"),
    /**
     * Alias of `grossCommission`, retained so existing readers keep working
     * while they migrate to the split columns below. Writers must set both.
     */
    totalCommission: decimal("total_commission", { precision: 12, scale: 2 }).notNull().default("0"),
    /** Sum of BOOKING.COMMAMT across the week's lines — what the agency earned. */
    grossCommission: decimal("gross_commission", { precision: 12, scale: 2 }).notNull().default("0"),
    /** Sum of AGENTBKG.AMOUNT — the advisor's share of the gross. */
    agentCommission: decimal("agent_commission", { precision: 12, scale: 2 }).notNull().default("0"),
    /** Agent commission plus adjustments — what was actually paid out. */
    netPayout: decimal("net_payout", { precision: 12, scale: 2 }).notNull().default("0"),
    totalFare: decimal("total_fare", { precision: 12, scale: 2 }).notNull().default("0"),
    invoiceCount: integer("invoice_count").notNull().default(0),
    adjustmentCount: integer("adjustment_count").notNull().default(0),
    adjustmentTotal: decimal("adjustment_total", { precision: 12, scale: 2 }).notNull().default("0"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    userStatementDateUnique: uniqueIndex("statements_user_statement_date_unique").on(
      table.userId,
      table.statementDate,
    ),
    statementDateIdx: index("statements_statement_date_idx").on(table.statementDate),
    statusIdx: index("statements_status_idx").on(table.status),
    statusChk: check(
      "statements_status_check",
      sql`status IN ('generated', 'emailed', 'failed')`,
    ),
  }),
);

export const statementsRelations = relations(statements, ({ one, many }) => ({
  user: one(users, { fields: [statements.userId], references: [users.id] }),
  lineItems: many(statementLineItems),
  adjustmentItems: many(statementAdjustmentItems),
}));

export type Statement = typeof statements.$inferSelect;
export type InsertStatement = typeof statements.$inferInsert;

// ============================================================================
// STATEMENT LINE ITEMS (Invoice Snapshots)
// Immutable copy of invoice data at the time a statement was generated.
// ============================================================================

export const statementLineItems = pgTable(
  "statement_line_items",
  {
    id: serial("id").primaryKey(),
    statementId: integer("statement_id")
      .notNull()
      .references(() => statements.id, { onDelete: "cascade" }),
    /**
     * Nullable because a paid TRAMS line must be storable before its local
     * invoice exists. Dropping such lines would understate the advisor's payout
     * and hide how many invoices never synced.
     */
    invoiceId: integer("invoice_id").references(() => invoices.id, {
      onDelete: "restrict",
    }),
    /** Frozen owning TRAMS account, so snapshots can be filtered per account. */
    tramsAccountId: integer("trams_account_id"),
    tramsInvoiceNo: integer("trams_invoice_no"),
    /** Frozen TRAMS INVOICE.INVOICENUMBER (metadata.tramsInvoiceNumber) for display parity. */
    tramsInvoiceNumber: text("trams_invoice_number"),
    tramsBookingNo: integer("trams_booking_no"),
    /**
     * AGENTBKG.AGENTBKGNO — one row per agent per booking, the finest grain a
     * TRAMS statement has. Two bookings on one invoice legitimately produce two
     * lines on the same statement, and only this column tells them apart.
     */
    tramsAgentBkgNo: integer("trams_agent_bkg_no"),
    recordLocator: varchar("record_locator", { length: 6 }),
    invoiceNumber: text("invoice_number"),
    primaryTraveler: text("primary_traveler"),
    supplierName: text("supplier_name"),
    travelType: text("travel_type"),
    confirmationNumber: text("confirmation_number"),
    departureDate: timestamp("departure_date"),
    returnDate: timestamp("return_date"),
    totalFare: decimal("total_fare", { precision: 12, scale: 2 }),
    commissionAmount: decimal("commission_amount", { precision: 12, scale: 2 }),
    paidAt: timestamp("paid_at"),
    issueDate: timestamp("issue_date"),
    /**
     * The date the line falls under inside the Wed–Tue window: INVOICE.ISSUEDATE
     * for ARC and Supplier, earliest vendor PAYMENT.PAYMENTDATE for Comm Track.
     * Sourced from TRAMS, never derived from local paid_at.
     */
    reportDate: date("report_date"),
    /** TRAMS branch label: ARC Non-CC, ARC CC, Supplier Non-CC, Supplier CC, Supplier CC No Remit, Comm Track. */
    section: text("section"),
    /**
     * Which writer produced the row. `trams_ingest` is the statement log ingest;
     * `local_snapshot` is the retired writer that derived lines from local
     * invoices. A backfilled range should contain no `local_snapshot` rows, and
     * the read path treats the two differently — only local rows carry a usable
     * `owner_*` pair.
     */
    source: text("source").notNull().default("local_snapshot"),
    /**
     * The advisor who sold the booking, which is not necessarily whose statement
     * this is: on an agency owner's statement every line is typically sold by an
     * associate. Resolved by grouping the week's lines by booking and taking the
     * agent who is not the AGENCY_OWNER.
     *
     * Identifiers rather than a name, so the name resolves at read time. No
     * foreign key on the user id: this is frozen historical attribution, and
     * deleting a user should neither be blocked by it nor rewrite who sold what.
     * Null user id with a non-null profile number means the seller is a TRAMS
     * profile with no AIVIA account.
     */
    sellingAgentProfileNo: integer("selling_agent_profile_no"),
    sellingAgentUserId: integer("selling_agent_user_id"),
    /** Frozen pay-status codes, so void exclusion can be proven after the fact. */
    clientPayStatus: text("client_pay_status"),
    vendorPayStatus: text("vendor_pay_status"),
    description: text("description"),
    agentRatePercent: decimal("agent_rate_percent", { precision: 5, scale: 2 }),
    agentCommissionAmount: decimal("agent_commission_amount", { precision: 12, scale: 2 }),
    // Owner-perspective rate/commission (agency owner's team statement). Differs
    // from agent_* only for associate advisors: agent_* is 0 (personal view),
    // owner_* applies the agency owner's payout rate (owner-view earnings).
    ownerRatePercent: decimal("owner_rate_percent", { precision: 5, scale: 2 }),
    ownerCommissionAmount: decimal("owner_commission_amount", { precision: 12, scale: 2 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    statementIdx: index("statement_line_items_statement_idx").on(table.statementId),
    statementAccountIdx: index("statement_line_items_statement_account_idx").on(
      table.statementId,
      table.tramsAccountId,
    ),
    invoiceIdx: index("statement_line_items_invoice_idx").on(table.invoiceId),
    /**
     * Keyed on the full TRAMS identity rather than (statement_id, invoice_id),
     * which collapsed two agent bookings on one invoice into a single row and
     * silently lost a paid line. NULL AGENTBKGNO values are equal, matching the
     * invoices table: one row per (statement, invoice, booking) when the agent
     * booking is unknown. Invoice + booking must be present so two incomplete
     * keys do not collide.
     */
    statementTramsIdentityUnique: uniqueIndex(
      "statement_line_items_statement_trams_identity_unique",
    )
      .on(table.statementId, table.tramsInvoiceNo, table.tramsBookingNo, table.tramsAgentBkgNo)
      .where(sql`${table.tramsInvoiceNo} IS NOT NULL AND ${table.tramsBookingNo} IS NOT NULL AND ${table.tramsAgentBkgNo} IS NOT NULL`),
    statementTramsNullAgentUnique: uniqueIndex(
      "statement_line_items_statement_trams_null_agent_unique",
    )
      .on(table.statementId, table.tramsInvoiceNo, table.tramsBookingNo)
      .where(sql`${table.tramsInvoiceNo} IS NOT NULL AND ${table.tramsBookingNo} IS NOT NULL AND ${table.tramsAgentBkgNo} IS NULL`),
    sourceChk: check(
      "statement_line_items_source_check",
      sql`source IN ('trams_ingest', 'local_snapshot')`,
    ),
    sellingAgentIdx: index("statement_line_items_selling_agent_idx").on(
      table.sellingAgentUserId,
    ),
  }),
);

export const statementLineItemsRelations = relations(statementLineItems, ({ one }) => ({
  statement: one(statements, { fields: [statementLineItems.statementId], references: [statements.id] }),
  invoice: one(invoices, { fields: [statementLineItems.invoiceId], references: [invoices.id] }),
}));

export type StatementLineItem = typeof statementLineItems.$inferSelect;
export type InsertStatementLineItem = typeof statementLineItems.$inferInsert;

// ============================================================================
// STATEMENT ADJUSTMENT ITEMS (Adjustment Snapshots)
// Immutable copy of adjustment data at the time a statement was generated.
// ============================================================================

export const statementAdjustmentItems = pgTable(
  "statement_adjustment_items",
  {
    id: serial("id").primaryKey(),
    statementId: integer("statement_id")
      .notNull()
      .references(() => statements.id, { onDelete: "cascade" }),
    /**
     * Nullable for the same reason `statement_line_items.invoice_id` is: a TRAMS
     * adjustment with no local `agent_adjustments` row still has to be storable,
     * or a commission recall silently drops out of the advisor's net payout.
     */
    adjustmentId: integer("adjustment_id").references(() => agentAdjustments.id, {
      onDelete: "restrict",
    }),
    /** Frozen owning TRAMS account, so adjustments can be filtered per account. */
    tramsAccountId: integer("trams_account_id"),
    adjustmentNo: integer("adjustment_no").notNull(),
    adjustmentDate: timestamp("adjustment_date").notNull(),
    amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
    remarks: text("remarks"),
    /** Which writer produced the row. See `statementLineItems.source`. */
    source: text("source").notNull().default("local_snapshot"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    statementIdx: index("statement_adjustment_items_statement_idx").on(table.statementId),
    adjustmentIdx: index("statement_adjustment_items_adjustment_idx").on(table.adjustmentId),
    /**
     * Keyed on the TRAMS identity (`adjustment_no`) rather than the local
     * `adjustment_id`, which is a convenience that may be absent.
     */
    statementAdjustmentUnique: uniqueIndex("statement_adjustment_items_stmt_adj_unique").on(
      table.statementId,
      table.adjustmentNo,
    ),
    sourceChk: check(
      "statement_adjustment_items_source_check",
      sql`source IN ('trams_ingest', 'local_snapshot')`,
    ),
  }),
);

export const statementAdjustmentItemsRelations = relations(statementAdjustmentItems, ({ one }) => ({
  statement: one(statements, { fields: [statementAdjustmentItems.statementId], references: [statements.id] }),
  adjustment: one(agentAdjustments, { fields: [statementAdjustmentItems.adjustmentId], references: [agentAdjustments.id] }),
}));

export type StatementAdjustmentItem = typeof statementAdjustmentItems.$inferSelect;
export type InsertStatementAdjustmentItem = typeof statementAdjustmentItems.$inferInsert;

// ============================================================================
// AR-13 · Onboarding identity & account data model
// ----------------------------------------------------------------------------
// Scope ref: §10.1 "AIVIA Onboarding Data Model". Deliberate deviations from
// the ticket's literal table list, per that data model:
//   • users is EXTENDED (columns above) — no parallel onboarding users table.
//   • `agencies` is NOT created: `teams` is the agency record
//     (agency_name → teams.name, owner → teams.agency_owner_user_id).
//   • user_role is NOT a new enum column: onboarding routes map onto the
//     existing roles / user_role_assignments RBAC tables (routes.is_associate).
//   • plans / routes / pathways are created here because AR-13's FKs require
//     them and no reference-data story shipped them first.
// ============================================================================

// ── Reference & config ──────────────────────────────────────────────────────

export const plans = pgTable("plans", {
  planId: serial("plan_id").primaryKey(),
  name: varchar("name", { length: 100 }).notNull().unique(), // Individual, Growth, Team, Enterprise
  stripePriceId: varchar("stripe_price_id", { length: 255 }).notNull(),
  monthlyPrice: decimal("monthly_price", { precision: 10, scale: 2 }).notNull(),
  setupFee: decimal("setup_fee", { precision: 10, scale: 2 }),
  gdsEligible: boolean("gds_eligible").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  // ── AR-344 · Onboarding domain ─────────────────────────────────────────────
  slug: varchar("slug", { length: 50 }).unique(), // stable key for code to reference
  tier: varchar("tier", { length: 30 }), // values TBD with Britta
  stripeProductId: varchar("stripe_product_id", { length: 255 }),
  allowsAssociates: boolean("allows_associates").notNull().default(false),
  associateSeatLimit: integer("associate_seat_limit"), // null = unlimited
  // ── AR-419 · Stripe catalogue ──────────────────────────────────────────────
  // Filled by `server-nest/scripts/seed-stripe-catalogue.ts` FROM the Stripe
  // account the environment's key points at; nothing here is typed by hand.
  // All nullable: a row that predates the seed is still a valid plan. The seed
  // also writes `stripe_product_id` (AR-344, above).
  //
  // `stripe_price_id` stays the MONTHLY recurring price — the only interval
  // sold to new members. Starter is the one legacy plan sold annual only; its
  // `stripe_price_id` is the annual price and `monthly_price` is annual/12 for
  // display. Legacy rows are `is_active = false` and never offered at
  // checkout; they exist so the AR-427 import has a row to point each existing
  // subscription at.
  //
  // One-time "Account setup and activation fee" price on the same product.
  // Charged once on the first invoice via `add_invoice_items`; `setup_fee`
  // above holds its amount.
  stripeSetupFeePriceId: varchar("stripe_setup_fee_price_id", { length: 255 }),
  // Annual price — legacy plans only (current plans have annual products in
  // Stripe with zero subscribers; they are deliberately not recorded).
  stripeAnnualPriceId: varchar("stripe_annual_price_id", { length: 255 }),
  annualPrice: decimal("annual_price", { precision: 10, scale: 2 }),
  // Per-seat price for associates beyond `included_seats` (Team Plan
  // Additional Agents today). Null = the plan does not sell seats.
  // `included_seats` is how many associates the plan price already covers;
  // AR-344's `associate_seat_limit` is the cap on how many it may have.
  stripeSeatPriceId: varchar("stripe_seat_price_id", { length: 255 }),
  seatMonthlyPrice: decimal("seat_monthly_price", { precision: 10, scale: 2 }),
  includedSeats: integer("included_seats"),
  // Ordering for upgrade / downgrade decisions (AR-423): higher rank = higher
  // plan. Legacy plans share rank 0 and can only move to an active plan.
  rank: integer("rank"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const routes = pgTable("routes", {
  routeId: serial("route_id").primaryKey(),
  name: varchar("name", { length: 120 }).notNull().unique(), // Single Advisor - New / Experienced, Multi-Agent Agency, Associate Advisor
  description: text("description"),
  planEligibility: varchar("plan_eligibility", { length: 255 }),
  isAssociate: boolean("is_associate").notNull().default(false),
  // ── AR-344 · Onboarding domain ─────────────────────────────────────────────
  // single_advisor_new / single_advisor_experienced / multi_agent_agency / associate_advisor
  slug: varchar("slug", { length: 50 }).unique(),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const pathways = pgTable("pathways", {
  pathwayId: serial("pathway_id").primaryKey(),
  name: varchar("name", { length: 120 }).notNull().unique(), // Sea + Sand, World Explorer, Theme Parks, Corporate Travel — exactly four, no Cruise (AR-344)
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  // ── AR-344 · Onboarding domain ─────────────────────────────────────────────
  slug: varchar("slug", { length: 50 }).unique(), // sea_and_sand / world_explorer / theme_parks / corporate_travel
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ── Supporting identity tables ──────────────────────────────────────────────

export const addresses = pgTable(
  "addresses",
  {
    addressId: serial("address_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    addressType: addressTypeEnum("address_type").notNull(),
    street: varchar("street", { length: 255 }).notNull(),
    line2: varchar("line2", { length: 255 }),
    city: varchar("city", { length: 120 }).notNull(),
    state: varchar("state", { length: 60 }).notNull(),
    zip: varchar("zip", { length: 20 }).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdx: index("addresses_user_id_idx").on(table.userId),
    // One current address per type per user — the PRO import upserts on this
    // pair, and without it two concurrent imports both insert a billing row.
    userTypeUnique: uniqueIndex("addresses_user_id_address_type_unique").on(
      table.userId,
      table.addressType,
    ),
  }),
);

/**
 * Cross-system identity links — one row per (user, system), so a single member
 * carries AIVIA / PRO / Auth0 / HubSpot contact+company / Stripe rows at once.
 *
 * (system_name, external_id) is intentionally NOT unique: several advisors in
 * one agency legitimately share a hubspot_company id. The PRO import resolves
 * a member by (system_name='worldvia_pro', external_id=<PRO _id>).
 */
export const externalSystemIds = pgTable(
  "external_system_ids",
  {
    linkId: serial("link_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    systemName: externalSystemNameEnum("system_name").notNull(),
    externalId: varchar("external_id", { length: 255 }).notNull(),
    status: varchar("status", { length: 32 }).notNull().default("connected"), // connected / created / conflict / error
    connectedAt: timestamp("connected_at").defaultNow().notNull(),
    // ── AR-330 · User-domain additive batch ──────────────────────────────────
    // AR-332 inverts the direction of this link: AIVIA mints a key, writes it
    // into a custom property on the vendor record, and resolves by that
    // property forever after. The vendor's own record id is demoted to a cache
    // for deep links, because HubSpot mints a fresh Record ID on every merge.
    // All nullable; `external_id` above is untouched and stays the read path
    // until FEATURE_EXTERNAL_ID_RESOLUTION is on.
    keyProperty: varchar("key_property", { length: 128 }), // e.g. 'aivia_user_id'
    keyValue: varchar("key_value", { length: 255 }), // what we wrote — normally users.uuid
    // CACHE ONLY. Never a join key. Refreshed each sync, used for admin deep
    // links. Its unique index on (system_name, key_value) is deliberately NOT
    // here — AR-330 adds no unique index touching an existing column; AR-346
    // owns it.
    externalRecordId: varchar("external_record_id", { length: 255 }),
    resolution: varchar("resolution", { length: 16 }), // id_property / record_id / email / unresolved
    lastPushedAt: timestamp("last_pushed_at"),
    lastVerifiedAt: timestamp("last_verified_at"),
    lastError: text("last_error"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userSystemUnique: uniqueIndex("external_system_ids_user_id_system_name_unique").on(
      table.userId,
      table.systemName,
    ),
    systemExternalIdx: index("external_system_ids_system_name_external_id_idx").on(
      table.systemName,
      table.externalId,
    ),
    userIdx: index("external_system_ids_user_id_idx").on(table.userId),
  }),
);

export const subscriptions = pgTable(
  "subscriptions",
  {
    subscriptionId: serial("subscription_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    stripeCustomerId: varchar("stripe_customer_id", { length: 255 }).notNull(),
    stripeSubscriptionId: varchar("stripe_subscription_id", { length: 255 }).notNull().unique(),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.planId, { onDelete: "restrict" }),
    couponCode: varchar("coupon_code", { length: 100 }),
    oneTimeFeePaid: decimal("one_time_fee_paid", { precision: 10, scale: 2 }),
    status: subscriptionStatusEnum("status").notNull(),
    // ── AR-344 · Onboarding domain ───────────────────────────────────────────
    startedAt: timestamp("started_at"),
    currentPeriodEnd: timestamp("current_period_end"),
    cancelAtPeriodEnd: boolean("cancel_at_period_end").notNull().default(false),
    planChangedAt: timestamp("plan_changed_at"), // inert until the 12-month rule is written down
    // ── AR-419 · Stripe subscription state ─────────────────────────────────
    // Mirrors of the Stripe subscription, written by the webhook handlers
    // (AR-420/423) and the import (AR-427). All nullable; Stripe stays the
    // source of truth and these are what the Billing page and the lifecycle
    // rules read without a round trip. Period and cancel-at-period-end are
    // AR-344's columns above.
    //
    // `aivia_product` is the same tag the subscription carries in Stripe
    // metadata: `membership` (the plan), `seat` (a legacy per-associate seat
    // subscription recorded against the owner). Sites subscriptions live on
    // `user_website_accounts`, never here.
    aiviaProduct: varchar("aivia_product", { length: 16 }),
    stripePriceId: varchar("stripe_price_id", { length: 255 }),
    billingInterval: varchar("billing_interval", { length: 16 }), // month / year
    seatQuantity: integer("seat_quantity"),
    cancelAt: timestamp("cancel_at"),
    canceledAt: timestamp("canceled_at"),
    latestInvoiceId: varchar("latest_invoice_id", { length: 255 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdx: index("subscriptions_user_id_idx").on(table.userId),
    planIdx: index("subscriptions_plan_id_idx").on(table.planId),
  }),
);

/**
 * AR-419 · One row per checkout attempt (account-first: the `users` row with
 * `checkout_pending` exists before this does). AR-420 drives it through
 * `started → contact → payment_pending → paid`; `abandoned` after 30 days with
 * no payment. Resuming a checkout re-reads this row, so a refresh mid-flow
 * loses nothing and a declined card leaves it retryable (`attempt` counts the
 * Stripe subscription creates, which is what the idempotency key is built
 * from: `subscription:create:<checkout_session_id>:<attempt>`).
 */
export const checkoutSessions = pgTable(
  "checkout_sessions",
  {
    checkoutSessionId: serial("checkout_session_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.planId, { onDelete: "restrict" }),
    status: varchar("status", { length: 24 }).notNull().default("started"), // started / contact / payment_pending / paid / abandoned
    step: integer("step").notNull().default(1),
    attempt: integer("attempt").notNull().default(0),
    couponCode: varchar("coupon_code", { length: 100 }),
    acceleratorSelected: boolean("accelerator_selected").notNull().default(false),
    stripeCustomerId: varchar("stripe_customer_id", { length: 255 }),
    stripeSubscriptionId: varchar("stripe_subscription_id", { length: 255 }),
    stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
    lastError: text("last_error"),
    expiresAt: timestamp("expires_at").notNull(),
    paidAt: timestamp("paid_at"),
    // ── AR-420 ──
    // Everything the member typed on the contact screen, as entered. The
    // canonical fields are also written to users / addresses / user_profiles;
    // this keeps the ones with no home yet (agency name, associate count) for
    // provisioning, and is what a resumed checkout re-fills the form from.
    contact: jsonb("contact"),
    // When the member ticked the membership terms on the last screen.
    termsAcceptedAt: timestamp("terms_accepted_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdx: index("checkout_sessions_user_id_idx").on(table.userId),
    statusIdx: index("checkout_sessions_status_idx").on(table.status),
    stripeSubscriptionIdx: index("checkout_sessions_stripe_subscription_id_idx").on(
      table.stripeSubscriptionId,
    ),
  }),
);

// ── Inferred types & insert schemas ─────────────────────────────────────────

export type Plan = typeof plans.$inferSelect;
export type InsertPlan = typeof plans.$inferInsert;
export const insertPlanSchema = createInsertSchema(plans);

export type Route = typeof routes.$inferSelect;
export type InsertRoute = typeof routes.$inferInsert;
export const insertRouteSchema = createInsertSchema(routes);

export type Pathway = typeof pathways.$inferSelect;
export type InsertPathway = typeof pathways.$inferInsert;
export const insertPathwaySchema = createInsertSchema(pathways);

export type Address = typeof addresses.$inferSelect;
export type InsertAddress = typeof addresses.$inferInsert;
export const insertAddressSchema = createInsertSchema(addresses);

export type ExternalSystemId = typeof externalSystemIds.$inferSelect;
export type InsertExternalSystemId = typeof externalSystemIds.$inferInsert;
export const insertExternalSystemIdSchema = createInsertSchema(externalSystemIds);

export type Subscription = typeof subscriptions.$inferSelect;
export type InsertSubscription = typeof subscriptions.$inferInsert;
export const insertSubscriptionSchema = createInsertSchema(subscriptions);

export type CheckoutSession = typeof checkoutSessions.$inferSelect;
export type InsertCheckoutSession = typeof checkoutSessions.$inferInsert;
export const insertCheckoutSessionSchema = createInsertSchema(checkoutSessions);

export type OnboardingStatus = (typeof onboardingStatusEnum.enumValues)[number];
export type AddressType = (typeof addressTypeEnum.enumValues)[number];
export type ExternalSystemName = (typeof externalSystemNameEnum.enumValues)[number];
export type SubscriptionStatus = (typeof subscriptionStatusEnum.enumValues)[number];

// ============================================================================
// AR-330 · User-domain additive batch — new tables
//
// Every table here arrives EMPTY with no reader. Each one names the ticket that
// turns it on behind its own feature flag. Deliberately absent, per AR-330's
// exclusion list and the tickets that own them:
//   · no NOT NULL / CHECK / unique index on any pre-existing column
//   · no team_memberships constraints — AR-338 Stage 3 applies those as
//     handwritten SQL, and only after AR-329 reads zero
//   · no query indexes on existing tables — AR-346, with CREATE INDEX
//     CONCURRENTLY in its own migration file
// The indexes below are only on these new tables, where the table is empty and
// the index cannot fail to apply.
// ============================================================================

/**
 * The reason taxonomy that status changes point at (AR-337).
 *
 * A table rather than an enum on purpose: PRO carries ~23 Downgrade Reasons,
 * 19 sub-reasons and a separate Suspend Reasons set, all with HubSpot internal
 * ids, and marketing changes them. An enum would need a migration per edit.
 */
export const statusReasonCodes = pgTable(
  "status_reason_codes",
  {
    reasonId: serial("reason_id").primaryKey(),
    code: varchar("code", { length: 64 }).notNull().unique(),
    label: varchar("label", { length: 255 }).notNull(),
    // Which vocabulary this belongs to: 'downgrade' | 'suspend' | 'deactivate'
    // | 'reactivate'. PRO keeps these as separate lists.
    category: varchar("category", { length: 32 }).notNull(),
    // Self-referencing so sub-reasons hang off their main reason.
    parentReasonId: integer("parent_reason_id")
      .references((): AnyPgColumn => statusReasonCodes.reasonId, { onDelete: "set null" }),
    // Preserved from PRO so churn reporting still lands in HubSpot.
    hubspotInternalId: varchar("hubspot_internal_id", { length: 255 }),
    isActive: boolean("is_active").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    parentIdx: index("status_reason_codes_parent_reason_id_idx").on(table.parentReasonId),
    categoryIdx: index("status_reason_codes_category_idx").on(table.category),
  }),
);

/**
 * One row per (provider, provider_subject) — the link between an AIVIA user and
 * every external identity provider (AR-331).
 *
 * `users.auth0_sub` is a single text column, so it holds exactly one provider
 * link. It stays as a denormalised pointer and is dual-written; this table is
 * what lets a member sign in through Auth0 AND hold a TRIO SSO principal at the
 * same time.
 *
 * Kept separate from `user_trio_accounts` below: deactivation should be able to
 * reclaim a TRIO EPR without destroying the sign-in link.
 */
export const userIdentities = pgTable(
  "user_identities",
  {
    identityId: serial("identity_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    provider: varchar("provider", { length: 32 }).notNull(), // auth0 / trio_sso / google / bubble_legacy
    providerSubject: varchar("provider_subject", { length: 255 }).notNull(), // the sub / external principal
    connection: varchar("connection", { length: 255 }), // Auth0 connection name or SSO entity id
    isPrimary: boolean("is_primary").notNull().default(false),
    emailAtProvider: varchar("email_at_provider", { length: 255 }), // detects divergence from users.email
    mfaEnrolled: boolean("mfa_enrolled"), // cached from Auth0, not authoritative
    lastLoginAt: timestamp("last_login_at"), // per-provider
    linkedAt: timestamp("linked_at").defaultNow().notNull(),
    unlinkedAt: timestamp("unlinked_at"), // soft unlink — never delete the row
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    // A duplicate link must be rejected, not silently overwritten (AR-331 AC).
    providerSubjectUnique: uniqueIndex("user_identities_provider_provider_subject_unique").on(
      table.provider,
      table.providerSubject,
    ),
    // "exactly one per user" — one *live* primary, so secondary rows are free
    // and so are soft-unlinked ones. `unlinked_at IS NULL` is load-bearing:
    // AR-331 unlinks by setting the timestamp and keeping the row, so without
    // it a row that was primary before being unlinked still occupies the slot
    // and the user's next primary link fails on this index.
    onePrimaryPerUser: uniqueIndex("user_identities_one_primary_idx")
      .on(table.userId)
      .where(sql`${table.isPrimary} AND ${table.unlinkedAt} IS NULL`),
    userIdx: index("user_identities_user_id_idx").on(table.userId),
  }),
);

/**
 * Append-only audit of what happens to external id links (AR-332).
 *
 * If `external_record_id` changes but `key_value` still resolves, that is a
 * vendor-side merge, and it gets logged rather than silently absorbed. This log
 * is the first honest visibility anyone has had into how often that happens.
 *
 * `system_name` reuses the existing `external_system_name` enum. AR-332 also
 * names freshdesk and duda, which that type does not carry yet — adding values
 * to a live enum is left to the ticket that needs them, so this batch cannot
 * produce a destructive push prompt.
 */
export const externalIdEvents = pgTable(
  "external_id_events",
  {
    eventId: serial("event_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    systemName: externalSystemNameEnum("system_name").notNull(),
    eventType: varchar("event_type", { length: 32 }).notNull(), // merge_detected / rebind / conflict / created / orphaned
    keyValue: varchar("key_value", { length: 255 }), // the key we own, as it stood at the event
    previousExternalRecordId: varchar("previous_external_record_id", { length: 255 }),
    newExternalRecordId: varchar("new_external_record_id", { length: 255 }),
    detail: jsonb("detail"), // raw vendor payload / diff, for after-the-fact reading
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdx: index("external_id_events_user_id_idx").on(table.userId),
    systemEventIdx: index("external_id_events_system_name_event_type_idx").on(
      table.systemName,
      table.eventType,
    ),
  }),
);

/**
 * The TRIO GDS account provisioned for a member — 1:1 with users (AR-334).
 *
 * PRO carries twenty TRIO_* columns flat on the user record, where none of it
 * can express a lifecycle and deactivation has to reach into user columns to
 * unwind provisioning. Distinct from `user_trams_accounts`, which is the TRAMS
 * back-office profile.
 */
export const userTrioAccounts = pgTable(
  "user_trio_accounts",
  {
    trioAccountId: serial("trio_account_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    isEnabled: boolean("is_enabled").notNull().default(false),
    eprId: varchar("epr_id", { length: 64 }),
    eprPcc: varchar("epr_pcc", { length: 32 }),
    agentSine: varchar("agent_sine", { length: 32 }),
    dkNumber: varchar("dk_number", { length: 64 }),
    pipeline: varchar("pipeline", { length: 64 }),
    validationStatus: varchar("validation_status", { length: 32 }),
    requestedAt: timestamp("requested_at"),
    activatedAt: timestamp("activated_at"),
    // Set when deactivation reclaims the EPR. Must be settable without
    // touching user_identities (AR-334 AC).
    deactivatedAt: timestamp("deactivated_at"),
    bookWithin72hr: boolean("book_within_72hr"),
    lastLoginAt: timestamp("last_login_at"), // TRIO-side login, drives the reclaim sweep
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userUnique: uniqueIndex("user_trio_accounts_user_id_unique").on(table.userId),
  }),
);

/**
 * The Duda / Advisor Showcase site provisioned for a member (AR-334).
 *
 * `stripe_subscription_id` here is the SITE subscription, separate from the
 * membership subscription in `subscriptions` — Duda has no grace period, so the
 * two cannot share a billing state.
 */
export const userWebsiteAccounts = pgTable(
  "user_website_accounts",
  {
    websiteAccountId: serial("website_account_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    provider: varchar("provider", { length: 32 }).notNull(), // duda / showcase
    siteId: varchar("site_id", { length: 255 }),
    plan: varchar("plan", { length: 64 }),
    stripeSubscriptionId: varchar("stripe_subscription_id", { length: 255 }),
    publishState: varchar("publish_state", { length: 32 }), // published / unpublished / never_published
    unpublishReason: text("unpublish_reason"),
    publicUrl: text("public_url"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userProviderUnique: uniqueIndex("user_website_accounts_user_id_provider_unique").on(
      table.userId,
      table.provider,
    ),
    userIdx: index("user_website_accounts_user_id_idx").on(table.userId),
  }),
);

/**
 * Team membership over time (AR-338).
 *
 * Today a move between teams is a destructive UPDATE of `users.team_id` with
 * nothing to audit. Here a move closes the current row and opens a new one.
 *
 * NO constraints here on purpose. AR-338 Stage 3 adds the partial unique
 * indexes (one current row per user, one agency owner per team) as handwritten
 * SQL after AR-329 reads zero — `db:push` cannot express a partial unique index
 * and would prompt destructively.
 */
export const teamMemberships = pgTable(
  "team_memberships",
  {
    membershipId: serial("membership_id").primaryKey(),
    teamId: integer("team_id")
      .notNull()
      .references(() => teams.id, { onDelete: "cascade" }),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    teamRole: teamRoleEnum("team_role").notNull(),
    isCurrent: boolean("is_current").notNull().default(true),
    joinedAt: timestamp("joined_at").defaultNow().notNull(),
    leftAt: timestamp("left_at"),
    movedByUserId: integer("moved_by_user_id")
      .references((): AnyPgColumn => users.id, { onDelete: "set null" }),
    moveReason: text("move_reason"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    teamIdx: index("team_memberships_team_id_idx").on(table.teamId),
    userCurrentIdx: index("team_memberships_user_id_is_current_idx").on(
      table.userId,
      table.isCurrent,
    ),
  }),
);

/**
 * Append-only audit of every account-status transition (AR-337).
 *
 * PRO records this in a single field, `Account_Status_previous` — a one-slot
 * undo buffer that cannot say who suspended a member, when, from where or why.
 *
 * Rows are IMMUTABLE: no UPDATE or DELETE anywhere. History is written in both
 * feature-flag states, because an audit log with gaps is worse than none.
 */
export const userStatusHistory = pgTable(
  "user_status_history",
  {
    historyId: serial("history_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    fromStatus: accountStatusEnum("from_status"), // null on the first transition
    toStatus: accountStatusEnum("to_status").notNull(),
    reasonCodeId: integer("reason_code_id")
      .references(() => statusReasonCodes.reasonId, { onDelete: "set null" }),
    reasonText: text("reason_text"),
    actorUserId: integer("actor_user_id")
      .references((): AnyPgColumn => users.id, { onDelete: "set null" }),
    actorKind: varchar("actor_kind", { length: 16 }).notNull(), // admin / member / system / webhook
    ipAddress: varchar("ip_address", { length: 45 }),
    userAgent: text("user_agent"),
    // Every row written by ONE cascade shares this, which is what makes a
    // team-wide suspension reversible as a unit rather than member by member.
    cascadeGroupId: pgUuid("cascade_group_id"),
    // The owner whose suspension reached this member.
    cascadedFromUserId: integer("cascaded_from_user_id")
      .references((): AnyPgColumn => users.id, { onDelete: "set null" }),
    // Separate from createdAt because a deactivation can be scheduled for a
    // future period end.
    effectiveAt: timestamp("effective_at").defaultNow().notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    userCreatedIdx: index("user_status_history_user_id_created_at_idx").on(
      table.userId,
      table.createdAt,
    ),
    cascadeGroupIdx: index("user_status_history_cascade_group_id_idx").on(table.cascadeGroupId),
  }),
);

/**
 * What each account status is allowed to reach (AR-339).
 *
 * PRO decides this in a different place on nearly every page and the rules do
 * not agree. Six seeded rows, read by one NestJS guard ordered after
 * authentication.
 *
 * A NULL `suspension_source` means "any source". Enforcing exactly one rule per
 * (status, source) is left to AR-339, which seeds the rows — a unique index
 * here would not hold anyway, since Postgres treats NULLs as distinct.
 */
export const accountStatusAccessRules = pgTable(
  "account_status_access_rules",
  {
    ruleId: serial("rule_id").primaryKey(),
    accountStatus: accountStatusEnum("account_status").notNull(),
    suspensionSource: suspensionSourceEnum("suspension_source"),
    // checkout_only / activation_only / full_member / payment_update_only / locked
    scope: varchar("scope", { length: 32 }).notNull(),
    landingPath: varchar("landing_path", { length: 255 }).notNull(),
    // Only `deactivated` sets this. A payment-failed suspension must be able to
    // log in and reach a form to fix the thing that suspended them — a
    // deliberate divergence from PRO, which force-logs-out both.
    forceLogout: boolean("force_logout").notNull().default(false),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    statusSourceIdx: index("account_status_access_rules_status_source_idx").on(
      table.accountStatus,
      table.suspensionSource,
    ),
  }),
);

/**
 * Future-dated account actions as queue rows (AR-340).
 *
 * PRO stores the currently-scheduled dunning workflow as a reference on
 * `User.Reminder` — a queue implemented as a field: not inspectable, not
 * auditable, and holding exactly one pending action. This holds many, and each
 * one can be cancelled with an audit trail.
 */
export const scheduledAccountActions = pgTable(
  "scheduled_account_actions",
  {
    actionId: serial("action_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    // deactivate_at_period_end / dunning_notice / suspend / cascade_suspend /
    // cascade_deactivate / reactivate
    actionType: varchar("action_type", { length: 32 }).notNull(),
    // 1–5 for dunning_notice. PRO's chain stops at 5 by design; hop 6 is a
    // human phone call (D-CO-08 open).
    dunningStep: integer("dunning_step"),
    scheduledFor: timestamp("scheduled_for").notNull(),
    status: varchar("status", { length: 16 }).notNull().default("pending"), // pending / processing / completed / cancelled / failed
    attempts: integer("attempts").notNull().default(0),
    lastError: text("last_error"),
    // Shared with the user_status_history rows the same cascade writes.
    cascadeGroupId: pgUuid("cascade_group_id"),
    payload: jsonb("payload"),
    createdByUserId: integer("created_by_user_id")
      .references((): AnyPgColumn => users.id, { onDelete: "set null" }),
    processedAt: timestamp("processed_at"),
    cancelledAt: timestamp("cancelled_at"),
    cancelledByUserId: integer("cancelled_by_user_id")
      .references((): AnyPgColumn => users.id, { onDelete: "set null" }),
    cancelReason: text("cancel_reason"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    // The worker's drain read.
    dueIdx: index("scheduled_account_actions_status_scheduled_for_idx").on(
      table.status,
      table.scheduledFor,
    ),
    userIdx: index("scheduled_account_actions_user_id_idx").on(table.userId),
    cascadeGroupIdx: index("scheduled_account_actions_cascade_group_id_idx").on(
      table.cascadeGroupId,
    ),
  }),
);

// ── AR-330 · Inferred types, insert schemas & enum unions ────────────────────

export type StatusReasonCode = typeof statusReasonCodes.$inferSelect;
export type InsertStatusReasonCode = typeof statusReasonCodes.$inferInsert;
export const insertStatusReasonCodeSchema = createInsertSchema(statusReasonCodes);

export type UserIdentity = typeof userIdentities.$inferSelect;
export type InsertUserIdentity = typeof userIdentities.$inferInsert;
export const insertUserIdentitySchema = createInsertSchema(userIdentities);

export type ExternalIdEvent = typeof externalIdEvents.$inferSelect;
export type InsertExternalIdEvent = typeof externalIdEvents.$inferInsert;
export const insertExternalIdEventSchema = createInsertSchema(externalIdEvents);

export type UserTrioAccount = typeof userTrioAccounts.$inferSelect;
export type InsertUserTrioAccount = typeof userTrioAccounts.$inferInsert;
export const insertUserTrioAccountSchema = createInsertSchema(userTrioAccounts);

export type UserWebsiteAccount = typeof userWebsiteAccounts.$inferSelect;
export type InsertUserWebsiteAccount = typeof userWebsiteAccounts.$inferInsert;
export const insertUserWebsiteAccountSchema = createInsertSchema(userWebsiteAccounts);

export type TeamMembership = typeof teamMemberships.$inferSelect;
export type InsertTeamMembership = typeof teamMemberships.$inferInsert;
export const insertTeamMembershipSchema = createInsertSchema(teamMemberships);

export type UserStatusHistoryEntry = typeof userStatusHistory.$inferSelect;
export type InsertUserStatusHistoryEntry = typeof userStatusHistory.$inferInsert;
export const insertUserStatusHistorySchema = createInsertSchema(userStatusHistory);

export type AccountStatusAccessRule = typeof accountStatusAccessRules.$inferSelect;
export type InsertAccountStatusAccessRule = typeof accountStatusAccessRules.$inferInsert;
export const insertAccountStatusAccessRuleSchema = createInsertSchema(accountStatusAccessRules);

export type ScheduledAccountAction = typeof scheduledAccountActions.$inferSelect;
export type InsertScheduledAccountAction = typeof scheduledAccountActions.$inferInsert;
export const insertScheduledAccountActionSchema = createInsertSchema(scheduledAccountActions);

export type AccountStatus = (typeof accountStatusEnum.enumValues)[number];
export type SuspensionSource = (typeof suspensionSourceEnum.enumValues)[number];
export type TeamStatus = (typeof teamStatusEnum.enumValues)[number];
export type TeamRole = (typeof teamRoleEnum.enumValues)[number];

// ============================================================================
// AR-344 · Onboarding domain
//
// The admin-authored Activation flow (modules → steps → per-type config) and
// the member's progress through it. Every table here arrives EMPTY with no
// reader; AR-328's editor panels are the design these columns serve.
//   · plans / routes / pathways / subscriptions are AR-13's — altered above,
//     columns only. users.route_id / pathway_id / plan_id stay authoritative;
//     member_onboarding holds position only.
//   · users.pathway_id stays until AR-346 drops it; member_pathways is
//     backfilled from it and is the multi-pathway home from here on.
//   · subscription_status is reused, not redefined.
//   · no constraint on any pre-existing column — AR-346.
// ============================================================================

export const onboardingStepTypeEnum = pgEnum("onboarding_step_type", [
  "add_on",
  "simple",
  "standard_form",
  "ai_form",
]);

// AR-328's Requirement toggle. Skippable = required, but the member can put it
// off (EIN letter, banking). One enum rather than two booleans, so an invalid
// combination can't be stored.
export const stepRequirementLevelEnum = pgEnum("step_requirement_level", [
  "required",
  "skippable",
  "optional",
]);

export const stepProgressStatusEnum = pgEnum("step_progress_status", [
  "not_started",
  "in_progress",
  "complete",
  "skipped",
  "failed", // e.g. Verify Identity failed — pairs with the step_failed email trigger
]);

// What a visibility rule branches on (AR-328 rule builder).
export const conditionTypeEnum = pgEnum("condition_type", [
  "route",
  "pathway",
  "plan",
  "member_answer",
  "experience_level",
]);

export const conditionOperatorEnum = pgEnum("condition_operator", [
  "equals",
  "not_equals",
  "in",
  "not_in",
  "is_set",
  "is_not_set",
]);

export const formFieldTypeEnum = pgEnum("form_field_type", [
  "text",
  "textarea",
  "select",
  "multi_select",
  "number",
  "date",
  "boolean",
]);

// internal = WorldVia staff (support@, sales), not the member's associates.
export const emailRecipientScopeEnum = pgEnum("email_recipient_scope", ["member", "internal", "both"]);

// Member events only — per the Member Lifecycle Map, no email fires on admin edits.
export const stepEmailTriggerEventEnum = pgEnum("step_email_trigger_event", [
  "step_started",
  "step_completed",
  "step_skipped",
  "step_failed", // e.g. Verify Identity → support@
  "module_completed",
]);

// performance_guaranty is the main member agreement. The checkout terms and
// conditions acknowledgement is not a document yet (open with the wider team).
export const documentTypeEnum = pgEnum("document_type", [
  "background_check_authorization",
  "performance_guaranty",
  "w9",
  "banking_authorization",
  "certificate_of_formation",
  "irs_ein_letter",
  "associate_agreement",
]);

export const documentStatusEnum = pgEnum("document_status", [
  "pending",
  "uploaded",
  "signed",
  "verified",
  "rejected",
  "skipped_commission_hold", // pairs with documents.commission_hold
]);

// ── Content model — admin-authored ──────────────────────────────────────────

export const onboardingModules = pgTable("onboarding_modules", {
  moduleId: serial("module_id").primaryKey(),
  slug: varchar("slug", { length: 50 }).notNull().unique(),
  name: varchar("name", { length: 120 }).notNull(),
  navHeaderLabel: varchar("nav_header_label", { length: 60 }), // shown in the member progress bar
  description: text("description"),
  sortOrder: integer("sort_order").notNull().default(0),
  showMiniTracker: boolean("show_mini_tracker").notNull().default(true), // admin-editable in the module panel
  isSystem: boolean("is_system").notNull().default(false), // Checkout renders locked
  isArchived: boolean("is_archived").notNull().default(false),
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const onboardingSteps = pgTable(
  "onboarding_steps",
  {
    stepId: serial("step_id").primaryKey(),
    moduleId: integer("module_id")
      .notNull()
      .references(() => onboardingModules.moduleId, { onDelete: "cascade" }),
    slug: varchar("slug", { length: 80 }).notNull(),
    title: varchar("title", { length: 200 }).notNull(),
    stepType: onboardingStepTypeEnum("step_type").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    requirementLevel: stepRequirementLevelEnum("requirement_level").notNull().default("required"),
    // Signed documents get one step each, so each signature fires its own email.
    isSystem: boolean("is_system").notNull().default(false), // hard-coded steps — visible to admins, not editable
    isArchived: boolean("is_archived").notNull().default(false),
    archivedAt: timestamp("archived_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    moduleSlugUnique: uniqueIndex("onboarding_steps_module_id_slug_unique").on(table.moduleId, table.slug),
  }),
);

// Scope chips. Modules scope by route, pathway and plan; steps by route and
// pathway only. No rows = visible to everyone.

export const moduleRoutes = pgTable(
  "module_routes",
  {
    id: serial("id").primaryKey(),
    moduleId: integer("module_id")
      .notNull()
      .references(() => onboardingModules.moduleId, { onDelete: "cascade" }),
    routeId: integer("route_id")
      .notNull()
      .references(() => routes.routeId, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    pairUnique: uniqueIndex("module_routes_module_id_route_id_unique").on(table.moduleId, table.routeId),
  }),
);

export const modulePathways = pgTable(
  "module_pathways",
  {
    id: serial("id").primaryKey(),
    moduleId: integer("module_id")
      .notNull()
      .references(() => onboardingModules.moduleId, { onDelete: "cascade" }),
    pathwayId: integer("pathway_id")
      .notNull()
      .references(() => pathways.pathwayId, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    pairUnique: uniqueIndex("module_pathways_module_id_pathway_id_unique").on(table.moduleId, table.pathwayId),
  }),
);

export const modulePlans = pgTable(
  "module_plans",
  {
    id: serial("id").primaryKey(),
    moduleId: integer("module_id")
      .notNull()
      .references(() => onboardingModules.moduleId, { onDelete: "cascade" }),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.planId, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    pairUnique: uniqueIndex("module_plans_module_id_plan_id_unique").on(table.moduleId, table.planId),
  }),
);

export const stepRoutes = pgTable(
  "step_routes",
  {
    id: serial("id").primaryKey(),
    stepId: integer("step_id")
      .notNull()
      .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
    routeId: integer("route_id")
      .notNull()
      .references(() => routes.routeId, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    pairUnique: uniqueIndex("step_routes_step_id_route_id_unique").on(table.stepId, table.routeId),
  }),
);

export const stepPathways = pgTable(
  "step_pathways",
  {
    id: serial("id").primaryKey(),
    stepId: integer("step_id")
      .notNull()
      .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
    pathwayId: integer("pathway_id")
      .notNull()
      .references(() => pathways.pathwayId, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    pairUnique: uniqueIndex("step_pathways_step_id_pathway_id_unique").on(table.stepId, table.pathwayId),
  }),
);

/**
 * The AND/OR rule builder — separate from the scope chips above.
 * Rows sharing a group_index are ANDed; different groups are ORed.
 * Polymorphic target, so no FK.
 */
export const visibilityConditions = pgTable(
  "visibility_conditions",
  {
    conditionId: serial("condition_id").primaryKey(),
    targetType: varchar("target_type", { length: 10 }).notNull(), // module | step
    targetId: integer("target_id").notNull(),
    conditionType: conditionTypeEnum("condition_type").notNull(),
    operator: conditionOperatorEnum("operator").notNull(),
    value: text("value"),
    groupIndex: integer("group_index").notNull().default(0),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    targetIdx: index("visibility_conditions_target_idx").on(table.targetType, table.targetId),
    // No FK on the polymorphic target, so at least pin the type: a typo like
    // 'steps' would otherwise store a rule the evaluator never matches.
    // Modules and steps are archived, not deleted, so targets don't orphan.
    targetTypeCheck: check("visibility_conditions_target_type_check", sql`target_type IN ('module', 'step')`),
  }),
);

// Standard Form step — its fields and where each answer lands.
export const stepFormFields = pgTable(
  "step_form_fields",
  {
    fieldId: serial("field_id").primaryKey(),
    stepId: integer("step_id")
      .notNull()
      .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
    fieldKey: varchar("field_key", { length: 60 }).notNull(),
    label: varchar("label", { length: 200 }).notNull(),
    fieldType: formFieldTypeEnum("field_type").notNull(),
    isRequired: boolean("is_required").notNull().default(false),
    sortOrder: integer("sort_order").notNull().default(0),
    profileFieldTarget: varchar("profile_field_target", { length: 80 }), // profile column the answer writes to
    options: jsonb("options"), // select / multi_select choices only
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    stepFieldKeyUnique: uniqueIndex("step_form_fields_step_id_field_key_unique").on(table.stepId, table.fieldKey),
  }),
);

// Simple step — one row per step.
export const stepSimpleConfig = pgTable("step_simple_config", {
  configId: serial("config_id").primaryKey(),
  stepId: integer("step_id")
    .notNull()
    .unique()
    .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
  videoUrl: text("video_url"), // Vimeo
  body: text("body"), // limited rich text
  fileUrl: text("file_url"),
  fileLabel: varchar("file_label", { length: 120 }),
  ctaLabel: varchar("cta_label", { length: 80 }),
  ctaUrl: text("cta_url"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// AI Form step — configuration only. Results live in ai_step_outputs.
export const stepAiConfig = pgTable("step_ai_config", {
  configId: serial("config_id").primaryKey(),
  stepId: integer("step_id")
    .notNull()
    .unique()
    .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
  systemPrompt: text("system_prompt").notNull(), // variables in {{double_braces}}
  seedInputs: jsonb("seed_inputs"), // which earlier outputs feed this step
  outputTargetField: varchar("output_target_field", { length: 80 }),
  model: varchar("model", { length: 80 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Add-On step — one row per step.
export const stepAddOnConfig = pgTable("step_add_on_config", {
  configId: serial("config_id").primaryKey(),
  stepId: integer("step_id")
    .notNull()
    .unique()
    .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
  // The offer's own title and description, distinct from the step title
  // (AR-328 Add-On panel: step "Offer an Add-On" → offer "Add the Accelerator Course?").
  title: varchar("title", { length: 200 }),
  description: text("description"),
  stripeProductId: varchar("stripe_product_id", { length: 255 }),
  stripePriceId: varchar("stripe_price_id", { length: 255 }),
  couponCode: varchar("coupon_code", { length: 80 }),
  placement: varchar("placement", { length: 30 }), // inline | modal — the step's module decides when it shows
  promoVideoUrl: text("promo_video_url"),
  valueAdd: text("value_add"),
  costDisplay: varchar("cost_display", { length: 60 }),
  // Editable so an admin moves an Accelerator date without a deploy.
  eventStartsAt: timestamp("event_starts_at"),
  eventEndsAt: timestamp("event_ends_at"),
  eventTimezone: varchar("event_timezone", { length: 60 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const emailTemplates = pgTable("email_templates", {
  templateId: serial("template_id").primaryKey(),
  slug: varchar("slug", { length: 80 }).notNull().unique(),
  subject: varchar("subject", { length: 255 }).notNull(),
  body: text("body").notNull(),
  recipientScope: emailRecipientScopeEnum("recipient_scope").notNull().default("member"),
  // Fixed recipients, on top of the member the scope resolves to at send time:
  // internal mail varies by template (support@ vs. sales) and member mail bcc's
  // HubSpot. Stored now so the trigger ticket needs no migration.
  toAddresses: text("to_addresses").array(),
  ccAddresses: text("cc_addresses").array(),
  bccAddresses: text("bcc_addresses").array(),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// A trigger hangs off a step, or — for module_completed ("You're active" on
// Activation) — off a module. Exactly one of the two, enforced below.
export const stepEmailTriggers = pgTable(
  "step_email_triggers",
  {
    triggerId: serial("trigger_id").primaryKey(),
    stepId: integer("step_id").references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
    moduleId: integer("module_id").references(() => onboardingModules.moduleId, { onDelete: "cascade" }),
    templateId: integer("template_id")
      .notNull()
      .references(() => emailTemplates.templateId, { onDelete: "cascade" }),
    triggerEvent: stepEmailTriggerEventEnum("trigger_event").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    stepTemplateEventUnique: uniqueIndex("step_email_triggers_step_template_event_unique").on(
      table.stepId,
      table.templateId,
      table.triggerEvent,
    ),
    moduleTemplateEventUnique: uniqueIndex("step_email_triggers_module_template_event_unique").on(
      table.moduleId,
      table.templateId,
      table.triggerEvent,
    ),
    oneTarget: check(
      "step_email_triggers_one_target",
      sql`(trigger_event = 'module_completed' AND module_id IS NOT NULL AND step_id IS NULL)
        OR (trigger_event <> 'module_completed' AND step_id IS NOT NULL AND module_id IS NULL)`,
    ),
  }),
);

// ── Member progress ─────────────────────────────────────────────────────────

// Position only. Route, pathway and plan live on users (AR-13).
export const memberOnboarding = pgTable(
  "member_onboarding",
  {
    memberOnboardingId: serial("member_onboarding_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .unique()
      .references(() => users.id, { onDelete: "cascade" }),
    currentModuleId: integer("current_module_id"),
    currentStepId: integer("current_step_id").references(() => onboardingSteps.stepId, {
      onDelete: "set null",
    }), // what lets a member resume
    activationTrackOptedIn: boolean("activation_track_opted_in").notNull().default(false),
    optedInAt: timestamp("opted_in_at"),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    // Named explicitly: the generated name runs past Postgres's 63-char limit,
    // gets truncated, and drizzle-kit would drop/re-add it on every push.
    currentModuleFk: foreignKey({
      name: "member_onboarding_current_module_id_fk",
      columns: [table.currentModuleId],
      foreignColumns: [onboardingModules.moduleId],
    }).onDelete("set null"),
  }),
);

export const memberPathways = pgTable(
  "member_pathways",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    pathwayId: integer("pathway_id")
      .notNull()
      .references(() => pathways.pathwayId, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    pairUnique: uniqueIndex("member_pathways_user_id_pathway_id_unique").on(table.userId, table.pathwayId),
  }),
);

export const stepProgress = pgTable(
  "step_progress",
  {
    progressId: serial("progress_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    stepId: integer("step_id")
      .notNull()
      .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
    status: stepProgressStatusEnum("status").notNull().default("not_started"),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    skippedReason: text("skipped_reason"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userStepUnique: uniqueIndex("step_progress_user_id_step_id_unique").on(table.userId, table.stepId),
  }),
);

export const stepAnswers = pgTable(
  "step_answers",
  {
    answerId: serial("answer_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    stepId: integer("step_id")
      .notNull()
      .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
    formFieldId: integer("form_field_id")
      .notNull()
      .references(() => stepFormFields.fieldId, { onDelete: "cascade" }),
    value: text("value"),
    answeredAt: timestamp("answered_at").defaultNow().notNull(),
  },
  (table) => ({
    userFieldUnique: uniqueIndex("step_answers_user_id_form_field_id_unique").on(table.userId, table.formFieldId),
  }),
);

export const aiStepOutputs = pgTable(
  "ai_step_outputs",
  {
    outputId: serial("output_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    stepId: integer("step_id")
      .notNull()
      .references(() => onboardingSteps.stepId, { onDelete: "cascade" }),
    version: integer("version").notNull().default(1),
    rawOutput: text("raw_output"), // what the model produced
    finalOutput: text("final_output"), // what the member kept — canonical
    model: varchar("model", { length: 80 }),
    promptSnapshot: text("prompt_snapshot"),
    generatedAt: timestamp("generated_at").defaultNow().notNull(),
    editedAt: timestamp("edited_at"),
    editedBy: integer("edited_by").references(() => users.id, { onDelete: "set null" }),
  },
  (table) => ({
    userStepVersionUnique: uniqueIndex("ai_step_outputs_user_step_version_unique").on(
      table.userId,
      table.stepId,
      table.version,
    ),
  }),
);

// Which routes each plan offers.
export const planRoutes = pgTable(
  "plan_routes",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.planId, { onDelete: "cascade" }),
    routeId: integer("route_id")
      .notNull()
      .references(() => routes.routeId, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    pairUnique: uniqueIndex("plan_routes_plan_id_route_id_unique").on(table.planId, table.routeId),
  }),
);

// ── Documents and notes ─────────────────────────────────────────────────────

export const documents = pgTable(
  "documents",
  {
    documentId: serial("document_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    documentType: documentTypeEnum("document_type").notNull(),
    storageKey: text("storage_key"),
    status: documentStatusEnum("status").notNull().default("pending"),
    uploadedAt: timestamp("uploaded_at"),
    verifiedAt: timestamp("verified_at"),
    verifiedBy: integer("verified_by").references(() => users.id, { onDelete: "set null" }),
    rejectionReason: text("rejection_reason"),
    commissionHold: boolean("commission_hold").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdx: index("documents_user_id_idx").on(table.userId),
  }),
);

export const adminNotes = pgTable(
  "admin_notes",
  {
    noteId: serial("note_id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    authorId: integer("author_id").references(() => users.id, { onDelete: "set null" }),
    body: text("body").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    userIdx: index("admin_notes_user_id_idx").on(table.userId),
  }),
);

// ── AR-344 · Relations (joins the editor reads together) ────────────────────

export const onboardingModulesRelations = relations(onboardingModules, ({ many }) => ({
  steps: many(onboardingSteps),
  emailTriggers: many(stepEmailTriggers),
}));

export const onboardingStepsRelations = relations(onboardingSteps, ({ one, many }) => ({
  module: one(onboardingModules, {
    fields: [onboardingSteps.moduleId],
    references: [onboardingModules.moduleId],
  }),
  formFields: many(stepFormFields),
  simpleConfig: one(stepSimpleConfig),
  aiConfig: one(stepAiConfig),
  addOnConfig: one(stepAddOnConfig),
  emailTriggers: many(stepEmailTriggers),
}));

export const stepFormFieldsRelations = relations(stepFormFields, ({ one }) => ({
  step: one(onboardingSteps, { fields: [stepFormFields.stepId], references: [onboardingSteps.stepId] }),
}));

export const stepSimpleConfigRelations = relations(stepSimpleConfig, ({ one }) => ({
  step: one(onboardingSteps, { fields: [stepSimpleConfig.stepId], references: [onboardingSteps.stepId] }),
}));

export const stepAiConfigRelations = relations(stepAiConfig, ({ one }) => ({
  step: one(onboardingSteps, { fields: [stepAiConfig.stepId], references: [onboardingSteps.stepId] }),
}));

export const stepAddOnConfigRelations = relations(stepAddOnConfig, ({ one }) => ({
  step: one(onboardingSteps, { fields: [stepAddOnConfig.stepId], references: [onboardingSteps.stepId] }),
}));

export const emailTemplatesRelations = relations(emailTemplates, ({ many }) => ({
  stepTriggers: many(stepEmailTriggers),
}));

export const stepEmailTriggersRelations = relations(stepEmailTriggers, ({ one }) => ({
  step: one(onboardingSteps, { fields: [stepEmailTriggers.stepId], references: [onboardingSteps.stepId] }),
  module: one(onboardingModules, {
    fields: [stepEmailTriggers.moduleId],
    references: [onboardingModules.moduleId],
  }),
  template: one(emailTemplates, {
    fields: [stepEmailTriggers.templateId],
    references: [emailTemplates.templateId],
  }),
}));

// ── AR-344 · Inferred types, Zod schemas & enum unions ──────────────────────

export type OnboardingModule = typeof onboardingModules.$inferSelect;
export type InsertOnboardingModule = typeof onboardingModules.$inferInsert;
export const insertOnboardingModuleSchema = createInsertSchema(onboardingModules);
export const selectOnboardingModuleSchema = createSelectSchema(onboardingModules);

export type OnboardingStep = typeof onboardingSteps.$inferSelect;
export type InsertOnboardingStep = typeof onboardingSteps.$inferInsert;
export const insertOnboardingStepSchema = createInsertSchema(onboardingSteps);
export const selectOnboardingStepSchema = createSelectSchema(onboardingSteps);

export type ModuleRoute = typeof moduleRoutes.$inferSelect;
export type InsertModuleRoute = typeof moduleRoutes.$inferInsert;
export const insertModuleRouteSchema = createInsertSchema(moduleRoutes);
export const selectModuleRouteSchema = createSelectSchema(moduleRoutes);

export type ModulePathway = typeof modulePathways.$inferSelect;
export type InsertModulePathway = typeof modulePathways.$inferInsert;
export const insertModulePathwaySchema = createInsertSchema(modulePathways);
export const selectModulePathwaySchema = createSelectSchema(modulePathways);

export type ModulePlan = typeof modulePlans.$inferSelect;
export type InsertModulePlan = typeof modulePlans.$inferInsert;
export const insertModulePlanSchema = createInsertSchema(modulePlans);
export const selectModulePlanSchema = createSelectSchema(modulePlans);

export type StepRoute = typeof stepRoutes.$inferSelect;
export type InsertStepRoute = typeof stepRoutes.$inferInsert;
export const insertStepRouteSchema = createInsertSchema(stepRoutes);
export const selectStepRouteSchema = createSelectSchema(stepRoutes);

export type StepPathway = typeof stepPathways.$inferSelect;
export type InsertStepPathway = typeof stepPathways.$inferInsert;
export const insertStepPathwaySchema = createInsertSchema(stepPathways);
export const selectStepPathwaySchema = createSelectSchema(stepPathways);

export type VisibilityCondition = typeof visibilityConditions.$inferSelect;
export type InsertVisibilityCondition = typeof visibilityConditions.$inferInsert;
export const insertVisibilityConditionSchema = createInsertSchema(visibilityConditions);
export const selectVisibilityConditionSchema = createSelectSchema(visibilityConditions);

export type StepFormField = typeof stepFormFields.$inferSelect;
export type InsertStepFormField = typeof stepFormFields.$inferInsert;
export const insertStepFormFieldSchema = createInsertSchema(stepFormFields);
export const selectStepFormFieldSchema = createSelectSchema(stepFormFields);

export type StepSimpleConfig = typeof stepSimpleConfig.$inferSelect;
export type InsertStepSimpleConfig = typeof stepSimpleConfig.$inferInsert;
export const insertStepSimpleConfigSchema = createInsertSchema(stepSimpleConfig);
export const selectStepSimpleConfigSchema = createSelectSchema(stepSimpleConfig);

export type StepAiConfig = typeof stepAiConfig.$inferSelect;
export type InsertStepAiConfig = typeof stepAiConfig.$inferInsert;
export const insertStepAiConfigSchema = createInsertSchema(stepAiConfig);
export const selectStepAiConfigSchema = createSelectSchema(stepAiConfig);

export type StepAddOnConfig = typeof stepAddOnConfig.$inferSelect;
export type InsertStepAddOnConfig = typeof stepAddOnConfig.$inferInsert;
export const insertStepAddOnConfigSchema = createInsertSchema(stepAddOnConfig);
export const selectStepAddOnConfigSchema = createSelectSchema(stepAddOnConfig);

export type EmailTemplate = typeof emailTemplates.$inferSelect;
export type InsertEmailTemplate = typeof emailTemplates.$inferInsert;
export const insertEmailTemplateSchema = createInsertSchema(emailTemplates);
export const selectEmailTemplateSchema = createSelectSchema(emailTemplates);

export type StepEmailTrigger = typeof stepEmailTriggers.$inferSelect;
export type InsertStepEmailTrigger = typeof stepEmailTriggers.$inferInsert;
export const insertStepEmailTriggerSchema = createInsertSchema(stepEmailTriggers);
export const selectStepEmailTriggerSchema = createSelectSchema(stepEmailTriggers);

export type MemberOnboarding = typeof memberOnboarding.$inferSelect;
export type InsertMemberOnboarding = typeof memberOnboarding.$inferInsert;
export const insertMemberOnboardingSchema = createInsertSchema(memberOnboarding);
export const selectMemberOnboardingSchema = createSelectSchema(memberOnboarding);

export type MemberPathway = typeof memberPathways.$inferSelect;
export type InsertMemberPathway = typeof memberPathways.$inferInsert;
export const insertMemberPathwaySchema = createInsertSchema(memberPathways);
export const selectMemberPathwaySchema = createSelectSchema(memberPathways);

export type StepProgress = typeof stepProgress.$inferSelect;
export type InsertStepProgress = typeof stepProgress.$inferInsert;
export const insertStepProgressSchema = createInsertSchema(stepProgress);
export const selectStepProgressSchema = createSelectSchema(stepProgress);

export type StepAnswer = typeof stepAnswers.$inferSelect;
export type InsertStepAnswer = typeof stepAnswers.$inferInsert;
export const insertStepAnswerSchema = createInsertSchema(stepAnswers);
export const selectStepAnswerSchema = createSelectSchema(stepAnswers);

export type AiStepOutput = typeof aiStepOutputs.$inferSelect;
export type InsertAiStepOutput = typeof aiStepOutputs.$inferInsert;
export const insertAiStepOutputSchema = createInsertSchema(aiStepOutputs);
export const selectAiStepOutputSchema = createSelectSchema(aiStepOutputs);

export type PlanRoute = typeof planRoutes.$inferSelect;
export type InsertPlanRoute = typeof planRoutes.$inferInsert;
export const insertPlanRouteSchema = createInsertSchema(planRoutes);
export const selectPlanRouteSchema = createSelectSchema(planRoutes);

// `MemberDocument*`, not `Document*` — those names would shadow DOM globals in client code.
export type MemberDocument = typeof documents.$inferSelect;
export type InsertMemberDocument = typeof documents.$inferInsert;
export const insertMemberDocumentSchema = createInsertSchema(documents);
export const selectMemberDocumentSchema = createSelectSchema(documents);

export type AdminNote = typeof adminNotes.$inferSelect;
export type InsertAdminNote = typeof adminNotes.$inferInsert;
export const insertAdminNoteSchema = createInsertSchema(adminNotes);
export const selectAdminNoteSchema = createSelectSchema(adminNotes);

export type OnboardingStepType = (typeof onboardingStepTypeEnum.enumValues)[number];
export type StepRequirementLevel = (typeof stepRequirementLevelEnum.enumValues)[number];
export type StepProgressStatus = (typeof stepProgressStatusEnum.enumValues)[number];
export type ConditionType = (typeof conditionTypeEnum.enumValues)[number];
export type ConditionOperator = (typeof conditionOperatorEnum.enumValues)[number];
export type FormFieldType = (typeof formFieldTypeEnum.enumValues)[number];
export type EmailRecipientScope = (typeof emailRecipientScopeEnum.enumValues)[number];
export type StepEmailTriggerEvent = (typeof stepEmailTriggerEventEnum.enumValues)[number];
export type MemberDocumentType = (typeof documentTypeEnum.enumValues)[number];
export type MemberDocumentStatus = (typeof documentStatusEnum.enumValues)[number];

// ============================================================================
// AR-364 · Stripe foundation
//
// The webhook event log. One row per Stripe event received, keyed on Stripe's
// own event id, so a replayed or out-of-order delivery is recognised before
// any handler runs — Stripe retries an unacknowledged event for up to 3 days.
// `status` is the lifecycle: received → processing (claimed atomically by one
// delivery) → processed | ignored | failed. A failed row is re-attempted on
// the next delivery; a processing row whose claim is older than the timeout
// (a crash between claim and outcome) is claimable again; a processed or
// ignored row is answered 200 without running anything.
//
// Deliberately absent: subscription or payment columns. The payload is kept
// whole for after-the-fact reading; what an event CHANGES lives on users /
// subscriptions / user_website_accounts, written by its handler, never here.
// ============================================================================
export const stripeWebhookEvents = pgTable(
  "stripe_webhook_events",
  {
    eventId: serial("event_id").primaryKey(),
    stripeEventId: varchar("stripe_event_id", { length: 255 }).notNull().unique(),
    eventType: varchar("event_type", { length: 100 }).notNull(),
    apiVersion: varchar("api_version", { length: 40 }),
    livemode: boolean("livemode").notNull().default(false),
    // received / processing / processed / ignored / failed
    status: varchar("status", { length: 16 }).notNull().default("received"),
    attempts: integer("attempts").notNull().default(0),
    error: text("error"),
    // When the current delivery took the row; a stale claim is reclaimable.
    claimedAt: timestamp("claimed_at"),
    // The Stripe customer the event is about, when its object carries one.
    stripeCustomerId: varchar("stripe_customer_id", { length: 255 }),
    // Resolved from the customer link at receipt; null when unknown.
    userId: integer("user_id").references(() => users.id, { onDelete: "set null" }),
    payload: jsonb("payload").notNull(),
    // Stripe's `event.created`, for ordering independent of delivery time.
    eventCreatedAt: timestamp("event_created_at").notNull(),
    receivedAt: timestamp("received_at").defaultNow().notNull(),
    processedAt: timestamp("processed_at"),
  },
  (table) => ({
    typeIdx: index("stripe_webhook_events_event_type_idx").on(table.eventType),
    statusIdx: index("stripe_webhook_events_status_idx").on(table.status),
    customerIdx: index("stripe_webhook_events_stripe_customer_id_idx").on(table.stripeCustomerId),
    userIdx: index("stripe_webhook_events_user_id_idx").on(table.userId),
  }),
);

export type StripeWebhookEvent = typeof stripeWebhookEvents.$inferSelect;
export type InsertStripeWebhookEvent = typeof stripeWebhookEvents.$inferInsert;
export const insertStripeWebhookEventSchema = createInsertSchema(stripeWebhookEvents);
export const selectStripeWebhookEventSchema = createSelectSchema(stripeWebhookEvents);
